import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { alertPeriodToDays, ALERT_PERIODS } from '../../utils/alertWindow';
import { CH_TYPE, chHairline } from '../../styles/appUi';
import {
  Paper,
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Autocomplete
} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { fetchDashboardSummary } from '../../store/dashboardSlice';
import { formatCurrency, formatPercentage, formatPrice } from '../../utils/formatters';
import { USD_TO_INR } from '../../utils/constants';
import { resolveDecimals, resolveDecimalsFor } from '../../utils/decimalPrecision';
import { fmt } from '../../utils/priceFormat';
import { settingsService } from '../../services/settings.service';
import { tradingService } from '../../services/trading.service';
import { UserSettings } from '../../types';
import api from '../../services/api';
import { BarChart, Bar, Cell, LabelList, ResponsiveContainer, Tooltip as RechartsTooltip, PieChart, Pie, Legend, LineChart, Line, XAxis, YAxis } from 'recharts';
import FnoScoreBadge from '../fno/FnoScoreBadge';
import AiVerdictChip from '../ai/AiVerdictChip';
import aiService, { AiAssessment } from '../../services/ai.service';
import { fetchAssessmentsBatch } from '../../store/fnoSlice';
import AutoTrailSwitch from '../trades/AutoTrailSwitch';
import { useAppTheme } from '../../hooks/useAppTheme';
import RenotifyDialog from './RenotifyDialog';
import RenotifyCountdown from '../common/RenotifyCountdown';
import AiBoardCard from '../ai/AiBoardCard';
import RiskCockpit from './RiskCockpit';
import QuickTradePanel from './QuickTradePanel';
import OptionBasketsMonitor from '../options/OptionBasketsMonitor';
import OptionsPortfolioHeader from '../options/OptionsPortfolioHeader';
import { useNavigate } from 'react-router-dom';
import EventTicker from './EventTicker';
import SymbolHeatmap, { HeatmapCell, BreadthPoint } from './SymbolHeatmap';
import CollapsibleSection from './CollapsibleSection';
import OpenChartButton from './OpenChartButton';
import PaperTradesMonitor from './PaperTradesMonitor';
import SlModeChip, { SlModeInfo, slModeLine } from './SlModeChip';
import PendingAutoExecWatch from './PendingAutoExecWatch';
import { KpiSkeleton, CardSkeleton } from '../common/Skeletons';
import { AnchorStrip, RAIL_GUTTER_PX } from './DashboardAnchors';
import MarketStatusRail from './MarketStatusRail';
import HelpLink from '../common/HelpLink';
import EmptyState from '../common/EmptyState';
import MetricCard from '../common/MetricCard';
import StrengthBadge from '../common/StrengthBadge';
import ExecuteTooltipContent from '../common/ExecuteTooltipContent';
import { chartOpenQuery, tfToMinutes } from '../../utils/timeframe';
import { useTabs } from '../../contexts/TabsContext';
import LevelOptionDialog from '../options/LevelOptionDialog';
import { effectiveSegment } from '../../utils/sizeOrdersAs';
import { levelOptionTriggersService, levelOptionCapable, LevelOptionTrigger, LevelKey, attExpiryText, attExpiryShort } from '../../services/levelOptionTriggers.service';
import { findTemplate } from '../../utils/optionTemplates';

// Safe date formatter — returns '—' for null/undefined/invalid dates instead
// of throwing "Invalid time value" from toLocaleString.
const fmtDate = (v: any, opts?: Intl.DateTimeFormatOptions): string => {
  if (v == null || v === '') return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', opts);
};

// Stale-while-revalidate cache (module-level → survives route unmount) so the
// Watchlist / Price-Level-Watch sections render INSTANTLY when the user returns
// to the Dashboard, while the existing fetch refreshes them in the background.
// (The summary/positions/PnL already persist via Redux.)
let watchlistCache: { watchlist: any[]; priceLevels: any[]; hvnLevels?: any[] } | null = null;

/** "✕L" / "✕S" when a strategy alert also exits the opposite side. LR_Cross titles
 *  read "BTCUSD: SHORT ✕L" (backend lrCrossText.ts); the list API does not return
 *  additionalData, so the title is the source. The action chip alone hides the exit. */
const ExitTag: React.FC<{ alert: any; color: string; small?: boolean }> = ({ alert, color, small }) => {
  const m = typeof alert?.title === 'string' ? alert.title.match(/✕([LS])(?![A-Za-z])/) : null;
  const c = m ? (m[1] === 'L' ? 'LONG' : 'SHORT') : alert?.additionalData?.closes;
  if (c !== 'LONG' && c !== 'SHORT') return null;
  return (
    <Tooltip arrow title={`Also exits the open ${c === 'LONG' ? 'long' : 'short'}`}>
      <Typography component="span" variant="caption" sx={{ ml: 0.5, color, fontWeight: 700, fontSize: small ? '0.62rem' : CH_TYPE.small }}>
        {c === 'LONG' ? '✕L' : '✕S'}
      </Typography>
    </Tooltip>
  );
};

const DashboardLayout: React.FC = () => {
  const { C, isDark } = useAppTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { openChartTab } = useTabs();
  const navigate = useNavigate();
  const { summary, loading } = useSelector((state: RootState) => state.dashboard);
  const fnoAssessments = useSelector((state: RootState) => state.fno.assessments);

  // AI verdicts, keyed by alertId. Fetched once per mount and refreshed on the same cadence as the
  // dashboard summary — the assessment is written asynchronously after the alert lands, so a brief
  // gap where a fresh alert has no chip yet is expected and harmless.
  const [aiByAlert, setAiByAlert] = useState<Record<number, AiAssessment>>({});
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await aiService.getAssessments(72);
        if (cancelled) return;
        const map: Record<number, AiAssessment> = {};
        for (const r of rows) if (r.alertId != null) map[r.alertId] = r;
        setAiByAlert(map);
      } catch {
        // Non-fatal: no AI key, layer disabled, or endpoint unavailable. The dashboard must render
        // exactly as before in that case, so this failure is deliberately silent.
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const [tradingActive, setTradingActive] = useState(false);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const [alertPeriod, setAlertPeriod] = useState<string>(() => {
    try { return localStorage.getItem('dashboard_alertPeriod') || '1d'; } catch { return '1d'; }
  });
  // Persist the Recent-Alerts window so the Charts symbol-panel alert filters
  // (LongAlert / ShortAlert / CoverAlert / SellAlert) can read the SAME window.
  useEffect(() => { try { localStorage.setItem('dashboard_alertPeriod', alertPeriod); } catch { /* ignore */ } }, [alertPeriod]);
  const [parkedAlertPeriod, setParkedAlertPeriod] = useState<string>('30d');

  // IP warning banner
  const [ipWarning, setIpWarning] = useState<string | null>(null);

  // Manual SL editing state
  const [editingSLTradeId, setEditingSLTradeId] = useState<number | null>(null);
  const [editingSLValue, setEditingSLValue] = useState('');

  // Manual Target editing state
  const [editingTargetTradeId, setEditingTargetTradeId] = useState<number | null>(null);
  const [editingTargetValue, setEditingTargetValue] = useState('');

  // Expanded symbols for grouped alert rows
  const [expandedSymbols, setExpandedSymbols] = useState<Set<string>>(new Set());

  // Renotify dialog state
  const [renotifyDialogAlert, setRenotifyDialogAlert] = useState<any | null>(null);

  // Alert custom params editing state
  const [editingAlertId, setEditingAlertId] = useState<number | null>(null);
  const [alertCustomEntry, setAlertCustomEntry] = useState('');
  const [alertCustomSL, setAlertCustomSL] = useState('');
  const [alertCustomTarget, setAlertCustomTarget] = useState('');
  const [alertRenotifyAt, setAlertRenotifyAt] = useState('');
  const [alertCustomQuantity, setAlertCustomQuantity] = useState('');
  const [alertCustomOrderType, setAlertCustomOrderType] = useState('LIMIT');

  // System Performance bar chart (follows perfPeriod)
  const [perfDailyPnl, setPerfDailyPnl] = useState<Array<{day: string, pnl: number}>>([]);
  // Portfolio-value growth series (combined account value over the perfPeriod window).
  const [perfPortfolio, setPerfPortfolio] = useState<Array<{ date: string; combined: number | null }>>([]);
  // R78 — weeklyPnl / intradayPnl / portfolioChartData state removed along
  // with the Top Stats Cards that used to display them.

  // Manual trade dialog state
  const [manualTradeDialog, setManualTradeDialog] = useState(false);
  const [trailHelpOpen, setTrailHelpOpen] = useState(false);
  const [plwHelpOpen, setPlwHelpOpen] = useState(false);
  const [recentAlertsHelpOpen, setRecentAlertsHelpOpen] = useState(false);
  const [parkedAlertsHelpOpen, setParkedAlertsHelpOpen] = useState(false);
  // Waiting-row count reported up by the Pending AutoTrade Execution Watch child
  // so its collapsible header can flag new entries while folded.
  const [autoExecWaiting, setAutoExecWaiting] = useState(0);
  const [manualTradeForm, setManualTradeForm] = useState({
    symbol: '', exchange: 'NSE', side: 'LONG', quantity: '', entryPrice: '',
    stopLoss: '', target: '', tradeType: 'POSITIONAL', productType: 'CNC', notes: ''
  });
  const [manualTradeSymbolResults, setManualTradeSymbolResults] = useState<any[]>([]);
  const [manualTradeSearching, setManualTradeSearching] = useState(false);
  const [manualTradeSelectedInst, setManualTradeSelectedInst] = useState<any | null>(null);
  const [manualTradeLtp, setManualTradeLtp] = useState<{ value: number; source: string } | null>(null);
  const [manualTradeLtpLoading, setManualTradeLtpLoading] = useState(false);

  // Debounced symbol search for the Manual Trade autosuggest. Fires 250ms after the
  // user stops typing; cancels in-flight if a newer keystroke arrives.
  useEffect(() => {
    if (!manualTradeDialog) return;
    const q = manualTradeForm.symbol.trim();
    if (q.length < 2) { setManualTradeSymbolResults([]); return; }
    let cancelled = false;
    setManualTradeSearching(true);
    const t = setTimeout(async () => {
      try {
        const resp = await api.get<any>(`/instruments?search=${encodeURIComponent(q)}&pageSize=10`);
        const items: any[] = resp.data?.data?.instruments || resp.data?.instruments || [];
        if (!cancelled) setManualTradeSymbolResults(items);
      } catch {
        if (!cancelled) setManualTradeSymbolResults([]);
      } finally {
        if (!cancelled) setManualTradeSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [manualTradeForm.symbol, manualTradeDialog]);

  // Compute suggested qty from instrument investment × leverage / entryPrice,
  // rounded down to lot-size multiple for F&O/MCX.
  const manualTradeSuggestedQty = useMemo(() => {
    const inst = manualTradeSelectedInst;
    const price = parseFloat(manualTradeForm.entryPrice);
    if (!inst || !Number.isFinite(price) || price <= 0) return null;
    const exch = String(inst.exchange || '').toUpperCase();
    const instrType = String(inst.instrumentType || '').toUpperCase();
    const isOption = instrType === 'CE' || instrType === 'PE' || instrType === 'OPTIDX' || instrType === 'OPTSTK';
    const isFuture = instrType === 'FUT' || instrType === 'FUTIDX' || instrType === 'FUTSTK' || instrType === 'FUTCUR' || instrType === 'FUTCOM';
    const lev = Number(inst.leverage) || 1;
    const lotSize = Math.max(1, Math.floor(Number(inst.lotSize) || 1));
    let investment = 0;
    if (exch === 'DELTA' || isFuture) investment = Number(inst.futInvestmentAmount) || 0;
    else if (isOption) {
      const isBuy = manualTradeForm.side === 'LONG';
      investment = Number(isBuy ? inst.optInvestmentAmountBuy : inst.optInvestmentAmountSell) || 0;
    } else investment = Number(inst.eqInvestmentAmount) || 0;
    if (investment <= 0) return null;
    const rawQty = (investment * lev) / price;
    if (!Number.isFinite(rawQty) || rawQty <= 0) return null;
    if (exch === 'DELTA') {
      return Math.max(1, Math.floor(rawQty * 10000) / 10000); // up to 4 decimals for crypto
    }
    if (isOption || isFuture || exch === 'MCX' || exch === 'NFO' || exch === 'BFO' || exch === 'CDS') {
      const lots = Math.max(0, Math.floor(rawQty / lotSize));
      return lots > 0 ? lots * lotSize : null;
    }
    return Math.max(1, Math.floor(rawQty));
  }, [manualTradeSelectedInst, manualTradeForm.entryPrice, manualTradeForm.side]);

  // Snackbar state
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Format timeframe from minutes to readable label
  const formatTimeframe = (tf: string | number | undefined): string => {
    if (!tf) return '';
    const n = parseInt(String(tf), 10);
    if (isNaN(n)) return String(tf);
    if (n < 60) return `${n}m`;
    if (n === 60) return '1h';
    if (n < 1440) return `${n / 60}h`;
    if (n === 1440) return '1D';
    return `${Math.round(n / 1440)}D`;
  };


  const toggleSymbol = (symbol: string) => {
    setExpandedSymbols(prev => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol); else next.add(symbol);
      return next;
    });
  };

  const getAlertRowStyle = (alert: any) => {
    // Color rule (matches the Trades Management column):
    //   GREEN  → bullish actions (BUY, COVER, COVER_FLAT) — long entry / short exit
    //   RED    → bearish actions (SHORT, SELL, SELL_FLAT) — short entry / long exit
    //   NEUTRAL→ everything else (NOTIFY / unknown)
    // The `confirmed` flag is preserved as a stronger tint vs. the lighter "row" tint.
    const rawAction = (alert.parsedData as any)?.action
      || alert.title?.match(/\[(BUY|SELL|SHORT|COVER|COVER_FLAT|SELL_FLAT)\]/)?.[1]
      || '';
    const action = String(rawAction).toUpperCase();
    const confirmed = (alert.parsedData as any)?.confirmed === 'Y';

    const isLong  = action === 'BUY' || action === 'COVER' || action === 'COVER_FLAT';
    const isShort = action === 'SHORT' || action === 'SELL' || action === 'SELL_FLAT';

    if (isLong)  return { bg: confirmed ? C.bullMuted : C.rowProfit, color: C.bull };
    if (isShort) return { bg: confirmed ? C.bearMuted : C.rowLoss,   color: C.bear };
    return { bg: C.accentMuted, color: C.accent };
  };

  // Trades Management filters (Issue 6)
  const [dashboardSideFilter, setDashboardSideFilter] = useState<string>('');
  const [dashboardSymbolFilter, setDashboardSymbolFilter] = useState<string>('');
  const [dashboardExchangeFilter, setDashboardExchangeFilter] = useState<string>('');
  const [dashboardAlphaSort, setDashboardAlphaSort] = useState<'off' | 'asc' | 'desc'>('asc');
  const [dashboardLatestPerSymbol, setDashboardLatestPerSymbol] = useState<boolean>(false);

  // Add Symbol dialog state (Issues 13, 14)
  const [addSymbolDialog, setAddSymbolDialog] = useState<{ open: boolean; mode: 'priceLevelWatch' | 'wlLong' | 'wlShort' }>({ open: false, mode: 'priceLevelWatch' });
  const [addSymbolSearch, setAddSymbolSearch] = useState('');
  const [addSymbolResults, setAddSymbolResults] = useState<any[]>([]);
  const [addSymbolSearching, setAddSymbolSearching] = useState(false);
  // Price Level Watch: second step (set initial levels before adding)
  const [addSymbolSelected, setAddSymbolSelected] = useState<any>(null);
  const [addSymbolPLValues, setAddSymbolPLValues] = useState({ nbuy: '', nshort: '', bbuy: '', bshort: '' });

  // Alert filters
  // Recent-Alerts filters. Persisted (R152) so the Charts page's LongAlert /
  // ShortAlert symbol filter can reproduce the SAME filtered list.
  const [alertFilters, setAlertFilters] = useState(() => {
    const def = { action: '', interval: '', strategy: '', confirmed: '', symbol: '' };
    try {
      const v = JSON.parse(localStorage.getItem('dashboard_alertFilters') || 'null');
      if (v && typeof v === 'object') return { ...def, ...v };
    } catch { /* ignore */ }
    return def;
  });
  useEffect(() => {
    try { localStorage.setItem('dashboard_alertFilters', JSON.stringify(alertFilters)); } catch { /* ignore */ }
  }, [alertFilters]);

  // Parked alerts filters
  const [parkedFilters, setParkedFilters] = useState({ action: '', interval: '', strategy: '', confirmed: '', symbol: '' });

  // Parked alerts expanded symbols (separate from recent alerts)
  const [expandedParkedSymbols, setExpandedParkedSymbols] = useState<Set<string>>(new Set());

  // System Performance state
  const [perfPeriod, setPerfPeriod] = useState<number>(30);
  const [perfData, setPerfData] = useState<any>(null);
  const [perfLoading, setPerfLoading] = useState(false);

  // Watchlist notify dialog (reuses RenotifyDialog)
  const [wlNotifyDialog, setWlNotifyDialog] = useState<{ instId: number; symbol: string; side: 'long' | 'short'; currentPrice: number; decimalPrecision?: number } | null>(null);

  // Watchlist + price level state
  const [watchlistData, setWatchlistData] = useState<{ watchlist: any[]; priceLevels: any[]; hvnLevels?: any[] } | null>(() => watchlistCache);
  // Keep the module-level cache fresh so the NEXT Dashboard visit renders these
  // sections instantly from the last good data while the fetch revalidates.
  useEffect(() => { if (watchlistData) watchlistCache = watchlistData; }, [watchlistData]);
  // R115 follow-up — Heatmap data sourced from /v1/charts/symbol-pct-changes
  // (same canonical source as the Charts symbol panel % column) since the
  // dashboard/watchlist endpoint's change24h field is empty for most symbols.
  const [heatmapPctItems, setHeatmapPctItems] = useState<Array<{
    symbol: string; exchange: string; pctChange: number; ltp?: number;
    assetClass?: any; turnoverUsd?: number;
  }>>([]);
  /** Which feeds actually answered. An expired Zerodha session returns zero NSE
   *  rows silently, and the heatmap used to just look thin; now it can say so. */
  const [pctSources, setPctSources] = useState<{ zerodha?: string; delta?: string }>({});
  const [breadth, setBreadth] = useState<BreadthPoint[]>([]);
  useEffect(() => {
    let cancelled = false;
    const fetchPct = async () => {
      try {
        const r = await api.get<any>('/charts/symbol-pct-changes');
        const d = r?.data?.data || {};
        if (!cancelled) {
          setHeatmapPctItems(d.items || []);
          setPctSources(d.sources || {});
        }
      } catch { /* silent */ }
    };
    // The A/D series only grows once a minute (it is written on the pct-changes
    // cache MISS), so re-reading it any faster would return the same rows.
    const fetchBreadth = async () => {
      try {
        const r = await api.get<any>('/charts/breadth-history', { params: { hours: 12 } });
        const pts = (r?.data?.data?.points || []) as BreadthPoint[];
        if (!cancelled) setBreadth(pts);
      } catch { /* silent — the strip degrades to "history builds while open" */ }
    };
    fetchPct(); fetchBreadth();
    const t = window.setInterval(() => { fetchPct(); fetchBreadth(); }, 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);
  // Price Level Watch: inline editing
  const [plEditId, setPlEditId] = useState<number | null>(null);
  const [plEditValues, setPlEditValues] = useState<{ nbuy: string; nshort: string; bbuy: string; bshort: string }>({ nbuy: '', nshort: '', bshort: '', bbuy: '' });
  const [plSymbolSearch, setPlSymbolSearch] = useState('');
  const [plSortAsc, setPlSortAsc] = useState(true);
  const [priceLevelAutoExecute, setPriceLevelAutoExecute] = useState(false);
  // Option strategies attached to NB/NS/BB/BS levels (⚡ in the level cells).
  const [lvlAtts, setLvlAtts] = useState<LevelOptionTrigger[]>([]);
  const [lvlDlg, setLvlDlg] = useState<{ inst: any; key: LevelKey } | null>(null);
  const reloadLvlAtts = useCallback(async () => {
    try { setLvlAtts(await levelOptionTriggersService.list()); } catch { /* optional */ }
  }, []);
  const [orderPlacementEnabled, setOrderPlacementEnabled] = useState(true);

  /* Pending Entry Watch (round 27) — deferred LIMIT entries the app
     monitors locally; dashboard panel mirrors Price Level Watch UX. */
  type PendingEntryRow = {
    id: number;
    /** PAPER chart entry — filled by the app's paper engine, never a broker order. */
    isSimulation?: boolean;
    instrumentId: number;
    symbol?: string;
    exchange?: string;
    decimalPrecision?: number;
    side: 'BUY' | 'SHORT';
    entryPrice: number;
    stopLoss: number | null;
    target: number | null;
    quantity: number;
    status: 'PENDING' | 'TRIGGERED' | 'CANCELLED' | 'EXPIRED';
    triggeredAt: string | null;
    brokerOrderId: string | null;
    createdAt: string;
    updatedAt: string;
    /** Round 28 — current market price (broker LTP) for the symbol. */
    ltp?: number | null;
    /** Round 89 — multi-entry scale-in group metadata. */
    groupId?: string | null;
    entryIndex?: number;
    entryCount?: number;
    /** G7 — DIP = fires when price returns to the level; STOP = fires when
     *  price breaks through it (auto-classified at creation vs live price). */
    triggerDirection?: 'DIP' | 'STOP';
    /** G11 — validity end (NSE defaults to end-of-session; null = GTC). */
    expiresAt?: string | null;
  };
  const [pendingEntries, setPendingEntries] = useState<PendingEntryRow[]>([]);
  const [peSymbolSearch, setPeSymbolSearch] = useState('');
  const [peSortAsc, setPeSortAsc] = useState(true);
  // Active staged campaigns — their PENDING (armed, not-yet-deployed) ladder entries show as a
  // separate "option baskets" block inside Pending Manual Entry Watch.
  const [activeCampaigns, setActiveCampaigns] = useState<any[]>([]);
  useEffect(() => {
    // Dashboard tracks REAL money only — show LIVE ACTIVE campaigns; PAPER campaigns are reviewed
    // on the Options page. (A PAPER campaign lingering in Pending Manual Entry was the bug.)
    const load = async () => { try { const r: any = await api.get('/options/campaigns', { headers: { _suppressToast: 'true' } }); setActiveCampaigns((r?.data?.data || []).filter((c: any) => c.status === 'ACTIVE' && c.tradingMode !== 'PAPER')); } catch { /* keep last */ } };
    load(); const id = window.setInterval(() => { if (!document.hidden) load(); }, 30000); return () => window.clearInterval(id);
  }, []);
  const campaignPendingRows = useMemo(() => activeCampaigns.flatMap((c: any) => (c.stages || []).filter((s: any) => s.status === 'PENDING').map((s: any) => ({ c, s }))), [activeCampaigns]);
  // Armed synthetic-symbol triggers — a chart BUY/SHORT on a SYNTHETIC arms a trigger,
  // not a pending entry, so they were invisible here (a PAPER-armed trigger looked like
  // a lost trade). Grouped by synthetic; Disarm cancels the whole scale-in group.
  const [armedSynthTriggers, setArmedSynthTriggers] = useState<any[]>([]);
  useEffect(() => {
    const load = async () => {
      try { const r: any = await api.get('/synthetic-symbols/armed', { headers: { _suppressToast: 'true' } }); setArmedSynthTriggers(r?.data?.data || []); }
      catch { /* feature off (403) or transient — keep last */ }
    };
    load(); const id = window.setInterval(() => { if (!document.hidden) load(); }, 30000); return () => window.clearInterval(id);
  }, []);
  const armedBySynth = useMemo(() => {
    const m = new Map<number, { name: string; underlyingSymbol: string; mode: string; rows: any[] }>();
    for (const t of armedSynthTriggers) {
      if (!m.has(t.syntheticSymbolId)) m.set(t.syntheticSymbolId, { name: t.name, underlyingSymbol: t.underlyingSymbol, mode: t.tradingMode, rows: [] });
      m.get(t.syntheticSymbolId)!.rows.push(t);
    }
    return Array.from(m.entries());
  }, [armedSynthTriggers]);
  const disarmSynth = async (syntheticSymbolId: number, name: string) => {
    if (!window.confirm(`Disarm all armed entries of ${name}?`)) return;
    try {
      await api.delete(`/synthetic-symbols/${syntheticSymbolId}/trigger`);
      setArmedSynthTriggers(prev => prev.filter(t => t.syntheticSymbolId !== syntheticSymbolId));
    } catch { /* toast via interceptor */ }
  };
  const [campCollapse, setCampCollapse] = useState<Record<number, boolean>>({});
  // Active campaigns grouped by underlying symbol (each basket collapsible).
  const campaignsBySymbol = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const c of activeCampaigns) { const k = c.symbol || '—'; if (!m.has(k)) m.set(k, []); m.get(k)!.push(c); }
    return Array.from(m.entries());
  }, [activeCampaigns]);
  // Round 90 — collapsible scale-in groups (keyed by groupId). Default collapsed.
  const [peExpanded, setPeExpanded] = useState<Record<string, boolean>>({});
  const [peEditId, setPeEditId] = useState<number | null>(null);
  const [peEditValues, setPeEditValues] = useState<{ entryPrice: string; stopLoss: string; target: string; quantity: string }>({
    entryPrice: '', stopLoss: '', target: '', quantity: ''
  });
  const fetchPendingEntries = useCallback(async () => {
    try {
      const r = await api.get<any>('/pending-entries');
      setPendingEntries(r.data?.data || []);
    } catch { /* non-critical */ }
  }, []);
  useEffect(() => {
    fetchPendingEntries();
    const id = window.setInterval(fetchPendingEntries, 10_000);
    return () => clearInterval(id);
  }, [fetchPendingEntries]);
  const startEditPendingEntry = (pe: PendingEntryRow) => {
    setPeEditId(pe.id);
    setPeEditValues({
      entryPrice: String(pe.entryPrice ?? ''),
      stopLoss: pe.stopLoss != null ? String(pe.stopLoss) : '',
      target: pe.target != null ? String(pe.target) : '',
      quantity: String(pe.quantity ?? ''),
    });
  };
  const cancelEditPendingEntry = () => { setPeEditId(null); };
  const savePendingEntry = async (id: number) => {
    try {
      const body: any = {};
      if (peEditValues.entryPrice !== '') body.entryPrice = parseFloat(peEditValues.entryPrice);
      if (peEditValues.stopLoss !== '') body.stopLoss = parseFloat(peEditValues.stopLoss);
      else body.stopLoss = null;
      if (peEditValues.target !== '') body.target = parseFloat(peEditValues.target);
      else body.target = null;
      if (peEditValues.quantity !== '') body.quantity = parseInt(peEditValues.quantity, 10);
      await api.put(`/pending-entries/${id}`, body);
      setPeEditId(null);
      toast.success('Pending entry updated');
      fetchPendingEntries();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to update');
    }
  };
  const removePendingEntry = async (id: number, symbol?: string) => {
    const pe = pendingEntries.find(x => x.id === id);
    const isPending = pe?.status === 'PENDING';
    const verb = isPending ? 'Cancel pending' : 'Dismiss';
    if (!window.confirm(`${verb} ${symbol ?? ''} entry?`)) return;
    try {
      await api.delete(`/pending-entries/${id}`);
      toast.success(`${symbol ?? ''} entry ${isPending ? 'cancelled' : 'dismissed'}`);
      fetchPendingEntries();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to remove');
    }
  };

  /* Round 90 — remove an entire scale-in group from the group header.
   *  - No legs filled  → cancel ALL pending legs (their shared SL/Target,
   *    which live on the pending rows, disappear with them).
   *  - Some legs filled → cancel only the remaining PENDING legs; the open
   *    position keeps its own SL/Target (now owned by the Trade row). */
  const removeGroup = async (legs: PendingEntryRow[], symbol?: string) => {
    const pendingLegs = legs.filter(l => l.status === 'PENDING');
    const anyFilled = legs.some(l => l.status === 'TRIGGERED');
    if (pendingLegs.length === 0) {
      toast('Nothing to remove — all legs already fired.');
      return;
    }
    const msg = anyFilled
      ? `Cancel the ${pendingLegs.length} remaining pending ${symbol ?? ''} entr${pendingLegs.length === 1 ? 'y' : 'ies'}? The open position keeps its SL/Target.`
      : `Remove all ${pendingLegs.length} pending ${symbol ?? ''} entries (and their SL/Target)?`;
    if (!window.confirm(msg)) return;
    try {
      await Promise.all(pendingLegs.map(l => api.delete(`/pending-entries/${l.id}`)));
      toast.success(anyFilled
        ? `${symbol ?? ''} remaining entries cancelled`
        : `${symbol ?? ''} scale-in removed`);
      fetchPendingEntries();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to remove group');
    }
  };

  // Fetch user settings
  const fetchUserSettings = async () => {
    try {
      setSettingsLoading(true);
      const response = await settingsService.getUserSettings();
      if (response.data.success && response.data.data) {
        setUserSettings(response.data.data);
        setTradingActive(response.data.data.autoTradeEnabled);
        setPriceLevelAutoExecute(response.data.data.priceLevelAutoExecute || false);
        setOrderPlacementEnabled(response.data.data.orderPlacementEnabled !== false);
      }
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Handle global kill switch toggle
  const handleOrderPlacementToggle = async () => {
    const next = !orderPlacementEnabled;
    if (!next) {
      const confirmed = window.confirm(
        'Disable order placement?\n\nThis is a GLOBAL kill switch. While OFF, NO new ENTRY order will be placed — automated strategy entries and manual entries are both blocked.\n\nProtective EXITS still run: stop-loss, target, trailing-SL and manual / square-off closes are reduce-only and are NEVER blocked, so open positions can always be managed. (The daily-loss cap works the same way — entries only.)'
      );
      if (!confirmed) return;
    }
    setOrderPlacementEnabled(next);
    try {
      await settingsService.updateUserSettings({ orderPlacementEnabled: next });
      toast.success(next ? 'Order placement ENABLED' : 'Order placement DISABLED — new ENTRY orders rejected (exits still run)');
      fetchUserSettings();
    } catch (error) {
      console.error('Failed to update order placement state:', error);
      setOrderPlacementEnabled(!next);
      toast.error('Failed to update kill switch');
    }
  };

  // Handle trading active toggle
  const handleTradingToggle = async () => {
    const newTradingState = !tradingActive;
    setTradingActive(newTradingState);

    try {
      await settingsService.updateUserSettings({ autoTradeEnabled: newTradingState });
      // Refresh settings to ensure sync
      fetchUserSettings();
    } catch (error) {
      console.error('Failed to update trading state:', error);
      // Revert on error
      setTradingActive(!newTradingState);
    }
  };

  // Track IP whitelist error attempts
  const [ipWhitelistErrorCount, setIpWhitelistErrorCount] = React.useState(0);
  const maxIpWhitelistAttempts = 3;

  // Handle position sync with IP whitelist error tracking
  const syncPositionsWithRetry = async () => {
    // Skip if we've exceeded max attempts for IP whitelist errors
    if (ipWhitelistErrorCount >= maxIpWhitelistAttempts) {
      console.log('Skipping position sync - IP whitelist error limit reached');
      return;
    }

    try {
      await tradingService.syncPositions();
      // Reset error count on success
      setIpWhitelistErrorCount(0);
    } catch (error: any) {
      // Check if it's an IP whitelist error (503 with IP_NOT_WHITELISTED code)
      if (error.response?.status === 503 && error.response?.data?.error?.code === 'IP_NOT_WHITELISTED') {
        const newCount = ipWhitelistErrorCount + 1;
        setIpWhitelistErrorCount(newCount);
        console.error(`IP whitelist error (attempt ${newCount}/${maxIpWhitelistAttempts}):`, error.response.data.error.message);

        if (newCount >= maxIpWhitelistAttempts) {
          toast.error(`Position sync disabled: ${error.response.data.error.message}. Please whitelist IP: ${error.response.data.error.clientIp}`, {
            duration: 10000
          });
        }
      } else {
        console.error('Failed to sync positions:', error);
      }
      throw error;
    }
  };

  // Handle manual refresh data
  const handleRefreshData = async () => {
    try {
      console.log('Manual refresh triggered - syncing positions...');
      // Sync positions from Delta Exchange
      await syncPositionsWithRetry();
      // Refresh dashboard summary
      dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
      console.log('Position sync and dashboard refresh completed');
    } catch (error) {
      console.error('Failed to refresh data:', error);
      // Still fetch dashboard even if sync fails
      dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
    }
  };

  // Handle square off trade
  const handleSquareOff = async (trade: any) => {
    try {
      toast.loading(`Closing ${trade.symbol} position...`, { id: `square-off-${trade.id}` });

      // Close the trade with market order
      await tradingService.closeTrade(trade.id, {
        orderType: 'MARKET',
        quantity: trade.quantity
      });

      toast.success(`Successfully closed ${trade.symbol} position`, { id: `square-off-${trade.id}` });

      // Refresh data
      await handleRefreshData();
    } catch (error: any) {
      console.error('Failed to square off trade:', error);
      toast.error(
        `Failed to close position: ${error.response?.data?.error?.message || error.message}`,
        { id: `square-off-${trade.id}` }
      );
    }
  };

  // Handle execute alert (place order from alert)
  const handleExecuteAlert = async (alertId: number, symbol?: string) => {
    try {
      toast.loading(`Executing alert${symbol ? ` for ${symbol}` : ''}...`, { id: `execute-${alertId}` });
      await tradingService.executeAlert(alertId);
      toast.success(`Successfully executed alert${symbol ? ` for ${symbol}` : ''}`, { id: `execute-${alertId}` });
      // Refresh dashboard data
      await handleRefreshData();
    } catch (error: any) {
      console.error('Failed to execute alert:', error);
      toast.error(
        `Failed to execute alert: ${error.response?.data?.message || error.message}`,
        { id: `execute-${alertId}` }
      );
    }
  };

  // Handle dismiss alert
  const handleDismissAlert = async (alert: any) => {
    try {
      toast.loading(`Dismissing alert for ${alert.symbol}...`, { id: `dismiss-${alert.id}` });
      await tradingService.dismissAlert(alert.id);
      toast.success(`Alert for ${alert.symbol} dismissed`, { id: `dismiss-${alert.id}` });
      dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
    } catch (error: any) {
      console.error('Failed to dismiss alert:', error);
      toast.error(
        `Failed to dismiss alert: ${error.response?.data?.error?.message || error.message}`,
        { id: `dismiss-${alert.id}` }
      );
    }
  };

  // Handle execute alert with custom params
  const handleExecuteWithCustomParams = async (alertId: number) => {
    const body: any = {};
    if (alertCustomEntry && alertCustomOrderType !== 'MARKET') body.customEntryPrice = parseFloat(alertCustomEntry);
    if (alertCustomSL) body.customSL = parseFloat(alertCustomSL);
    if (alertCustomTarget) body.customTarget = parseFloat(alertCustomTarget);
    if (alertCustomQuantity) body.customQuantity = parseInt(alertCustomQuantity);
    if (alertCustomOrderType) body.orderType = alertCustomOrderType;

    try {
      toast.loading('Placing order...', { id: `exec-custom-${alertId}` });
      await api.post(`/alerts/${alertId}/execute`, body);
      toast.success('Order placed successfully', { id: `exec-custom-${alertId}` });
      setEditingAlertId(null);
      setAlertCustomEntry('');
      setAlertCustomSL('');
      setAlertCustomTarget('');
      setAlertRenotifyAt('');
      setAlertCustomQuantity('');
      setAlertCustomOrderType('LIMIT');
      dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
    } catch (error: any) {
      toast.error(`Failed: ${error.response?.data?.error?.message || error.message}`, { id: `exec-custom-${alertId}` });
    }
  };

  useEffect(() => { reloadLvlAtts(); }, [watchlistData, reloadLvlAtts]);

  // Save price level edits for an instrument
  const savePriceLevels = async (instId: number) => {
    try {
      const body: any = {
        nbuy: plEditValues.nbuy !== '' ? parseFloat(plEditValues.nbuy) : null,
        nshort: plEditValues.nshort !== '' ? parseFloat(plEditValues.nshort) : null,
        bbuy: plEditValues.bbuy !== '' ? parseFloat(plEditValues.bbuy) : null,
        bshort: plEditValues.bshort !== '' ? parseFloat(plEditValues.bshort) : null,
      };
      await api.put(`/instruments/${instId}`, body);
      setPlEditId(null);
      // Refresh watchlist data
      const res = await api.get<{ success: boolean; data: { watchlist: any[]; priceLevels: any[] } }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } });
      if (res.data.success) setWatchlistData(res.data.data);
      toast.success('Price levels updated');
    } catch (e: any) {
      toast.error('Failed to save price levels');
    }
  };

  // Create quick alert from watchlist (BBUY for long, BSHORT for short)

  // Remove instrument from Price Level Watch (clears all 4 trigger levels)
  const removeFromPriceLevels = async (instId: number, symbol: string) => {
    try {
      await api.put(`/instruments/${instId}`, { nbuy: null, nshort: null, bbuy: null, bshort: null });
      const res = await api.get<{ success: boolean; data: { watchlist: any[]; priceLevels: any[] } }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } });
      if (res.data.success) setWatchlistData(res.data.data);
      toast.success(`${symbol} removed from Pending Manual Trigger Watch`);
    } catch {
      toast.error('Failed to remove from Price Level Watch');
    }
  };

  // Remove a single HVN level (from Price Level Watch's HVN sub-list).
  const removeHvnLevel = async (id: number, symbol: string) => {
    try {
      await api.delete(`/hvn/${id}`);
      const res = await api.get<{ success: boolean; data: { watchlist: any[]; priceLevels: any[]; hvnLevels?: any[] } }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } });
      if (res.data.success) setWatchlistData(res.data.data);
      toast.success(`HVN level removed${symbol ? ` for ${symbol}` : ''}`);
    } catch {
      toast.error('Failed to remove HVN level');
    }
  };

  // Remove instrument from watchlist
  const removeFromWatchlist = async (instId: number, field: 'wlLong' | 'wlShort') => {
    try {
      await api.put(`/instruments/${instId}`, { [field]: false });
      const res = await api.get<{ success: boolean; data: { watchlist: any[]; priceLevels: any[] } }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } });
      if (res.data.success) setWatchlistData(res.data.data);
    } catch (e: any) {
      toast.error('Failed to update watchlist');
    }
  };

  // Toggle the per-user ★ favourite for a watchlist row.
  const toggleWatchlistFav = async (inst: any) => {
    const newVal = !inst.isFavourite;
    try {
      await api.put(`/instruments/${inst.id}`, { isFavourite: newVal });
      const res = await api.get<{ success: boolean; data: any }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } });
      if (res.data.success) setWatchlistData(res.data.data);
    } catch { toast.error('Failed to update favourite'); }
  };

  // ── Bulk-remove helpers (R128) ──────────────────────────────────────────────
  const refetchWatchlist = async () => {
    const res = await api.get<{ success: boolean; data: any }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } });
    if (res.data.success) setWatchlistData(res.data.data);
  };

  // Watchlist — remove every symbol from the Long (or Short) watchlist.
  const clearAllWatchlist = async (field: 'wlLong' | 'wlShort') => {
    const items = (watchlistData?.watchlist || []).filter((i: any) => i[field]);
    if (!items.length) return;
    const label = field === 'wlLong' ? 'Long' : 'Short';
    if (!window.confirm(`Remove all ${label} watchlist symbols (${items.length})?`)) return;
    try {
      await Promise.all(items.map((i: any) => api.put(`/instruments/${i.id}`, { [field]: false })));
      await refetchWatchlist();
      toast.success(`Cleared ${items.length} ${label} watchlist symbol${items.length > 1 ? 's' : ''}`);
    } catch { toast.error(`Failed to clear ${label} watchlist`); }
  };

  // Price Level Watch — clear all Long (NBUY/BBUY) or Short (NSHORT/BSHORT) levels.
  const clearPriceLevelsBySide = async (side: 'long' | 'short') => {
    const keys = side === 'long' ? ['nbuy', 'bbuy'] : ['nshort', 'bshort'];
    const items = (watchlistData?.priceLevels || []).filter((p: any) => keys.some(k => p[k] != null));
    if (!items.length) return;
    const label = side === 'long' ? 'Long (NBUY/BBUY)' : 'Short (NSHORT/BSHORT)';
    if (!window.confirm(`Remove all ${label} entries across ${items.length} symbol${items.length > 1 ? 's' : ''}?`)) return;
    try {
      const body = side === 'long' ? { nbuy: null, bbuy: null } : { nshort: null, bshort: null };
      await Promise.all(items.map((p: any) => api.put(`/instruments/${p.id}`, body)));
      await refetchWatchlist();
      toast.success(`Cleared all ${side === 'long' ? 'Long' : 'Short'} price-level entries`);
    } catch { toast.error('Failed to clear price levels'); }
  };

  // Price Level Watch — remove every HVN level.
  const clearAllHvn = async () => {
    const hvns = (watchlistData?.hvnLevels || []);
    if (!hvns.length) return;
    if (!window.confirm(`Remove all HVN entries (${hvns.length})?`)) return;
    try {
      await Promise.all(hvns.map((h: any) => api.delete(`/hvn/${h.id}`)));
      await refetchWatchlist();
      toast.success(`Cleared ${hvns.length} HVN entr${hvns.length > 1 ? 'ies' : 'y'}`);
    } catch { toast.error('Failed to clear HVN levels'); }
  };

  // Pending Entry Watch — cancel every PENDING entry on a side (BUY = Long, SHORT = Short).
  const cancelAllPending = async (side: 'BUY' | 'SHORT') => {
    const items = pendingEntries.filter(p => p.status === 'PENDING' && p.side === side);
    if (!items.length) return;
    const label = side === 'BUY' ? 'Long' : 'Short';
    if (!window.confirm(`Cancel all pending ${label} entries (${items.length})?`)) return;
    try {
      await Promise.all(items.map(p => api.delete(`/pending-entries/${p.id}`)));
      await fetchPendingEntries();
      toast.success(`Cancelled ${items.length} pending ${label} entr${items.length > 1 ? 'ies' : 'y'}`);
    } catch { toast.error(`Failed to cancel pending ${label} entries`); }
  };

  // Helper to open alert edit panel with pre-populated parsedData values
  const openAlertEditPanel = (alert: any) => {
    const pd = alert.parsedData || {};
    const inst = alert.instrumentData || alert.instrument || null;
    const dp = inst?.decimalPrecision != null ? Number(inst.decimalPrecision) : 2;
    const fmt = (v: any) => {
      if (v == null || v === '') return '';
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      return n.toFixed(dp);
    };

    setEditingAlertId(alert.id);
    setAlertCustomEntry(
      alert.customEntryPrice != null ? fmt(alert.customEntryPrice)
      : alert.currentPrice ? fmt(alert.currentPrice)
      : pd.entryLevel ? fmt(pd.entryLevel)
      : pd.price ? fmt(pd.price)
      : ''
    );
    setAlertCustomSL(alert.customSL != null ? fmt(alert.customSL) : (pd.isl ? fmt(pd.isl) : ''));
    setAlertCustomTarget(
      alert.customTarget != null ? fmt(alert.customTarget)
      : pd.target ? fmt(pd.target)
      : pd.tgt ? fmt(pd.tgt)
      : ''
    );
    setAlertRenotifyAt(alert.renotifyAt != null ? String(alert.renotifyAt) : '');
    setAlertCustomOrderType(alert.customEntryPrice || pd.price ? 'LIMIT' : 'MARKET');

    // Auto-compute Qty from the user's "Size orders as" + investment + leverage:
    //   Equity → eqInvestmentAmount × leverage; Futures (incl. Delta perps) → futInvestmentAmount × leverage.
    // Auto (unset) = Futures on DELTA / NFO / BFO / MCX / CDS / NCO, Equity otherwise.
    if (pd.quantity) {
      setAlertCustomQuantity(String(pd.quantity));
    } else if (inst) {
      const exch = String(inst.exchange || '').toUpperCase();
      // "Size orders as": explicit Equity/Futures, else Auto (server rule, utils/sizeOrdersAs).
      const seg = effectiveSegment(exch, inst.instrumentType, inst.tradeSegment);
      const investAmt = seg === 'EQUITY' ? (Number(inst.eqInvestmentAmount) || 0) : (Number(inst.futInvestmentAmount) || 0);
      const useLeverage = true;

      const price = parseFloat(String(alert.currentPrice || alert.customEntryPrice || pd.entryLevel || pd.price || 0));
      const lotSize = Number(inst.lotSize) || 1;
      const leverage = useLeverage ? (Number(inst.leverage) || 1) : 1;
      // Investment is denominated in INR. Delta prices are in USD — convert
      // INR investment to the price's USD unit before dividing.
      const isUsdPrice = exch === 'DELTA';
      const usdToInr = isUsdPrice ? USD_TO_INR : 1;

      if (investAmt > 0 && price > 0 && lotSize > 0) {
        const investAmtInPriceUnit = investAmt / usdToInr;
        const qty = Math.max(1, Math.floor((investAmtInPriceUnit * leverage) / (price * lotSize)));
        setAlertCustomQuantity(String(qty));
      } else {
        setAlertCustomQuantity('');
      }
    } else {
      setAlertCustomQuantity('');
    }
  };

  // Handle save renotify/custom prices without executing
  const handleSaveAlertCustomPrices = async (alertId: number) => {
    const body: any = {};
    if (alertCustomEntry) body.customEntryPrice = parseFloat(alertCustomEntry);
    if (alertCustomSL) body.customSL = parseFloat(alertCustomSL);
    if (alertCustomTarget) body.customTarget = parseFloat(alertCustomTarget);
    if (alertRenotifyAt) body.renotifyAt = parseFloat(alertRenotifyAt);

    try {
      await api.put(`/alerts/${alertId}/custom-prices`, body);
      toast.success('Alert prices saved');
      setEditingAlertId(null);
      setAlertCustomEntry('');
      setAlertCustomSL('');
      setAlertCustomTarget('');
      setAlertRenotifyAt('');
      dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
    } catch (error: any) {
      toast.error(`Failed to save: ${error.response?.data?.error?.message || error.message}`);
    }
  };

  // Handle park alert (by alertId)
  const handleParkAlert = async (alertId: number, symbol?: string) => {
    try {
      toast.loading(`Parking alert${symbol ? ` for ${symbol}` : ''}...`, { id: `park-${alertId}` });
      await tradingService.parkAlert(alertId);
      toast.success(`Alert${symbol ? ` for ${symbol}` : ''} has been parked`, { id: `park-${alertId}` });
      dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
    } catch (err: any) {
      console.error('Failed to park alert:', err);
      toast.error(
        `Failed to park alert: ${err.response?.data?.error?.message || err.message}`,
        { id: `park-${alertId}` }
      );
      setSnackbarMessage(`Failed to park alert: ${err.message}`);
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    // Initial load: sync positions and fetch dashboard
    const initializeData = async () => {
      try {
        await syncPositionsWithRetry();
        dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
      } catch (error) {
        console.error('Failed to sync positions on mount:', error);
        // Still fetch dashboard even if sync fails
        dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
      }
      // R78 — Intraday-PnL and portfolio-chart fetches removed along with the
      // Top Stats Cards that consumed them.
    };

    initializeData();
    fetchUserSettings();
  }, [dispatch, alertPeriod]);

  // Fetch system performance
  useEffect(() => {
    const fetchPerf = async () => {
      try {
        setPerfLoading(true);
        const [perfResp, pnlResp, pgResp] = await Promise.all([
          tradingService.getSystemPerformance(perfPeriod),
          api.get<any>(`/dashboard/charts/daily-pnl?period=${perfPeriod}d`, { headers: { _suppressToast: 'true' } }),
          api.get<any>(`/dashboard/charts/portfolio-growth?period=${perfPeriod <= 7 ? '1W' : perfPeriod <= 31 ? '1M' : perfPeriod <= 92 ? '3M' : '1Y'}`, { headers: { _suppressToast: 'true' } }),
        ]);
        if (perfResp.data.success) setPerfData(perfResp.data.data);
        if (pnlResp.data.success) setPerfDailyPnl(pnlResp.data.data || []);
        setPerfPortfolio(Array.isArray(pgResp.data?.data) ? pgResp.data.data : []);
      } catch (e) {
        console.error('Failed to fetch system performance:', e);
      } finally {
        setPerfLoading(false);
      }
    };
    fetchPerf();
  }, [perfPeriod]);

  // Fetch watchlist data — also refresh whenever the tab regains focus, so that
  // levels/watchlist updates made on the Charts page (which writes to the same
  // user_instrument_prefs table) appear immediately when the user comes back.
  useEffect(() => {
    let cancelled = false;
    const fetchWatchlist = async () => {
      try {
        const res = await api.get<{ success: boolean; data: { watchlist: any[]; priceLevels: any[] } }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } });
        if (!cancelled && res.data.success) setWatchlistData(res.data.data);
      } catch (e) { /* non-critical */ }
    };
    fetchWatchlist();
    const onVisible = () => { if (document.visibilityState === 'visible') fetchWatchlist(); };
    const onFocus = () => fetchWatchlist();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Set up auto-refresh based on user settings
  useEffect(() => {
    if (!userSettings?.refreshIntervalSeconds) return;

    const refreshInterval = userSettings.refreshIntervalSeconds * 1000;

    const interval = setInterval(async () => {
      try {
        // Sync positions from Delta Exchange
        await syncPositionsWithRetry();
      } catch (error) {
        console.error('Failed to sync positions:', error);
      }
      // Refresh dashboard summary
      dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
      fetchUserSettings();
      // Refresh watchlist/price-level prices
      api.get<{ success: boolean; data: { watchlist: any[]; priceLevels: any[] } }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } })
        .then(res => { if (res.data.success) setWatchlistData(res.data.data); })
        .catch(() => {});
      // R78 — Intraday-PnL refresh removed along with the Top Stats Cards.
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [dispatch, userSettings?.refreshIntervalSeconds, ipWhitelistErrorCount, alertPeriod]);

  const todayPnl = summary?.pnl?.today?.amount || 0;
  const portfolioValue = summary?.portfolio?.totalValue || 0;
  const availableMargin = summary?.portfolio?.availableMargin || 0;
  const activePositions = summary?.positions?.total || 0;
  // R78 — todayPnlPercent + portfolio-reserve warning derivations removed
  // along with the Top Stats Cards. Only portfolioValue survives because
  // it feeds totalOpenRiskPct in the Trading Controls section below.

  // Calculate total unrealized P&L from open trades
  const totalUnrealizedPnl = summary?.recentTrades?.reduce((sum: number, trade: any) => {
    if (trade.status === 'OPEN') {
      return sum + parseFloat(trade.unrealizedPnl || 0);
    }
    return sum;
  }, 0) || 0;

  // Total Open Risk — sum of SL Risk across every OPEN/DRAFT trade in the
  // Trades Management list. This is the maximum loss the user takes if every
  // active trade hits its stop. Mirror per-trade math used on the Trades row
  // (line ~1675): risk = (entry − operativeSL) × qty × lot × usdToInr for
  // LONG, symmetric for SHORT. usdToInr is 85 for Delta, 1 elsewhere.
  // Profit-locked trades (TSL above entry for LONG) contribute a NEGATIVE
  // value — i.e. they reduce the open-risk total because their stop is
  // already in profit. That's intentional: we want a true "if everything
  // hits its stop right now, what's my outcome" number.
  // Round 29 — Open Risk must equal the sum of the SL Risk column shown
  // in the Trades Management table (with sign). The column uses
  // `manualStopLoss || stopLoss` and intentionally ignores
  // `currentTrailingSl` so the headline number matches what the user sees
  // row-by-row. Profit-locked trades (TSL > entry for LONG) used to be
  // silently flipping the headline negative even though the column itself
  // showed positive values — that mismatch is fixed here.
  // Audit A-021: a position with NO stop has unbounded risk; it used to add 0, so ten
  // unstopped positions read "Open risk ₹0.00". Count them so the tile can say so.
  const positionsWithoutSl = (summary?.recentTrades || []).filter((trade: any) => {
    if (trade.status !== 'OPEN' && trade.status !== 'DRAFT') return false;
    const has = (v: any) => v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0;
    return !has(trade.currentTrailingSl) && !has(trade.manualStopLoss) && !has(trade.stopLoss);
  }).length;
  const totalOpenRisk = (summary?.recentTrades || []).reduce((sum: number, trade: any) => {
    if (trade.status !== 'OPEN' && trade.status !== 'DRAFT') return sum;
    // Must match the per-row SL Risk column EXACTLY (the user defines Open Risk
    // as the sum of that column). The column's operative SL = most-protective of
    // [ TSL , (manualStopLoss || stopLoss) ] — i.e. a manual SL OVERRIDES the
    // initial SL, then TSL competes via max(LONG)/min(SHORT). Mirror that here.
    const tsl = (trade.currentTrailingSl != null && Number.isFinite(Number(trade.currentTrailingSl)))
      ? Number(trade.currentTrailingSl) : null;
    const isl = (trade.manualStopLoss || trade.stopLoss);
    const slCands: number[] = [];
    if (tsl != null) slCands.push(tsl);
    if (isl != null && Number.isFinite(Number(isl))) slCands.push(Number(isl));
    if (!slCands.length || !trade.averageEntryPrice || !trade.quantity) return sum;
    const sl = trade.side === 'LONG' ? Math.max(...slCands) : Math.min(...slCands);
    const lotSz = trade.lotSize || 1;
    const exch = String(trade.exchange || trade.instrument?.exchange || '').toUpperCase();
    const usdToInr = exch === 'DELTA' ? USD_TO_INR : 1;
    const entry = Number(trade.averageEntryPrice);
    const qty = Number(trade.quantity);
    const risk = trade.side === 'LONG'
      ? (entry - sl) * qty * lotSz * usdToInr
      : (sl - entry) * qty * lotSz * usdToInr;
    return sum + (Number.isFinite(risk) ? risk : 0);
  }, 0);
  const totalPortfolioForRiskPct = portfolioValue || 0;
  const totalOpenRiskPct = totalPortfolioForRiskPct > 0
    ? (totalOpenRisk / totalPortfolioForRiskPct) * 100
    : 0;

  // Group alerts by symbol for accordion display
  // Load FNO assessments for all visible alerts
  useEffect(() => {
    const allAlerts = [
      ...(summary?.recentAlerts || []),
      ...(summary?.parkedAlerts || [])
    ];
    const ids = allAlerts.map((a: any) => a.id).filter(Boolean);
    if (ids.length) dispatch(fetchAssessmentsBatch(ids));
  }, [summary?.recentAlerts, summary?.parkedAlerts, dispatch]);

  const groupedAlerts = useMemo(() => {
    const groups: Record<string, any[]> = {};
    const alertsToShow = summary?.recentAlerts || [];

    alertsToShow.forEach((alert: any) => {
      const symbol = alert.instrument?.symbol || alert.symbol || alert.parsedData?.symbol || 'UNKNOWN';
      if (!groups[symbol]) groups[symbol] = [];
      groups[symbol].push(alert);
    });

    // Sort each group's alerts by createdAt desc, then cap at 10 per symbol
    Object.keys(groups).forEach(sym => {
      groups[sym].sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      groups[sym] = groups[sym].slice(0, 10);
    });

    // Sort groups by their latest alert
    return Object.entries(groups).sort(([, a], [, b]) =>
      new Date((b as any[])[0].createdAt).getTime() - new Date((a as any[])[0].createdAt).getTime()
    );
  }, [summary?.recentAlerts]);

  // Group parked alerts by symbol (Issue 10/12).
  // The period dropdown filters at the ALERT level (each alert by its PARKED
  // date = acknowledgedAt, falling back to createdAt), NOT at the symbol/group
  // level. So a symbol shows only its alerts parked within the window — it is
  // NOT pulled in (with all its old alerts) just because its newest alert is
  // recent. Surviving alerts are grouped by symbol, newest-parked first, the
  // most-recently-parked symbol on top, and capped at 10 alerts per symbol.
  const groupedParkedAlerts = useMemo(() => {
    const parkedTime = (a: any) => new Date(a?.acknowledgedAt || a?.createdAt || 0).getTime();
    let cutoff = -Infinity;
    if (parkedAlertPeriod !== 'all') {
      const days = parkedAlertPeriod === '90d' ? 90 : parseInt(parkedAlertPeriod);
      if (Number.isFinite(days)) cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    }
    const groups: Record<string, any[]> = {};
    const parkedToShow = summary?.parkedAlerts || [];
    parkedToShow.forEach((alert: any) => {
      if (parkedTime(alert) < cutoff) return; // drop alerts parked outside the window
      const symbol = alert.instrument?.symbol || alert.symbol || alert.parsedData?.symbol || 'UNKNOWN';
      if (!groups[symbol]) groups[symbol] = [];
      groups[symbol].push(alert);
    });
    Object.keys(groups).forEach(sym => {
      groups[sym].sort((a: any, b: any) => parkedTime(b) - parkedTime(a)); // newest parked first
      groups[sym] = groups[sym].slice(0, 10); // max 10 alerts per symbol when expanded
    });
    return Object.entries(groups).sort(([, a], [, b]) =>
      parkedTime((b as any[])[0]) - parkedTime((a as any[])[0]) // most-recently-parked symbol on top
    );
  }, [summary?.parkedAlerts, parkedAlertPeriod]);

  const toggleParkedSymbol = (symbol: string) => {
    setExpandedParkedSymbols(prev => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol); else next.add(symbol);
      return next;
    });
  };

  // Search instruments for Add Symbol dialog
  const searchInstrumentsForDialog = async (q: string) => {
    if (!q.trim()) { setAddSymbolResults([]); return; }
    setAddSymbolSearching(true);
    try {
      const res = await api.get<any>(`/instruments?search=${encodeURIComponent(q)}&limit=10`);
      const items = res.data?.data?.instruments || res.data?.instruments || [];
      setAddSymbolResults(items);
    } catch { setAddSymbolResults([]); }
    finally { setAddSymbolSearching(false); }
  };

  // Add symbol from dialog
  const handleAddSymbolFromDialog = async (inst: any) => {
    if (addSymbolDialog.mode === 'priceLevelWatch') {
      // Go to step 2: set initial price levels
      setAddSymbolSelected(inst);
      setAddSymbolPLValues({ nbuy: '', nshort: '', bbuy: '', bshort: '' });
      return;
    }
    try {
      const field = addSymbolDialog.mode === 'wlLong' ? 'wlLong' : 'wlShort';
      await api.put(`/instruments/${inst.id}`, { [field]: true });
      const res = await api.get<{ success: boolean; data: { watchlist: any[]; priceLevels: any[] } }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } });
      if (res.data.success) setWatchlistData(res.data.data);
      toast.success(`${inst.symbol} added to ${addSymbolDialog.mode === 'wlLong' ? 'Long' : 'Short'} Watchlist`);
      setAddSymbolDialog({ open: false, mode: 'priceLevelWatch' });
      setAddSymbolSearch('');
      setAddSymbolResults([]);
    } catch { toast.error('Failed to add symbol'); }
  };

  // Confirm adding symbol to Price Level Watch with initial levels
  const handleConfirmPriceLevelAdd = async () => {
    if (!addSymbolSelected) return;
    const { nbuy, nshort, bbuy, bshort } = addSymbolPLValues;
    const payload: any = {
      nbuy: nbuy ? parseFloat(nbuy) : null,
      nshort: nshort ? parseFloat(nshort) : null,
      bbuy: bbuy ? parseFloat(bbuy) : null,
      bshort: bshort ? parseFloat(bshort) : null
    };
    if (!payload.nbuy && !payload.nshort && !payload.bbuy && !payload.bshort) {
      toast.error('Set at least one price level'); return;
    }
    try {
      await api.put(`/instruments/${addSymbolSelected.id}`, payload);
      const res = await api.get<{ success: boolean; data: { watchlist: any[]; priceLevels: any[] } }>('/dashboard/watchlist', { headers: { _suppressToast: 'true' } });
      if (res.data.success) setWatchlistData(res.data.data);
      toast.success(`${addSymbolSelected.symbol} added to Pending Manual Trigger Watch`);
      setAddSymbolDialog({ open: false, mode: 'priceLevelWatch' });
      setAddSymbolSearch(''); setAddSymbolResults([]); setAddSymbolSelected(null);
    } catch { toast.error('Failed to add symbol'); }
  };

  if (loading && !summary) {
    // Phase A.1 — skeleton shell matching the actual Dashboard layout
    // (sticky Risk Cockpit + market-state header + KPI strip + 3 main cards)
    // gives a much better perceived-load feel than a single spinner.
    return (
      <Box>
        <Box sx={{ mb: 1.5, height: 64, borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }} />
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[0, 1, 2, 3, 4, 5].map(i => (
            <Box key={i} sx={{ width: 110, height: 30, borderRadius: 1, bgcolor: 'action.hover' }} />
          ))}
        </Box>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[0, 1, 2, 3].map(i => (
            <Grid key={i} size={{ xs: 6, sm: 6, md: 3 }}><KpiSkeleton /></Grid>
          ))}
        </Grid>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, md: 4 }}><CardSkeleton rows={5} /></Grid>
          <Grid size={{ xs: 12, md: 4 }}><CardSkeleton rows={5} /></Grid>
          <Grid size={{ xs: 12, md: 4 }}><CardSkeleton rows={5} /></Grid>
        </Grid>
      </Box>
    );
  }

  const lvlDlgEl = lvlDlg ? (
    <LevelOptionDialog
      open
      onClose={() => setLvlDlg(null)}
      instrumentId={lvlDlg.inst.id}
      symbol={lvlDlg.inst.symbol}
      levelKey={lvlDlg.key}
      levelValue={lvlDlg.inst[lvlDlg.key] != null ? Number(lvlDlg.inst[lvlDlg.key]) : null}
      existing={lvlAtts.find(a => a.instrumentId === lvlDlg.inst.id && a.levelKey === lvlDlg.key && (a.status === 'ARMED' || a.status === 'FIRING')) || null}
      autoExecute={priceLevelAutoExecute}
      onSaved={reloadLvlAtts}
    />
  ) : null;

  return (
    /* RESERVE THE RAIL'S GUTTER.
       The Jump rail is position:fixed and takes no space in the flow, so it used
       to draw ON TOP of every card at laptop widths — at 1366px it covered the
       first ~90px of the page and "RISK COCKPIT" read "encil to set cap".
       Padding the content by exactly the rail's width, at the same breakpoint
       the rail appears at, makes the overlap impossible instead of unlikely. */
    <Box sx={{ pl: { xs: 0, md: `${RAIL_GUTTER_PX}px` } }}>
      {/* Risk Cockpit (Phase A · V2 + Phase B circuit-breaker wiring).
          R78a — daily-loss-cap box + Open Risk / Today P&L / Available Margin
          / Open Positions sub-tiles all share one wrapping flex row. The
          tiles were briefly hidden via capOnly in R78 but the user wants
          them back since the Top Stats Cards section below was removed. */}
      <RiskCockpit
        totalOpenRisk={totalOpenRisk}
        positionsWithoutSl={positionsWithoutSl}
        todayPnl={todayPnl}
        availableMargin={availableMargin}
        openPositions={activePositions}
        portfolioValue={portfolioValue}
        cap={(userSettings as any)?.dailyLossCap ?? null}
        capEnforced={!!(userSettings as any)?.dailyLossCapEnabled}
      />

      {/* Quick-Trade floating panel (Phase A · V3) */}
      <QuickTradePanel />

      {/* R78 — EventTicker moved into the top Trading Controls area below. */}

      {/* A.1 — Sticky section-anchor strip (replaces dropped 3-col reorg).
          R78 — market status + Auto/Speech/Telegram switches now ride along
          in the same fixed left rail, removing the wide horizontal header. */}
      <AnchorStrip
        anchors={[
          { id: 'heatmap',         label: 'Heatmap' },
          { id: 'trades',          label: 'Trades' },
          { id: 'autotrade-watch', label: 'AutoTrade Watch', short: 'AutoTrade' },
          { id: 'pending',         label: 'Manual Entry',    short: 'Entry' },
          { id: 'price-watch',     label: 'Manual Trigger',  short: 'Trigger' },
          { id: 'alerts',          label: 'Alerts', badge: (summary?.recentAlerts || []).length || undefined },
          { id: 'parked',          label: 'Parked' },
          { id: 'watchlist',       label: 'Watchlist' },
          { id: 'sysperf',         label: 'Performance' },
        ]}
        footer={
          <MarketStatusRail
            autoTradeEnabled={userSettings?.autoTradeEnabled}
            speechAlertEnabled={userSettings?.speechAlertEnabled}
            telegramNotificationEnabled={userSettings?.telegramNotificationEnabled}
          />
        }
      />

      {/* R78 — Market status header removed; the badges + Auto/Speech/Telegram
          switches now live inside the fixed left rail (see MarketStatusRail
          passed to <AnchorStrip footer={…}/> above). */}
      {/* R78 — SymbolHeatmap relocated to just above the Trades Management
          section (search for "Watchlist heatmap" further down in this file). */}

      {/* IP Warning Banner */}
      {ipWarning && (
        <Alert
          severity="error"
          sx={{ mx: 0, mb: 1, borderRadius: 2 }}
          action={
            <Button size="small" onClick={() => setIpWarning(null)} sx={{ color: 'inherit' }}>
              DISMISS
            </Button>
          }
        >
          <AlertTitle>Delta Exchange API Error</AlertTitle>
          {ipWarning}
        </Alert>
      )}

      {/* R78 — Top Stats Cards removed (Today P&L / Portfolio Value /
          Available Margin / Active Positions). All four duplicated info
          shown in detail inside Trading Controls + Trades Management below. */}

      {/* R78 — Events ticker moved here, embedded with Trading Controls. */}
      <EventTicker />

      {/* The nightly AI board, condensed. Placed above the trading controls because the point of
          the layer is that the landing page leads with the few things worth acting on rather than
          a raw alert list. Renders nothing at all until a run has happened, never triggers a run,
          and never calls a model — the Dashboard cannot spend money. */}
      <AiBoardCard />

      {/* Live Trading Control */}
      <Paper sx={{
        p: 3,
        mb: 2,
        bgcolor: 'background.paper',
        borderLeft: `4px solid ${orderPlacementEnabled ? C.neutral : C.bear}`,
        boxShadow: 2,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-1px)'
        }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                Trading Controls
              </Typography>
              <Tooltip
                arrow
                placement="right"
                title="Master controls — Order Placement (the kill switch that gates every broker order), Auto Trade (executes CONFIRMED=Y alerts automatically), and Open P&L / Open Risk read-outs. The order gate ALSO enforces your daily-loss cap (Settings → Trading Preferences) on every entry order, per-user since R80."
              >
                <IconButton size="small" sx={{ color: C.neutral }}>
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <HelpLink to="risk-management/kill-switch" label="Risk guide" variant="icon" />
              {!orderPlacementEnabled && (
                <Chip
                  size="small"
                  label="ORDERS DISABLED"
                  sx={{
                    bgcolor: C.bearMuted,
                    color: C.bear,
                    fontWeight: 700,
                    border: `1px solid ${C.bearBorder}`,
                    letterSpacing: 0.5,
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Monitor and control automated trading system
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontSize: CH_TYPE.body }}>
                Open P&L
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: totalUnrealizedPnl >= 0 ? C.bull : C.bear }}>
                {formatCurrency(totalUnrealizedPnl)}
              </Typography>
            </Box>
            {/* Open Risk read-out removed here — already surfaced in the
                topmost Risk Cockpit strip (avoids duplication). The
                totalOpenRisk / totalOpenRiskPct computations are retained
                above because the Risk Cockpit consumes them. */}
            <Tooltip
              title={
                orderPlacementEnabled
                  ? 'KILL SWITCH — click to BLOCK new ENTRY orders. Protective EXITS (stop-loss, target, trail, square-off) are STILL allowed so open positions can always be managed/closed. The daily-loss cap likewise blocks entries only.'
                  : 'New ENTRIES are BLOCKED (exits/stop-loss still run). Click to re-enable entry order placement.'
              }
              arrow
              placement="top"
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={orderPlacementEnabled}
                    onChange={handleOrderPlacementToggle}
                    disabled={settingsLoading}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: C.bull },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: C.bull },
                      '& .MuiSwitch-switchBase:not(.Mui-checked)': { color: C.bear },
                      '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': { backgroundColor: C.bear, opacity: 0.5 },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: CH_TYPE.small, lineHeight: 1 }}>
                      Order Placement
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: orderPlacementEnabled ? C.bull : C.bear }}>
                      {orderPlacementEnabled ? 'LIVE' : 'BLOCKED'}
                    </Typography>
                  </Box>
                }
                labelPlacement="start"
                sx={{ margin: 0 }}
              />
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={tradingActive}
                  onChange={handleTradingToggle}
                  disabled={settingsLoading}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: C.bull,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: C.bull,
                    }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: CH_TYPE.small, lineHeight: 1 }}>
                    Auto Trade
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                    {tradingActive ? 'ON' : 'OFF'}
                  </Typography>
                </Box>
              }
              labelPlacement="start"
              sx={{ margin: 0 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* R78 — Watchlist heatmap moved here, just above Trades Management.
          R115 follow-up — driven by /v1/charts/symbol-pct-changes (Zerodha
          bulk OHLC + Delta tickers) rather than the dashboard/watchlist
          endpoint, since change24h on the latter is empty for most symbols. */}
      <Box data-dash-anchor="heatmap" sx={{ scrollMarginTop: 120 }} />
      {(() => {
        if (heatmapPctItems.length === 0) return null;
        const cells: HeatmapCell[] = heatmapPctItems.map(it => ({
          symbol: it.symbol,
          exchange: it.exchange,
          pctChange: Number(it.pctChange),
          spotPrice: it.ltp != null ? Number(it.ltp) : null,
          assetClass: it.assetClass,
          turnoverUsd: it.turnoverUsd != null ? Number(it.turnoverUsd) : null,
        }));
        // Zerodha access tokens expire DAILY. When that happens every NSE/BSE row
        // disappears from this board, and for a long time it did so in total
        // silence — the heatmap just looked crypto-only. Say it instead.
        const zNote = pctSources.zerodha === 'session-expired'
          ? ' NSE/BSE symbols are missing: the Zerodha session has expired (its token expires daily) — reconnect it in Settings.'
          : pctSources.zerodha === 'failed'
            ? ' NSE/BSE symbols may be missing: the Zerodha quote request failed.'
            : '';
        return (
          <Box sx={{ mb: 1.5 }}>
            <SymbolHeatmap
              cells={cells}
              breadth={breadth}
              title="Heatmap"
              subheader={'Top movers by today’s % change and top 24h turnover, over the symbols this account covers. Click any cell to open the chart.' + zNote}
              splitTopBottom
            />
          </Box>
        );
      })()}

      <Box data-dash-anchor="trades" sx={{ scrollMarginTop: 120 }} />
      <CollapsibleSection id="trades-mgmt" title="Trades Management" count={(summary?.recentTrades || []).filter((t: any) => t.status === 'OPEN').length}
        info={
          <Tooltip title="How Trailing SL works — click to open the help guide" arrow>
          <IconButton size="small" onClick={() => setTrailHelpOpen(true)} sx={{ color: C.neutral }}>
          <InfoOutlined fontSize="small" />
          </IconButton>
          </Tooltip>
        }
      >
      {/* Trades Management */}
      <Paper sx={{
        p: 2.5,
        mb: 2,
        bgcolor: 'background.paper',
        borderLeft: `3px solid ${chHairline(isDark)}`,
        boxShadow: 2,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-1px)'
        }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <Box>
            
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip arrow title="Close EVERY open position immediately at market — across all exchanges (Delta Exchange India + Zerodha). A market exit order is placed per open trade and the trade is marked CLOSED on confirmation. Does not touch pending/DRAFT entries.">
            <Button variant="outlined" size="small" onClick={async () => {
              try {
                toast.loading('Closing all positions...', { id: 'square-off-all' });
                await tradingService.squareOffAll();
                toast.success('All positions closed', { id: 'square-off-all' });
                await handleRefreshData();
              } catch (error: any) {
                toast.error(`Failed: ${error.response?.data?.error?.message || error.message}`, { id: 'square-off-all' });
              }
            }} sx={{
              borderColor: C.warnBorder,
              color: C.warn,
              fontWeight: 600,
              '&:hover': { borderColor: C.warn, backgroundColor: C.warnMuted }
            }}>Square Off All</Button>
            </Tooltip>
            <Tooltip arrow title="Close all open LONG positions immediately at market (all exchanges — Delta India + Zerodha). SHORT positions are left untouched.">
            <Button variant="outlined" size="small" onClick={async () => {
              try {
                toast.loading('Closing long positions...', { id: 'square-off-long' });
                await tradingService.squareOffAll({ side: 'LONG' });
                toast.success('Long positions closed', { id: 'square-off-long' });
                await handleRefreshData();
              } catch (error: any) {
                toast.error(`Failed: ${error.response?.data?.error?.message || error.message}`, { id: 'square-off-long' });
              }
            }} sx={{
              borderColor: C.bullBorder,
              color: C.bull,
              fontWeight: 600,
              '&:hover': { borderColor: C.bull, backgroundColor: C.bullMuted }
            }}>Square Off Long</Button>
            </Tooltip>
            <Tooltip arrow title="Close all open SHORT positions immediately at market (all exchanges — Delta India + Zerodha). LONG positions are left untouched.">
            <Button variant="outlined" size="small" onClick={async () => {
              try {
                toast.loading('Closing short positions...', { id: 'square-off-short' });
                await tradingService.squareOffAll({ side: 'SHORT' });
                toast.success('Short positions closed', { id: 'square-off-short' });
                await handleRefreshData();
              } catch (error: any) {
                toast.error(`Failed: ${error.response?.data?.error?.message || error.message}`, { id: 'square-off-short' });
              }
            }} sx={{
              borderColor: C.bearBorder,
              color: C.bear,
              fontWeight: 600,
              '&:hover': { borderColor: C.bear, backgroundColor: C.bearMuted }
            }}>Square Off Short</Button>
            </Tooltip>
            <Tooltip arrow title="Record a trade manually — opens a form to enter symbol, side, quantity, entry, SL and target. Use for positions taken outside the app, or to seed a paper trade. It does NOT place a broker order; it creates a tracked trade row.">
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setManualTradeDialog(true)} sx={{
              borderColor: C.accentBorder,
              color: C.accent,
              fontWeight: 600,
              '&:hover': { borderColor: C.accent, backgroundColor: C.accentMuted }
            }}>Add Manual Trade</Button>
            </Tooltip>
            <Tooltip arrow title="Reload dashboard data now from the server/brokers — open trades, live prices, P&L, margin and alerts. Read-only; places no orders.">
            <Button variant="contained" size="small" onClick={handleRefreshData} sx={{
              bgcolor: C.neutral,
              color: C.onSolid,
              fontWeight: 700,
              '&:hover': { bgcolor: C.neutral, filter: 'brightness(0.9)' }
            }}>Refresh Data</Button>
            </Tooltip>
            <Tooltip arrow title="Download the current trades table as a CSV file (symbol, side, status, qty, entry, current, SL, target, P&L, P&L%, timestamps). Nothing is sent anywhere — the file saves locally.">
            <Button variant="outlined" size="small" onClick={() => {
              const trades = summary?.recentTrades || [];
              if (!trades.length) { toast.error('No trades to export'); return; }
              const headers = ['Symbol','Side','Status','Qty','Entry','Current','SL','Target','P&L','P&L%','Initiated','Closed'];
              const csv = [headers.join(','), ...trades.map((t: any) =>
                [t.symbol, t.side, t.status, t.quantity, t.averageEntryPrice, t.currentPrice,
                 t.stopLoss||'', t.target||'', t.totalPnl?.toFixed(2)||'', t.pnlPercent?.toFixed(2)||'',
                 t.createdAt ? new Date(t.createdAt).toLocaleString('en-IN') : '',
                 t.closedAt ? new Date(t.closedAt).toLocaleString('en-IN') : ''].join(',')
              )].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `trades_${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }} sx={{
              borderColor: C.neutralBorder,
              color: C.neutral,
              fontWeight: 600,
              '&:hover': { borderColor: C.neutral, backgroundColor: C.neutralMuted }
            }}>Export Data</Button>
            </Tooltip>
          </Box>
        </Box>

        {/* Issue 6: Side filter + symbol search + exchange + sort + latest-per-symbol */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small" placeholder="Search symbol..."
            value={dashboardSymbolFilter}
            onChange={e => setDashboardSymbolFilter(e.target.value)}
            sx={{ flex: '1 1 160px' }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {(['', 'LONG', 'SHORT'] as const).map(side => (
              <Button key={side} size="small"
                variant={dashboardSideFilter === side ? 'contained' : 'outlined'}
                onClick={() => setDashboardSideFilter(side)}
                sx={{
                  fontSize: CH_TYPE.small, py: 0.4, px: 1.2, minWidth: 0,
                  ...(side === '' ? {} : side === 'LONG'
                    ? { borderColor: C.bullBorder, color: dashboardSideFilter === 'LONG' ? C.onSolid : C.bull, backgroundColor: dashboardSideFilter === 'LONG' ? C.bull : undefined }
                    : { borderColor: C.bearBorder, color: dashboardSideFilter === 'SHORT' ? C.onSolid : C.bear, backgroundColor: dashboardSideFilter === 'SHORT' ? C.bear : undefined })
                }}
              >
                {side || 'All'}
              </Button>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.3 }}>Exch:</Typography>
            {(['', 'NSE', 'BSE', 'NFO', 'BFO', 'MCX', 'CDS', 'DELTA'] as const).map(ex => (
              <Chip key={ex || 'all'} size="small"
                label={ex || 'All'}
                onClick={() => setDashboardExchangeFilter(ex)}
                variant={dashboardExchangeFilter === ex ? 'filled' : 'outlined'}
                sx={{ height: 22, fontSize: CH_TYPE.small, fontWeight: 600,
                  backgroundColor: dashboardExchangeFilter === ex ? C.neutral : undefined,
                  color: dashboardExchangeFilter === ex ? C.onSolid : C.neutral }}
              />
            ))}
          </Box>
          <Button size="small" variant="outlined"
            onClick={() => setDashboardAlphaSort(s => s === 'off' ? 'asc' : s === 'asc' ? 'desc' : 'off')}
            sx={{ fontSize: CH_TYPE.small, py: 0.3, minWidth: 86 }}>
            Symbol {dashboardAlphaSort === 'asc' ? '↑' : dashboardAlphaSort === 'desc' ? '↓' : '—'}
          </Button>
          <FormControlLabel
            control={<Switch size="small" checked={dashboardLatestPerSymbol} onChange={e => setDashboardLatestPerSymbol(e.target.checked)} />}
            label={<Typography variant="caption" sx={{ fontSize: CH_TYPE.small }}>Latest per symbol</Typography>}
            sx={{ ml: 0.5, mr: 0 }}
          />
        </Box>

        {/* Options roll-up — plain-English "am I OK?" summary of all open baskets
            (live P&L, at-risk, next expiry, attention). Hidden when nothing is open. */}
        <Box sx={{ mb: 2 }}><OptionsPortfolioHeader compact hideWhenEmpty liveOnly onViewAll={() => navigate('/options?tab=monitor')} /></Box>

        {/* Option baskets — a blended sub-section of this same panel (unified with the
            non-option trades below), with live P&L + Close. Renders only when open. */}
        <OptionBasketsMonitor compact liveOnly />

        {/* PAPER chart trades — same treatment as PAPER baskets above: own sub-section,
            hide/show chip, separate Paper P&L; never mixed into the real table below. */}
        <PaperTradesMonitor
          trades={((summary as any)?.paperTrades || []) as any}
          onOpenChart={(t) => openChartTab(String(t.symbol || ''), String(t.exchange || ''), chartOpenQuery(t.originIntervalMinutes))}
          onChanged={() => dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)))}
        />

        {summary?.recentTrades && summary.recentTrades.length > 0 ? (
          <Box sx={{ overflowX: 'auto', maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', fontWeight: 'bold', borderBottom: 2, borderColor: 'divider', pb: 1, minWidth: 1620, p: 1, borderRadius: 1, fontSize: CH_TYPE.body, color: 'text.primary', position: 'sticky', top: 0, zIndex: 4, bgcolor: (t) => t.palette.mode === 'dark' ? '#1c2128' : '#eef1f4' }}>
              <Box sx={{ flex: 1.2, position: 'sticky', left: 0, zIndex: 5, bgcolor: (t) => t.palette.mode === 'dark' ? '#1c2128' : '#eef1f4' }}>Symbol</Box>
              <Box sx={{ width: 44, flexShrink: 0, textAlign: 'center' }}>Chart</Box>
              <Box sx={{ flex: 0.8 }}>Side</Box>
              <Box sx={{ flex: 0.7 }}>Qty</Box>
              <Box sx={{ flex: 1 }}>Entry</Box>
              <Box sx={{ flex: 1 }}>Current</Box>
              <Box sx={{ flex: 1.2 }}>SL <span style={{ fontWeight: 400, fontSize: CH_TYPE.small }}>(TSL/M/I)</span></Box>
              <Box sx={{ flex: 1 }}>SL Risk (₹)</Box>
              <Box sx={{ flex: 1 }}>Target</Box>
              <Box sx={{ flex: 1 }}>Target Gain (₹)</Box>
              <Box sx={{ flex: 1 }}>P&L</Box>
              <Box sx={{ flex: 0.8 }}>%</Box>
              <Box sx={{ flex: 1.2 }}>Trail</Box>
              <Box sx={{ flex: 1.2 }}>Initiated</Box>
              <Box sx={{ flex: 0.9, textAlign: 'center' }}>SL Trigger</Box>
              <Box sx={{ flex: 0.8 }}>Actions</Box>
            </Box>
            {(() => {
              let list = (summary.recentTrades as any[]).filter((trade: any) => {
                if (dashboardSideFilter && trade.side !== dashboardSideFilter) return false;
                if (dashboardSymbolFilter && !(trade.symbol || '').toLowerCase().includes(dashboardSymbolFilter.toLowerCase())) return false;
                if (dashboardExchangeFilter && String(trade.exchange || '').toUpperCase() !== dashboardExchangeFilter) return false;
                return true;
              });
              if (dashboardLatestPerSymbol) {
                const seen = new Map<string, any>();
                for (const t of list) {
                  const key = String(t.symbol || '');
                  const existing = seen.get(key);
                  if (!existing) { seen.set(key, t); continue; }
                  const tTime = new Date(t.createdAt || 0).getTime();
                  const eTime = new Date(existing.createdAt || 0).getTime();
                  if (tTime > eTime) seen.set(key, t);
                }
                list = Array.from(seen.values());
              }
              if (dashboardAlphaSort !== 'off') {
                list = [...list].sort((a, b) => {
                  const cmp = String(a.symbol || '').localeCompare(String(b.symbol || ''));
                  return dashboardAlphaSort === 'asc' ? cmp : -cmp;
                });
              }
              return list; // show ALL open/draft trades (was capped at 10 — hid open positions)
            })().map((trade: any) => {
              // Strict priority: instrument.decimalPrecision → tickSize-derived → exchange default.
              const dp = resolveDecimals(trade);
              // Calculate background color based on trade status and P&L
              const getRowBgColor = () => {
                if (trade.status === 'DRAFT') return C.dimMuted;
                if (trade.status === 'CLOSED') {
                  const pnl = trade.totalPnl || trade.realizedPnl || 0;
                  if (pnl > 0) return C.rowProfit;
                  if (pnl < 0) return C.rowLoss;
                  return C.dimMuted;
                }
                const pnl = trade.totalPnl || trade.unrealizedPnl || 0;
                if (pnl > 0) return C.rowProfit;
                if (pnl < 0) return C.rowLoss;
                return C.dimMuted;
              };

              // Compute SL Risk + Target Gain in Rs
              // isl = original/manual SL (used for R-multiple anchor in profit-lock, never moves automatically)
              // tsl = current trailing SL (the operative breach level; moves monotonically in profit direction)
              // The displayed "SL" is the operative level: TSL if trail is active, else manual, else ISL.
              const islValue = trade.manualStopLoss || trade.stopLoss;
              const tslValue = trade.currentTrailingSl ? Number(trade.currentTrailingSl) : null;
              // R-multiple gate — drives the visual badge state (Active vs Waiting).
              // Round 32: this gate NO LONGER hides the TSL value from the SL column.
              // The trail manager seeds currentTrailingSl from the initial alert SL on
              // the very first poll AND continues to ratchet once auto-trail has been
              // enabled (autoTrailEnabled stays true even if R later dips). The actual
              // breach happens at currentTrailingSl regardless of the live R-multiple,
              // so the cell must show that value or the user is misled into thinking
              // the trade will close at a level that is no longer the operative one.
              const rTrailThreshold = (userSettings as any)?.trailSlActivationR != null
                ? Number((userSettings as any).trailSlActivationR)
                : 2;
              const currentRMultiple = trade.mfeRMultiple ?? 0;
              const rTrailEligible = rTrailThreshold <= 0 || currentRMultiple >= rTrailThreshold;
              const isSnoozed = !!(trade.autoTrailMutedUntil && new Date(trade.autoTrailMutedUntil) > new Date());
              // Trail is "active" (live ratcheting) when R-eligible and not snoozed
              const trailIsActive = !!(tslValue != null && rTrailEligible && !isSnoozed);
              // Trail is "frozen" when snoozed — TSL stops updating but STILL protects (breach detection keeps running)
              const trailIsFrozen = !!(tslValue != null && rTrailEligible && isSnoozed);
              // Operative SL = most-protective of (TSL, manual, initial). The
              // breach engine fires on currentTrailingSl when set, and bumps it
              // for manual ratchets. Picking max for LONG / min for SHORT mirrors
              // the chart's operative-SL line and matches actual behaviour.
              const slCandidates: number[] = [];
              if (tslValue != null && Number.isFinite(tslValue)) slCandidates.push(tslValue);
              if (islValue != null && Number.isFinite(islValue)) slCandidates.push(Number(islValue));
              const operativeSL = slCandidates.length
                ? (trade.side === 'LONG' ? Math.max(...slCandidates) : Math.min(...slCandidates))
                : null;
              // Driving source — used for the badge/tooltip. TSL whenever it
              // is the most-protective level (typical case while a profitable
              // trade is being trailed); else Manual; else Initial.
              const slSource: 'TSL' | 'M' | 'I' = (() => {
                if (tslValue != null && operativeSL != null && Math.abs(tslValue - operativeSL) < 1e-9) return 'TSL';
                if (trade.manualStopLoss != null && operativeSL != null && Math.abs(Number(trade.manualStopLoss) - operativeSL) < 1e-9) return 'M';
                return 'I';
              })();
              const effectiveTarget = trade.target;
              const lotSz = (trade as any).lotSize || 1;
              const usdToInr = ((trade as any).exchange || '').toUpperCase() === 'DELTA' ? USD_TO_INR : 1;
              // SL Risk uses operative SL — so it updates live as the trail ratchets
              // When TSL > entry for LONG (profit locked above entry), slRiskRs is negative (green)
              let slRiskRs: number | null = null;
              if (operativeSL && trade.averageEntryPrice && trade.quantity) {
                if (trade.side === 'LONG') {
                  slRiskRs = (trade.averageEntryPrice - operativeSL) * trade.quantity * lotSz * usdToInr;
                } else {
                  slRiskRs = (operativeSL - trade.averageEntryPrice) * trade.quantity * lotSz * usdToInr;
                }
              }
              // Issue 7: Target Gain
              let targetGainRs: number | null = null;
              if (effectiveTarget && trade.averageEntryPrice && trade.quantity) {
                if (trade.side === 'LONG') {
                  targetGainRs = (effectiveTarget - trade.averageEntryPrice) * trade.quantity * lotSz * usdToInr;
                } else {
                  targetGainRs = (trade.averageEntryPrice - effectiveTarget) * trade.quantity * lotSz * usdToInr;
                }
              }
              // Profit lock — P&L >= configurable multiplier × SL Risk
              const currentPnl = trade.totalPnl || trade.unrealizedPnl || 0;
              const plMult = (userSettings as any)?.additionalSettings?.profitLockMultiplier || 5;
              const profitLocked = slRiskRs !== null && slRiskRs > 0 && currentPnl >= plMult * slRiskRs;
              // SL Trigger Off — open trade has its stop-loss auto-close disabled (alert-only mode)
              const slDisabled = trade.status === 'OPEN' && trade.slTriggerEnabled === false;

              return (
                <Box
                  key={trade.id}
                  sx={{
                    display: 'flex',
                    borderBottom: profitLocked ? `2px solid ${C.warn}` : 1,
                    borderColor: profitLocked ? C.warn : 'divider',
                    py: 1,
                    px: 1,
                    minWidth: 1620,
                    backgroundColor: profitLocked ? C.warnMuted : getRowBgColor(),
                    borderRadius: 1,
                    mb: 0.25,
                    transition: 'all 0.2s',
                    fontSize: CH_TYPE.body,
                    alignItems: 'center',
                    boxShadow: profitLocked ? `0 0 0 1px ${C.warn}` : undefined,
                    outline: slDisabled ? `2px dashed ${C.bear}` : 'none',
                    outlineOffset: slDisabled ? '-2px' : 0,
                    '&:hover': {
                      boxShadow: 'inset 0 0 0 9999px rgba(0,0,0,0.05)'
                    }
                  }}
                >
                  <Box sx={{ flex: 1.2, fontWeight: 600, color: C.neutral, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', position: 'sticky', left: 0, zIndex: 2, alignSelf: 'stretch', my: -1, py: 1, ml: -1, pl: 1, backgroundColor: 'background.paper', backgroundImage: profitLocked ? `linear-gradient(${C.warnMuted}, ${C.warnMuted})` : trade.side === 'LONG' ? `linear-gradient(${C.bullMuted}, ${C.bullMuted})` : trade.side === 'SHORT' ? `linear-gradient(${C.bearMuted}, ${C.bearMuted})` : `linear-gradient(${C.dimMuted}, ${C.dimMuted})` }}>
                    <Tooltip title={trade.symbol} arrow><Box component="span" sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trade.symbol}</Box></Tooltip>
                    {trade.exchange && <Chip label={String(trade.exchange).toUpperCase()} size="small" sx={{ bgcolor: 'transparent', color: 'text.secondary', fontWeight: 500, fontSize: CH_TYPE.micro, height: 18, border: (theme) => `1px solid ${theme.palette.divider}` }} />}
                    {profitLocked && <Tooltip title={`Profit Lock: P&L ≥ ${(userSettings as any)?.additionalSettings?.profitLockMultiplier || 5}× SL Risk`}><span style={{ fontSize: '0.9rem' }}>🔒</span></Tooltip>}
                    {trade.wlLong && <Chip label="WL:L" size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18 }} />}
                    {trade.wlShort && <Chip label="WL:S" size="small" sx={{ bgcolor: C.bearMuted, color: C.bear, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18 }} />}
                    {trade.isManual && <Chip label="Manual" size="small" sx={{ bgcolor: C.accentMuted, color: C.accent, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18 }} />}
                    {slDisabled && <Tooltip title="SL auto-close is disabled — alert-only mode"><Chip label="SL-OFF" size="small" sx={{ bgcolor: C.bearMuted, color: C.bear, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18, border: `1px dashed ${C.bear}` }} /></Tooltip>}
                  </Box>
                  {/* R149 — dedicated Chart column right after the symbol. */}
                  <Box sx={{ width: 44, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <OpenChartButton onOpen={() => openChartTab(trade.symbol, String(trade.exchange || ''), chartOpenQuery(trade.originIntervalMinutes, (trade as any).strategyId ?? (trade as any).strategy?.name ?? (trade as any).strategyName))} />
                  </Box>
                  <Box sx={{ flex: 0.8 }}>
                    <Chip
                      label={trade.side}
                      color={trade.side === 'LONG' ? 'success' : 'error'}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: CH_TYPE.small }}
                    />
                  </Box>
                  <Box sx={{ flex: 0.7 }}>{trade.quantity}</Box>
                  <Box sx={{ flex: 1 }}>{formatPrice(trade.averageEntryPrice, resolveDecimalsFor(trade, trade.averageEntryPrice))}</Box>
                  <Box sx={{ flex: 1 }}>{formatPrice(trade.currentPrice || trade.averageEntryPrice, resolveDecimalsFor(trade, trade.currentPrice || trade.averageEntryPrice))}</Box>
                  {/* ── Unified SL column ──────────────────────────────────────────
                      Shows the OPERATIVE stop-loss with a type badge:
                        TSL (teal)  = trailing SL is active (this is what will trigger square-off)
                        M   (amber) = manual SL override, no trail yet
                        I   (red)   = initial SL from alert, no overrides
                      Click to edit the Manual SL override (ISL anchor for R-multiples).
                      TSL value is computed server-side and shown here live.
                  ──────────────────────────────────────────────────────────────── */}
                  <Box sx={{ flex: 1.2, fontWeight: 600 }}>
                    {editingSLTradeId === trade.id ? (
                      <TextField
                        size="small"
                        value={editingSLValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingSLValue(e.target.value)}
                        onKeyDown={async (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter') {
                            try {
                              await tradingService.setManualStopLoss(trade.id, parseFloat(editingSLValue));
                              toast.success('Manual SL updated');
                              setEditingSLTradeId(null);
                              dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
                            } catch (err: any) {
                              toast.error(err.response?.data?.error?.message || 'Failed to set SL');
                            }
                          } else if (e.key === 'Escape') {
                            setEditingSLTradeId(null);
                          }
                        }}
                        onBlur={() => setEditingSLTradeId(null)}
                        autoFocus
                        sx={{ width: 90, '& input': { fontSize: CH_TYPE.body, padding: '2px 4px' } }}
                      />
                    ) : operativeSL != null ? (
                      /* Round 32 — single unified SL display. Always shows the
                       * OPERATIVE breach level (most-protective of TSL / Manual /
                       * Initial). Badge tells the user which one is driving it
                       * so they're never misled into thinking the trade will
                       * close at the wrong level. */
                      (() => {
                        const badge = slSource;
                        const badgeColor = slSource === 'TSL'
                          ? (trailIsFrozen ? C.warn : C.bull)
                          : slSource === 'M' ? C.warn : C.bear;
                        const badgeBg = slSource === 'TSL'
                          ? (trailIsFrozen ? C.warnMuted : C.bullMuted)
                          : slSource === 'M' ? C.warnMuted : C.bearMuted;
                        const badgeBorder = slSource === 'TSL'
                          ? (trailIsFrozen ? C.warnBorder : C.bullBorder)
                          : slSource === 'M' ? C.warnBorder : C.bearBorder;
                        const valueColor = slSource === 'TSL'
                          ? (trailIsFrozen ? C.warn : C.bull)
                          : slSource === 'M' ? C.warn : C.bear;
                        const tip = (() => {
                          const lines: string[] = [];
                          // Round 33 — describe current state + upcoming transition + hold-window behaviour.
                          if (slSource === 'TSL') {
                            lines.push(`CURRENT: Trailing SL @ ${formatPrice(operativeSL, resolveDecimalsFor(trade, operativeSL))} — active breach level`);
                            if (trailIsFrozen) {
                              lines.push('Trail FROZEN — snoozed; TSL not ratcheting but still monitors breach');
                            } else if (trailIsActive) {
                              const intervalMins = trade.originIntervalMinutes || 5;
                              const now = new Date();
                              const minsSinceMidnight = now.getHours() * 60 + now.getMinutes();
                              const minsIntoInterval = minsSinceMidnight % intervalMins;
                              const minsUntilNext = intervalMins - minsIntoInterval;
                              lines.push(`Trail LIVE — ratchets on ${intervalMins}m HA candles (next ~${minsUntilNext < 2 ? 'soon' : `${minsUntilNext}m`})`);
                              if (rTrailThreshold > 0) {
                                lines.push(`MFE ${currentRMultiple.toFixed(2)}R / ${rTrailThreshold}R ✓ (activation gate passed)`);
                              }
                            }
                          } else if (slSource === 'M') {
                            lines.push(`CURRENT: Manual SL @ ${formatPrice(operativeSL, resolveDecimalsFor(trade, operativeSL))} (your override)`);
                            if (rTrailThreshold > 0 && currentRMultiple < rTrailThreshold) {
                              lines.push(`NEXT: Trailing SL takes over once MFE reaches ${rTrailThreshold}R (now ${currentRMultiple.toFixed(2)}R)`);
                            }
                          } else {
                            // I — Initial SL (no override)
                            lines.push(`CURRENT: Initial SL @ ${formatPrice(operativeSL, resolveDecimalsFor(trade, operativeSL))} (from alert)`);
                            if (rTrailThreshold > 0 && currentRMultiple < rTrailThreshold) {
                              lines.push(`NEXT: Trailing SL takes over once MFE reaches ${rTrailThreshold}R (now ${currentRMultiple.toFixed(2)}R)`);
                            }
                          }
                          // Hold-window + SL-Trigger behaviour for breach
                          const modeInfo = (trade as any).slModeInfo as SlModeInfo | undefined;
                          if (modeInfo) {
                            lines.push(slModeLine(modeInfo, trade.instrument));
                          } else {
                            const holdMins = (userSettings as any)?.slBreachHoldMinutes ?? 1;
                            const spikeSecs = (userSettings as any)?.spikeBreachHoldSeconds ?? 30;
                            lines.push(`On breach: wait ${holdMins}m (normal) / ${spikeSecs}s (spike) before square-off`);
                          }
                          if (trade.slTriggerEnabled === false) {
                            lines.push('SL Trigger OFF — Telegram alert only on confirmed breach (no auto-close)');
                          }
                          if (trade.manualStopLoss != null && Math.abs(Number(trade.manualStopLoss) - operativeSL) > 1e-9) {
                            lines.push(`Manual override: ${formatPrice(trade.manualStopLoss, resolveDecimalsFor(trade, trade.manualStopLoss))}`);
                          }
                          if (trade.stopLoss != null && Math.abs(Number(trade.stopLoss) - operativeSL) > 1e-9) {
                            lines.push(`Initial SL (R-anchor): ${formatPrice(trade.stopLoss, resolveDecimalsFor(trade, trade.stopLoss))}`);
                          }
                          lines.push('Click to edit manual SL — or drag the SL line on the chart');
                          return lines.join(' | ');
                        })();
                        return (
                          <Tooltip title={tip} arrow>
                            <Box
                              onClick={() => { if (trade.status === 'OPEN') { setEditingSLTradeId(trade.id); setEditingSLValue(String(trade.manualStopLoss || trade.stopLoss || '')); } }}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.4, cursor: trade.status === 'OPEN' ? 'pointer' : 'default', opacity: trailIsFrozen ? 0.7 : 1 }}
                            >
                              <Chip label={badge} size="small"
                                sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 700,
                                  bgcolor: badgeBg, color: badgeColor,
                                  border: `1px solid ${badgeBorder}`, cursor: 'inherit' }} />
                              <span style={{ color: valueColor, fontWeight: 700 }}>{formatPrice(operativeSL, resolveDecimalsFor(trade, operativeSL))}</span>
                            </Box>
                          </Tooltip>
                        );
                      })()
                    ) : (
                      <span style={{ color: C.dim }}>—</span>
                    )}
                  </Box>
                  {/* SL Risk (₹) — uses operative SL (TSL when active, else ISL/manual).
                      Negative value = TSL has moved above entry = profit locked in. */}
                  <Box sx={{ flex: 1, fontWeight: 600, fontSize: CH_TYPE.body,
                    color: slRiskRs === null ? 'text.secondary' : slRiskRs <= 0 ? C.bull : C.bear }}>
                    {slRiskRs === null ? '—'
                      : slRiskRs <= 0
                        ? <Tooltip title="TSL is above entry — profit locked in at this level" arrow>
                            <span>+₹{Math.abs(Math.round(slRiskRs)).toLocaleString('en-IN')}🔒</span>
                          </Tooltip>
                        : `₹${Math.round(slRiskRs).toLocaleString('en-IN')}`
                    }
                  </Box>
                  <Box sx={{ flex: 1, fontWeight: 600 }}>
                    {editingTargetTradeId === trade.id ? (
                      <TextField
                        size="small"
                        value={editingTargetValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingTargetValue(e.target.value)}
                        onKeyDown={async (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter') {
                            try {
                              await tradingService.setTarget(trade.id, parseFloat(editingTargetValue));
                              toast.success('Target updated');
                              setEditingTargetTradeId(null);
                              dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
                            } catch (err: any) {
                              toast.error(err.response?.data?.error?.message || 'Failed to set Target');
                            }
                          } else if (e.key === 'Escape') {
                            setEditingTargetTradeId(null);
                          }
                        }}
                        onBlur={() => setEditingTargetTradeId(null)}
                        autoFocus
                        sx={{ width: 80, '& input': { fontSize: CH_TYPE.body, padding: '2px 4px' } }}
                      />
                    ) : (
                      <Box
                        onClick={() => {
                          if (trade.status === 'OPEN') {
                            setEditingTargetTradeId(trade.id);
                            setEditingTargetValue(String(trade.target || ''));
                          }
                        }}
                        sx={{ cursor: trade.status === 'OPEN' ? 'pointer' : 'default', '&:hover': trade.status === 'OPEN' ? { textDecoration: 'underline' } : {} }}
                      >
                        {trade.target ? <span style={{ color: C.bull }}>{formatPrice(trade.target, resolveDecimalsFor(trade, trade.target))}</span> : '-'}
                      </Box>
                    )}
                  </Box>
                  {/* Issue 7: Target Gain column */}
                  <Box sx={{ flex: 1, fontWeight: 600, color: targetGainRs !== null && targetGainRs > 0 ? C.bull : 'text.secondary', fontSize: CH_TYPE.body }}>
                    {targetGainRs !== null ? `₹${Math.round(targetGainRs).toLocaleString('en-IN')}` : '—'}
                  </Box>
                  <Box sx={{
                    flex: 1, fontWeight: 700,
                    color: (trade.totalPnl || trade.unrealizedPnl || 0) >= 0 ? C.bull : C.bear,
                    borderLeft: trade.status === 'DRAFT' ? 'none'
                      : `3px solid ${(trade.totalPnl || trade.unrealizedPnl || 0) >= 0 ? C.bull : C.bear}`,
                    pl: trade.status === 'DRAFT' ? 0 : 1
                  }}>
                    {trade.status === 'DRAFT' ? '-' : formatCurrency(trade.totalPnl || trade.unrealizedPnl)}
                  </Box>
                  <Box sx={{ flex: 0.8, fontWeight: 600, color: (trade.pnlPercent || 0) >= 0 ? C.bull : C.bear }}>
                    {trade.status === 'DRAFT' ? 'Pending' : (() => {
                      // Both numerator and denominator must be in the SAME currency. Delta
                      // stores P&L in INR (positionSync × 85) while entry × qty is the USD
                      // notional, so we convert notional → INR for Delta. Zerodha (NSE/BSE/
                      // NFO/MCX) is already INR end-to-end so usdToInr = 1.
                      const exch = String((trade as any).exchange || '').toUpperCase();
                      const usdToInr = exch === 'DELTA' ? USD_TO_INR : 1;
                      const lot = (trade as any).lotSize || 1;
                      const entry = Number(trade.averageEntryPrice) || 0;
                      const qty = Number(trade.quantity) || 0;
                      const notionalNative = entry * qty * lot;
                      const notionalInr = notionalNative * usdToInr;
                      const lev = Number((trade as any).instrument?.leverage || (trade as any).leverage || 1) || 1;
                      // Invested amount = capital actually deployed (margin = notional / leverage).
                      // Matches the user's configured Investment and Delta's UPNL% (return on margin).
                      const investmentInr = lev > 0 ? notionalInr / lev : notionalInr;
                      const totalPnl = Number(trade.totalPnl ?? trade.unrealizedPnl ?? 0);
                      const pnlPct = (typeof trade.pnlPercent === 'number')
                        ? trade.pnlPercent
                        : (investmentInr > 0 ? (totalPnl / investmentInr) * 100 : 0);
                      const notionalPct = notionalInr > 0 ? (totalPnl / notionalInr) * 100 : 0;
                      const fmtInr = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                      const tip = exch === 'DELTA'
                        ? `% on invested amount (margin, leverage-aware) — matches Delta UPNL%\n` +
                          `\n` +
                          `Notional (USD): ${formatPrice(entry, resolveDecimalsFor(trade, entry))} × ${qty} × ${lot} = $${notionalNative.toLocaleString('en-IN', { maximumFractionDigits: 2 })}\n` +
                          `Notional (INR @ ₹${usdToInr}/USD): ${fmtInr(notionalInr)}\n` +
                          `Invested / margin (INR, ${lev}x): ${fmtInr(investmentInr)}\n` +
                          `Total P&L (INR): ${fmtInr(totalPnl)}\n` +
                          `% = P&L / Invested × 100 = ${pnlPct.toFixed(2)}%\n` +
                          `\n` +
                          `Reference (on full notional): ${notionalPct.toFixed(2)}%`
                        : `% on invested amount (margin, leverage-aware)\n` +
                          `\n` +
                          `Notional (Entry × Qty × Lot): ${formatPrice(entry, resolveDecimalsFor(trade, entry))} × ${qty} × ${lot} = ${fmtInr(notionalInr)}\n` +
                          `Invested / margin (${lev}x): ${fmtInr(investmentInr)}\n` +
                          `Total P&L: ${fmtInr(totalPnl)}\n` +
                          `% = P&L / Invested × 100 = ${pnlPct.toFixed(2)}%\n` +
                          `\n` +
                          `Reference (on full notional): ${notionalPct.toFixed(2)}%`;
                      return (
                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>} arrow>
                          <span style={{ cursor: 'help' }}>{formatPercentage(pnlPct)}</span>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                  {/* Origin interval + Auto-Trail switch */}
                  <Box sx={{ flex: 1.2, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
                    onClick={e => e.stopPropagation()}>
                    {trade.originIntervalMinutes && (
                      <Chip
                        size="small"
                        label={`${trade.originIntervalMinutes}m`}
                        sx={{ height: 18, fontSize: CH_TYPE.micro, bgcolor: C.neutralMuted, color: C.neutral, fontWeight: 700 }}
                      />
                    )}
                    {trade.status === 'OPEN' && (() => {
                      const rThreshold = (userSettings as any)?.trailSlActivationR != null
                        ? Number((userSettings as any).trailSlActivationR)
                        : 2;
                      const currentR = trade.mfeRMultiple ?? 0;
                      const rEligible = rThreshold <= 0 || currentR >= rThreshold;
                      const isSnoozedNow = trade.autoTrailMutedUntil && new Date(trade.autoTrailMutedUntil) > new Date();
                      // Permanently turned OFF = manual mute with NO expiry.
                      const isDisabled = !!(trade as any).autoTrailManuallyMuted && !trade.autoTrailMutedUntil;
                      // Round 29 — use backend's autoTrailEnabled as the source of
                      // truth for ON state. Once activated, auto-trail stays ON
                      // (the manager only flips it back via user snooze, not by
                      // MFE retracement). Pre-R29 we recomputed uiState from
                      // current mfeR ≥ threshold, which falsely flipped Trail to
                      // OFF when a winner gave back some R but was still being
                      // actively trailed by the backend.
                      const isOn = trade.autoTrailEnabled === true;
                      const uiState: 'waiting' | 'on' | 'snoozed' | 'disabled' = isDisabled
                        ? 'disabled'
                        : isSnoozedNow
                        ? 'snoozed'
                        : (isOn ? 'on' : (rEligible ? 'on' : 'waiting'));
                      return (
                        <AutoTrailSwitch
                          tradeId={trade.id}
                          compact
                          initialStatus={{
                            uiState,
                            autoTrailEnabled: trade.autoTrailEnabled,
                            autoTrailEligibleAt: trade.autoTrailEligibleAt,
                            autoTrailMutedUntil: trade.autoTrailMutedUntil,
                            autoTrailManuallyMuted: trade.autoTrailManuallyMuted,
                            originIntervalMinutes: trade.originIntervalMinutes ?? 15,
                            originIntervalSource: trade.originIntervalSource ?? 'sync-default',
                            trailingMode: trade.trailingMode ?? 'off',
                            currentTrailingSl: trade.currentTrailingSl,
                            spikeModeActive: trade.spikeModeActive ?? false,
                            slBreachActive: trade.slBreachActive ?? false,
                            mfeRMultiple: currentR,
                            trailSlActivationR: rThreshold,
                            rEligible,
                          }}
                        />
                      );
                    })()}
                  </Box>
                  <Box sx={{ flex: 1.2, fontSize: CH_TYPE.small, color: 'text.secondary' }}>
                    {trade.createdAt ? new Date(trade.createdAt).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </Box>
                  {/* SL Trigger toggle — ON = auto-close on SL breach, OFF = alert only;
                      below it, the stop rule chip (price + hold / candle close, per trade). */}
                  <Box sx={{ flex: 0.9, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 0.25 }}
                    onClick={e => e.stopPropagation()}>
                    <Tooltip title={trade.slTriggerEnabled !== false ? 'SL auto-close ON — click to disable (alert-only)' : 'SL alert-only — click to enable auto-close'} arrow>
                      <Switch
                        size="small"
                        checked={trade.slTriggerEnabled !== false}
                        disabled={trade.status !== 'OPEN'}
                        color="error"
                        onChange={async (e) => {
                          try {
                            await api.put(`/trades/${trade.id}/sl-trigger`, { slTriggerEnabled: e.target.checked });
                            dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
                          } catch {
                            // non-critical; next poll will refresh
                          }
                        }}
                      />
                    </Tooltip>
                    {trade.status === 'OPEN' && (
                      <SlModeChip tradeId={trade.id} info={(trade as any).slModeInfo} ownTf={(trade as any).originIntervalMinutes}
                        instrument={trade.instrument}
                        backup={{ price: (trade as any).backupStopPrice, orderId: (trade as any).backupStopOrderId, error: (trade as any).backupStopError }}
                        onChanged={() => dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)))} />
                    )}
                  </Box>
                  <Box sx={{ flex: 0.8, display: 'flex', gap: 0.5 }}>
                    {trade.status === 'OPEN' && (
                      <IconButton
                        size="small"
                        onClick={() => handleSquareOff(trade)}
                        sx={{
                          backgroundColor: C.bearMuted,
                          color: C.bear,
                          padding: '4px',
                          border: `1px solid ${C.bearBorder}`,
                          '&:hover': { backgroundColor: C.bear, color: C.onSolid }
                        }}
                        title="Square Off"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    {trade.status === 'DRAFT' && (
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (window.confirm(`Cancel pending order for ${trade.symbol}? This will cancel the order on Delta Exchange.`)) {
                            try {
                              await tradingService.cancelDraftTrade(trade.id);
                              toast.success(`Order for ${trade.symbol} cancelled`);
                              dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
                            } catch (err: any) {
                              toast.error(err.response?.data?.error?.message || 'Failed to cancel order');
                            }
                          }
                        }}
                        sx={{
                          backgroundColor: C.warnMuted,
                          color: C.warn,
                          padding: '4px',
                          border: `1px solid ${C.warnBorder}`,
                          '&:hover': { backgroundColor: C.warn, color: C.onSolid }
                        }}
                        title="Cancel Pending Order"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    {trade.status === 'LIMBO' && (
                      <IconButton
                        size="small"
                        onClick={async () => {
                          const priceStr = window.prompt(
                            `Trade ${trade.symbol} is in LIMBO (closed externally but close price unknown).\n\nEnter the close/exit price to resolve:`,
                            String(trade.currentPrice || trade.averageEntryPrice || '')
                          );
                          if (priceStr === null) return;
                          const price = parseFloat(priceStr);
                          if (isNaN(price) || price <= 0) {
                            toast.error('Invalid price entered');
                            return;
                          }
                          try {
                            await tradingService.resolveLimboTrade(trade.id, price);
                            toast.success(`LIMBO trade resolved at ₹${price}`);
                            dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
                          } catch (err: any) {
                            toast.error(err.response?.data?.error?.message || 'Failed to resolve');
                          }
                        }}
                        sx={{
                          backgroundColor: C.accentMuted,
                          color: C.accent,
                          padding: '4px',
                          border: `1px solid ${C.accentBorder}`,
                          '&:hover': { backgroundColor: C.accent, color: C.onSolid }
                        }}
                        title="Resolve LIMBO Trade"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    {trade.isManual && trade.status !== 'CLOSED' && (
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (window.confirm(`Remove manual trade ${trade.symbol}? This will delete it without placing any broker order.`)) {
                            try {
                              await tradingService.deleteTrade(trade.id);
                              toast.success(`Manual trade ${trade.symbol} removed`);
                              dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
                            } catch (err: any) {
                              toast.error(err.response?.data?.error?.message || 'Failed to remove trade');
                            }
                          }
                        }}
                        sx={{
                          backgroundColor: C.dimMuted,
                          color: C.dim,
                          padding: '4px',
                          border: `1px solid ${C.dim}`,
                          '&:hover': { backgroundColor: C.dim, color: C.onSolid }
                        }}
                        title="Remove Manual Trade (no broker order)"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <EmptyState kind="trade" size="sm" title="No active trades"
            description="Open positions and recently closed trades will appear here." />
        )}
      </Paper>
      </CollapsibleSection>

      <Box data-dash-anchor="autotrade-watch" sx={{ scrollMarginTop: 120 }} />
      {/* R133 — Pending AutoTrade Execution Watch (TradeDesk Entry Qualifier) */}
      <CollapsibleSection id="autotrade-watch" title="Pending AutoTrade Execution Watch" count={autoExecWaiting}>
        <PendingAutoExecWatch onOpenChart={(sym, exch, extra) => openChartTab(sym, exch, extra)} onWaitingCountChange={setAutoExecWaiting} />
      </CollapsibleSection>

      <Box data-dash-anchor="pending" sx={{ scrollMarginTop: 120 }} />
      <CollapsibleSection id="pending-entry" title="Pending Manual Entry Watch" count={pendingEntries.filter(p => p.status === 'PENDING').length + campaignPendingRows.length + armedSynthTriggers.length}
        info={
          <Tooltip title="Deferred LIMIT entries placed from the chart's BUY/SHORT button. The exchange does NOT see these levels — the app monitors LTP locally and fires a MARKET order when each entry is reached. DIP entries (default) fire when price comes BACK to the level; STOP-tagged entries were placed on the breakout side of the live price and fire when price breaks THROUGH it. Entries are auto-cancelled when you exit the position, when price crosses the entry's SL before it fires (invalidated), or at the validity end (EOD chip — NSE mirrors broker DAY validity; Delta entries are GTC). Multi-entry scale-in groups show as E1/E2/… legs that share one SL/Target and average into a single position." arrow>
          <IconButton size="small" sx={{ color: C.neutral }}>
          <InfoOutlined fontSize="small" />
          </IconButton>
          </Tooltip>
        }
      >
      {/* Pending Entry Watch — round 27 deferred LIMITs (app-monitored) */}
      <Paper sx={{ p: 2.5, mb: 2, borderLeft: `3px solid ${chHairline(isDark)}`, boxShadow: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title="Cancel ALL pending Long entries"><span>
              <Button size="small" variant="outlined" color="success" onClick={() => cancelAllPending('BUY')} sx={{ fontSize: CH_TYPE.small, py: 0.3 }}>Cancel Long</Button>
            </span></Tooltip>
            <Tooltip title="Cancel ALL pending Short entries"><span>
              <Button size="small" variant="outlined" color="error" onClick={() => cancelAllPending('SHORT')} sx={{ fontSize: CH_TYPE.small, py: 0.3 }}>Cancel Short</Button>
            </span></Tooltip>
            <TextField
              size="small" placeholder="Symbol filter"
              value={peSymbolSearch}
              onChange={(e) => setPeSymbolSearch(e.target.value.toUpperCase())}
              sx={{ width: 140, '& .MuiInputBase-input': { fontSize: CH_TYPE.body, py: 0.5 } }}
            />
            <Button size="small" variant="outlined"
              onClick={() => setPeSortAsc(s => !s)}
              sx={{ fontSize: CH_TYPE.small, py: 0.3 }}>
              {peSortAsc ? '↑ Symbol' : '↓ Symbol'}
            </Button>
          </Box>
        </Box>
        {/* Armed synthetic-symbol triggers — chart BUY/SHORT on a SYNTHETIC arms these
            (the app fires the multi-leg basket when the combined premium crosses). */}
        {armedBySynth.length > 0 && (
          <Box sx={{ mb: 2, p: 1, borderRadius: 1.5, border: t => `1px solid ${t.palette.divider}`, bgcolor: t => t.palette.mode === 'dark' ? 'rgba(0,137,123,0.10)' : 'rgba(0,137,123,0.06)' }}>
            <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.body, mb: 0.5 }}>
              ⏳ Synthetic triggers — armed ({armedSynthTriggers.length})
              <Typography component="span" sx={{ fontSize: CH_TYPE.small, color: 'text.secondary', ml: 1 }}>
                fire the multi-leg option basket when the combined premium crosses the entry
              </Typography>
            </Typography>
            {armedBySynth.map(([sid, g]) => (
              <Box key={sid} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.4, borderTop: t => `1px dashed ${t.palette.divider}` }}>
                <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.body, cursor: 'pointer', color: 'primary.main' }}
                  onClick={() => openChartTab(g.name, 'SYNTH')}>
                  {g.name}
                </Typography>
                <Chip size="small" label={g.mode} sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 800, color: C.onSolid, bgcolor: g.mode === 'LIVE' ? C.bear : C.dim }} />
                <Typography sx={{ fontSize: CH_TYPE.small, color: 'text.secondary' }}>
                  {g.rows[0]?.side} · {g.rows.map((t: any) => `E${t.entryIndex ?? 1} ${Number(t.entryPremium).toFixed(1)}×${t.lots}`).join(' · ')}
                  {g.rows[0]?.slPremium != null ? ` · SL ${Number(g.rows[0].slPremium).toFixed(1)}` : ''}
                  {g.rows[0]?.targetPremium != null ? ` · TGT ${Number(g.rows[0].targetPremium).toFixed(1)}` : ''}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" color="error" variant="outlined" sx={{ fontSize: CH_TYPE.small, py: 0.1 }}
                  onClick={() => disarmSynth(sid, g.name)}>Disarm</Button>
              </Box>
            ))}
          </Box>
        )}
        {/* Option baskets — staged campaigns, grouped by symbol, each basket collapsible, showing
            every ladder entry with its fired/armed status, qty, underlying LTP and level. */}
        {activeCampaigns.length > 0 && (
          <Box sx={{ mb: 2, p: 1, borderRadius: 1.5, border: t => `1px solid ${t.palette.divider}`, bgcolor: t => t.palette.mode === 'dark' ? 'rgba(126,87,194,0.10)' : 'rgba(126,87,194,0.06)' }}>
            <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.body, mb: 0.5 }}>🧩 Option baskets — staged campaigns ({campaignPendingRows.length} armed)</Typography>
            {campaignsBySymbol.map(([sym, camps]) => (
              <Box key={sym} sx={{ mb: 0.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.small, color: 'text.secondary', mt: 0.5 }}>{sym}</Typography>
                {camps.map((c: any) => {
                  const open = campCollapse[c.id] ?? true;
                  const qtyByIdx: Record<number, number> = {};
                  for (const d of (c.aggregate?.stageDetails || [])) qtyByIdx[d.index] = Number(d.legs?.[0]?.quantity) || 0;
                  const fired = (c.stages || []).filter((s: any) => s.status === 'DEPLOYED').length;
                  const ltp = Number(c.spot) > 0 ? Math.round(Number(c.spot)).toLocaleString('en-IN') : '—';
                  return (
                    <Box key={c.id} sx={{ mb: 0.5, borderRadius: 1, border: t => `1px solid ${t.palette.divider}` }}>
                      <Box onClick={() => setCampCollapse(p => ({ ...p, [c.id]: !open }))} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1, py: 0.5, cursor: 'pointer', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: CH_TYPE.small, fontWeight: 700 }}>{open ? '▾' : '▸'} #{c.id}</Typography>
                        <Chip size="small" label={c.tradingMode === 'PAPER' ? 'PAPER' : 'LIVE'} color={c.tradingMode === 'PAPER' ? 'warning' : 'error'} sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 700 }} />
                        <Typography variant="caption" color="text.secondary">{String(c.view || '').replace(/_/g, ' ').toLowerCase()}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{fired} fired · {(c.stages || []).length - fired} armed · LTP {ltp}</Typography>
                      </Box>
                      {open && (
                        <Box sx={{ px: 1, pb: 0.5 }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '54px 74px 110px 130px 54px 84px 84px', gap: 1, fontSize: CH_TYPE.micro, fontWeight: 700, color: 'text.secondary', py: 0.3, borderTop: t => `1px solid ${t.palette.divider}` }}>
                            <Box>Entry</Box><Box>Status</Box><Box>Spread</Box><Box>Fires when</Box><Box sx={{ textAlign: 'right' }}>Qty</Box><Box sx={{ textAlign: 'right' }}>LTP</Box><Box sx={{ textAlign: 'right' }}>Level</Box>
                          </Box>
                          {(c.stages || []).map((s: any) => {
                            const arrow = s.side === 'UP' ? '↑' : s.side === 'DOWN' ? '↓' : '';
                            const deployed = s.status === 'DEPLOYED';
                            return (
                              <Box key={s.index} sx={{ display: 'grid', gridTemplateColumns: '54px 74px 110px 130px 54px 84px 84px', gap: 1, fontSize: CH_TYPE.small, py: 0.3, alignItems: 'center' }}>
                                <Box sx={{ fontWeight: 700 }}>E{s.index}{arrow}</Box>
                                <Box>{deployed ? <span style={{ color: '#2e7d32', fontWeight: 700 }}>fired ✓</span> : <span style={{ color: '#ed6c02' }}>armed</span>}</Box>
                                <Box sx={{ fontSize: CH_TYPE.small }}>{(s.kind || '').replace('_', ' ').toLowerCase()}</Box>
                                <Box>price {s.triggerDir === 'DIP' ? '≤' : '≥'} {Math.round(s.triggerPrice).toLocaleString('en-IN')}</Box>
                                <Box sx={{ textAlign: 'right' }}>{deployed ? (qtyByIdx[s.index] || '—') : '—'}</Box>
                                <Box sx={{ textAlign: 'right' }}>{ltp}</Box>
                                <Box sx={{ textAlign: 'right', fontWeight: 700 }}>{Math.round(s.anchorLevel ?? s.triggerPrice).toLocaleString('en-IN')}</Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Armed entries fire their anchored spread when price reaches the level. Manage on Options → Monitor, or drag the levels on the chart.</Typography>
          </Box>
        )}

        {pendingEntries.length === 0 && activeCampaigns.length === 0 ? (
          <EmptyState kind="trade" size="sm" title="No pending entries"
            description="Use the BUY / SHORT button on a chart with the LIMIT order type to queue a deferred entry here, or draw a staged campaign from an Outlook chip." />
        ) : pendingEntries.length === 0 ? null : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1.2fr) 44px 70px 100px 100px 100px 70px 90px 90px 110px', gap: 1, fontSize: CH_TYPE.small, fontWeight: 700, color: 'text.secondary', mb: 0.5, px: 0.5 }}>
              <Box>Symbol</Box>
              <Box sx={{ textAlign: 'center' }}>Chart</Box>
              <Box>Side</Box>
              <Box sx={{ textAlign: 'right' }}>Entry</Box>
              <Box sx={{ textAlign: 'right' }}>SL</Box>
              <Box sx={{ textAlign: 'right' }}>Target</Box>
              <Box sx={{ textAlign: 'right' }}>Qty</Box>
              <Box>Status</Box>
              <Box sx={{ textAlign: 'right' }}>LTP</Box>
              <Box sx={{ textAlign: 'right' }}>Actions</Box>
            </Box>
            {pendingEntries
              .filter(pe => !peSymbolSearch || (pe.symbol || '').includes(peSymbolSearch))
              .sort((a, b) => {
                const s = peSortAsc
                  ? (a.symbol || '').localeCompare(b.symbol || '')
                  : (b.symbol || '').localeCompare(a.symbol || '');
                if (s !== 0) return s;
                // Round 89 — keep scale-in legs of one group adjacent, ordered E1..EN.
                const g = (a.groupId || '').localeCompare(b.groupId || '');
                if (g !== 0) return g;
                return (a.entryIndex || 0) - (b.entryIndex || 0);
              })
              .map((pe, idx, arr) => {
                const dp = resolveDecimals(pe);
                const isEditing = peEditId === pe.id;
                // Round 89 — render a per-group header above the first leg of a
                // scale-in group so the legs read as one unit.
                const groupKey = pe.groupId || `single-${pe.id}`;
                const prevKey = idx > 0 ? (arr[idx - 1].groupId || `single-${arr[idx - 1].id}`) : null;
                const isFirstOfGroup = groupKey !== prevKey;
                const groupLegs = pe.groupId ? arr.filter(x => x.groupId === pe.groupId) : [pe];
                const isMultiGroup = groupLegs.length > 1 || (pe.entryCount ?? 1) > 1;
                const filledCount = groupLegs.filter(x => x.status === 'TRIGGERED').length;
                const headSl = groupLegs.find(x => x.stopLoss != null)?.stopLoss ?? null;
                const headTgt = groupLegs.find(x => x.target != null)?.target ?? null;
                const expanded = !!peExpanded[groupKey];
                // Collapsed scale-in groups show only their header; single
                // (non-group) entries always render their row.
                const showRow = expanded || !isMultiGroup;
                return (
                  <React.Fragment key={pe.id}>
                  {isFirstOfGroup && isMultiGroup && (
                    <Box
                      onClick={() => setPeExpanded(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', cursor: 'pointer',
                        mt: 0.8, mb: 0.2, px: 0.5, py: 0.5, borderRadius: 1,
                        bgcolor: (theme) => theme.palette.action.hover,
                        '&:hover': { bgcolor: (theme) => theme.palette.action.selected },
                      }}>
                      {expanded ? <ExpandLessIcon sx={{ fontSize: 18, color: C.neutral }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: C.neutral }} />}
                      <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.body }}>{pe.symbol}</Typography>
                      {pe.exchange && <Chip label={pe.exchange} size="small" sx={{ fontSize: CH_TYPE.micro, height: 18, '& .MuiChip-label': { px: 0.4 } }} />}
                      <Chip label={pe.side} size="small" sx={{ bgcolor: pe.side === 'BUY' ? C.bullMuted : C.bearMuted, color: pe.side === 'BUY' ? C.bull : C.bear, fontWeight: 800, fontSize: CH_TYPE.micro, height: 18, '& .MuiChip-label': { px: 0.6 } }} />
                      {pe.isSimulation && (
                        <Tooltip title="PAPER entry — fills as a simulated trade at the live price; no broker order.">
                          <Chip label="PAPER" size="small" sx={{ bgcolor: '#546e7a', color: '#fff', fontWeight: 800, fontSize: CH_TYPE.micro, height: 18, '& .MuiChip-label': { px: 0.6 } }} />
                        </Tooltip>
                      )}
                      <Chip label={`Scale-in · ${filledCount}/${groupLegs.length} filled`} size="small" sx={{ bgcolor: C.accentMuted, color: C.accent, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18, '& .MuiChip-label': { px: 0.6 } }} />
                      <Typography sx={{ fontSize: CH_TYPE.small, color: 'text.secondary' }}>
                        SL <Box component="span" sx={{ color: C.bear, fontWeight: 700 }}>{headSl != null ? formatPrice(headSl, resolveDecimalsFor(pe, headSl)) : '—'}</Box>
                        {' · '}Target <Box component="span" sx={{ color: C.bull, fontWeight: 700 }}>{headTgt != null ? formatPrice(headTgt, resolveDecimalsFor(pe, headTgt)) : '—'}</Box>
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <OpenChartButton onOpen={() => openChartTab(pe.symbol!, pe.exchange || '')} size={15} />
                      <Tooltip title={filledCount > 0 ? 'Cancel remaining pending entries (keeps open position)' : 'Remove all pending entries + SL/Target'}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeGroup(groupLegs, pe.symbol); }} sx={{ p: 0.3, color: C.bear }}>
                          <CloseIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  {showRow && (
                  <Box sx={{
                    display: 'grid', gridTemplateColumns: 'minmax(120px, 1.2fr) 44px 70px 100px 100px 100px 70px 90px 90px 110px',
                    gap: 1, py: 0.5, px: 0.5, alignItems: 'center', fontSize: CH_TYPE.body,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1, transition: 'background-color 120ms ease',
                    bgcolor: idx % 2 ? 'transparent' : 'rgba(128,128,128,0.045)',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                      <Tooltip title={pe.symbol || ''} arrow><Typography sx={{ fontWeight: 700, fontSize: CH_TYPE.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pe.symbol}</Typography></Tooltip>
                      {pe.exchange && <Chip label={pe.exchange} size="small" sx={{ fontSize: CH_TYPE.micro, height: 18, '& .MuiChip-label': { px: 0.4 } }} />}
                      {(pe.entryCount ?? 1) > 1 && pe.entryIndex != null && (
                        <Tooltip title={`Scale-in leg ${pe.entryIndex} of ${pe.entryCount}`} arrow>
                          <Chip label={`E${pe.entryIndex}/${pe.entryCount}`} size="small"
                            sx={{ fontSize: CH_TYPE.micro, height: 18, bgcolor: C.accentMuted, color: C.accent, fontWeight: 700, '& .MuiChip-label': { px: 0.4 } }} />
                        </Tooltip>
                      )}
                      {pe.triggerDirection === 'STOP' && (
                        <Tooltip title={`Breakout (STOP) entry — fires when price ${pe.side === 'BUY' ? 'RISES to' : 'FALLS to'} the level (was placed on the breakout side of the live price). DIP entries fire when price comes back to the level.`} arrow>
                          <Chip label="STOP" size="small"
                            sx={{ fontSize: CH_TYPE.micro, height: 18, bgcolor: 'rgba(255,152,0,0.18)', color: '#ff9800', fontWeight: 800, '& .MuiChip-label': { px: 0.4 } }} />
                        </Tooltip>
                      )}
                      {pe.expiresAt && pe.status === 'PENDING' && (
                        <Tooltip title={`Valid until ${new Date(pe.expiresAt).toLocaleString()} — expires automatically after that (mirrors broker DAY validity on NSE).`} arrow>
                          <Chip label="EOD" size="small"
                            sx={{ fontSize: CH_TYPE.micro, height: 18, bgcolor: C.neutralMuted, color: C.neutral, fontWeight: 700, '& .MuiChip-label': { px: 0.4 } }} />
                        </Tooltip>
                      )}
                    </Box>
                    {/* R149 — dedicated Chart column right after the symbol. */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <OpenChartButton onOpen={() => openChartTab(pe.symbol!, pe.exchange || '')} />
                    </Box>
                    <Chip
                      label={pe.side}
                      size="small"
                      sx={{
                        bgcolor: pe.side === 'BUY' ? C.bullMuted : C.bearMuted,
                        color: pe.side === 'BUY' ? C.bull : C.bear,
                        fontWeight: 800, fontSize: CH_TYPE.micro, height: 18,
                        '& .MuiChip-label': { px: 0.6 },
                      }}
                    />
                    {isEditing ? (
                      <>
                        <TextField size="small" type="number" value={peEditValues.entryPrice}
                          onChange={(e) => setPeEditValues(v => ({ ...v, entryPrice: e.target.value }))}
                          sx={{ '& .MuiInputBase-input': { fontSize: CH_TYPE.small, py: 0.3, textAlign: 'right' } }} />
                        <TextField size="small" type="number" value={peEditValues.stopLoss}
                          onChange={(e) => setPeEditValues(v => ({ ...v, stopLoss: e.target.value }))}
                          sx={{ '& .MuiInputBase-input': { fontSize: CH_TYPE.small, py: 0.3, textAlign: 'right' } }} />
                        <TextField size="small" type="number" value={peEditValues.target}
                          onChange={(e) => setPeEditValues(v => ({ ...v, target: e.target.value }))}
                          sx={{ '& .MuiInputBase-input': { fontSize: CH_TYPE.small, py: 0.3, textAlign: 'right' } }} />
                        <TextField size="small" type="number" value={peEditValues.quantity}
                          onChange={(e) => setPeEditValues(v => ({ ...v, quantity: e.target.value }))}
                          sx={{ '& .MuiInputBase-input': { fontSize: CH_TYPE.small, py: 0.3, textAlign: 'right' } }} />
                      </>
                    ) : (
                      <>
                        <Box sx={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatPrice(pe.entryPrice, resolveDecimalsFor(pe, pe.entryPrice))}</Box>
                        <Box sx={{ textAlign: 'right', fontFamily: 'monospace', color: pe.stopLoss != null ? C.bear : 'text.disabled' }}>
                          {pe.stopLoss != null ? formatPrice(pe.stopLoss, resolveDecimalsFor(pe, pe.stopLoss)) : '—'}
                        </Box>
                        <Box sx={{ textAlign: 'right', fontFamily: 'monospace', color: pe.target != null ? C.bull : 'text.disabled' }}>
                          {pe.target != null ? formatPrice(pe.target, resolveDecimalsFor(pe, pe.target)) : '—'}
                        </Box>
                        <Box sx={{ textAlign: 'right', fontFamily: 'monospace' }}>{pe.quantity.toLocaleString('en-IN')}</Box>
                      </>
                    )}
                    <Box>
                      <Chip
                        label={pe.status}
                        size="small"
                        sx={{
                          bgcolor: pe.status === 'PENDING' ? C.accentMuted
                            : pe.status === 'TRIGGERED' ? C.bullMuted
                            : C.neutralMuted,
                          color: pe.status === 'PENDING' ? C.accent
                            : pe.status === 'TRIGGERED' ? C.bull
                            : C.neutral,
                          fontWeight: 700, fontSize: CH_TYPE.micro, height: 18,
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    </Box>
                    <Box sx={{
                      textAlign: 'right', fontFamily: 'monospace',
                      // Color the LTP green when it has crossed the entry's
                      // firing threshold (close to firing) — direction-aware:
                      // DIP fires when price returns to the level, STOP when
                      // it breaks through it.
                      color: pe.ltp == null ? 'text.disabled'
                        : (pe.triggerDirection === 'STOP'
                            ? ((pe.side === 'BUY' && pe.ltp >= pe.entryPrice) || (pe.side === 'SHORT' && pe.ltp <= pe.entryPrice))
                            : ((pe.side === 'BUY' && pe.ltp <= pe.entryPrice) || (pe.side === 'SHORT' && pe.ltp >= pe.entryPrice)))
                          ? C.bull
                          : 'text.primary',
                      fontWeight: 600,
                    }}>
                      {pe.ltp != null ? formatPrice(pe.ltp, resolveDecimalsFor(pe, pe.ltp)) : '—'}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'flex-end' }}>
                      {isEditing ? (
                        <>
                          <Tooltip title="Save"><IconButton size="small" onClick={() => savePendingEntry(pe.id)} sx={{ p: 0.2, color: C.bull }}>
                            <SaveIcon sx={{ fontSize: 14 }} />
                          </IconButton></Tooltip>
                          <Tooltip title="Cancel"><IconButton size="small" onClick={cancelEditPendingEntry} sx={{ p: 0.2, color: C.dim }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton></Tooltip>
                        </>
                      ) : (
                        <>
                          {pe.status === 'PENDING' && (
                            <Tooltip title="Edit levels"><IconButton size="small" onClick={() => startEditPendingEntry(pe)} sx={{ p: 0.2, color: C.neutral }}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton></Tooltip>
                          )}
                          {(pe.status === 'PENDING' || pe.status === 'TRIGGERED' || pe.status === 'CANCELLED' || pe.status === 'EXPIRED') && (
                            <Tooltip title={pe.status === 'PENDING' ? 'Cancel pending entry' : 'Dismiss from list'}><IconButton size="small" onClick={() => removePendingEntry(pe.id, pe.symbol)} sx={{ p: 0.2, color: C.bear }}>
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton></Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                  )}
                  </React.Fragment>
                );
              })}
          </>
        )}
      </Paper>
      </CollapsibleSection>

      <Box data-dash-anchor="price-watch" sx={{ scrollMarginTop: 120 }} />
      <CollapsibleSection id="price-watch" title="Pending Manual Trigger Watch" count={watchlistData?.priceLevels?.length || 0}
        info={
          <Tooltip title="How Pending Manual Trigger Watch works — click for the trigger types and Auto-Execute behaviour" arrow>
          <IconButton size="small" onClick={() => setPlwHelpOpen(true)} sx={{ color: C.warn }}>
          <InfoOutlined fontSize="small" />
          </IconButton>
          </Tooltip>
        }
      >
      {/* Price Level Watch */}
      {watchlistData && (
        <Paper sx={{ p: 2.5, mb: 2, borderLeft: `3px solid ${chHairline(isDark)}`, boxShadow: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Row-operating controls only exist once there are rows: clearing,
                  filtering and sorting an empty list is noise, and it cost ~120px
                  of an empty section. "+ Symbol" below creates the first row and
                  therefore always shows. */}
              {watchlistData.priceLevels.length > 0 && (<>
<Tooltip title="Remove all Long entries (NBUY / BBUY) across every symbol"><span>
                <Button size="small" variant="outlined" color="success" onClick={() => clearPriceLevelsBySide('long')} sx={{ fontSize: CH_TYPE.small, py: 0.3 }}>Clear Long</Button>
              </span></Tooltip>
              <Tooltip title="Remove all Short entries (NSHORT / BSHORT) across every symbol"><span>
                <Button size="small" variant="outlined" color="error" onClick={() => clearPriceLevelsBySide('short')} sx={{ fontSize: CH_TYPE.small, py: 0.3 }}>Clear Short</Button>
              </span></Tooltip>
              <Tooltip title="Remove all HVN entries"><span>
                <Button size="small" variant="outlined" onClick={clearAllHvn} sx={{ fontSize: CH_TYPE.small, py: 0.3 }}>Clear HVN</Button>
              </span></Tooltip>
              <Tooltip title="Filter rows by symbols starting with this text. Auto-uppercased." arrow>
                <TextField size="small" placeholder="Filter rows…" value={plSymbolSearch}
                  onChange={e => setPlSymbolSearch((e.target.value || '').toUpperCase())}
                  sx={{ width: 160 }} inputProps={{ style: { padding: '4px 8px', fontSize: CH_TYPE.body, textTransform: 'uppercase' } }} />
              </Tooltip>
              <Tooltip title={`Toggle sort: ${plSortAsc ? 'ascending → descending' : 'descending → ascending'}`} arrow>
                <Button size="small" variant="outlined" onClick={() => setPlSortAsc(v => !v)}
                  sx={{ fontSize: CH_TYPE.small, py: 0.3, minWidth: 70 }}>
                  Symbol {plSortAsc ? '↑' : '↓'}
                </Button>
              </Tooltip>
              </>)}
              <Tooltip title="Add an instrument to watch. After picking the symbol, set up to four trigger levels (NBUY/NSHORT/BBUY/BSHORT)." arrow>
                <Button size="small" variant="contained"
                  sx={{ backgroundColor: C.warn, color: C.onSolid, fontSize: CH_TYPE.small, py: 0.3, minWidth: 70 }}
                  onClick={() => { setAddSymbolDialog({ open: true, mode: 'priceLevelWatch' }); setAddSymbolSearch(''); setAddSymbolResults([]); }}>
                  + Symbol
                </Button>
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 1 }}>
                <Typography variant="caption" sx={{ color: priceLevelAutoExecute ? C.warn : C.dim, fontWeight: 600, fontSize: CH_TYPE.small }}>
                  Auto-Execute
                </Typography>
                <Switch
                  size="small"
                  checked={priceLevelAutoExecute}
                  onChange={async (e) => {
                    const newVal = e.target.checked;
                    try {
                      await settingsService.updateUserSettings({ priceLevelAutoExecute: newVal });
                      setPriceLevelAutoExecute(newVal);
                      toast.success(`Price Level auto-execute ${newVal ? 'ON' : 'OFF'}`);
                    } catch (err) {
                      toast.error('Failed to update setting');
                    }
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: C.warn },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: C.warnMuted }
                  }}
                />
              </Box>
            </Box>
          </Box>
          {priceLevelAutoExecute && (
            <Box sx={{ px: 1.5, py: 0.4, bgcolor: C.warnMuted, borderRadius: 1, mb: 0.5, border: `1px solid ${C.warnBorder}` }}>
              <Typography variant="caption" sx={{ color: C.warn, fontWeight: 600 }}>
                Auto-Execute is ON — price level triggers will directly place orders on exchange
              </Typography>
            </Box>
          )}
          {/* Header */}
          {/* A table header above zero rows reads as a broken table. */}
          {watchlistData.priceLevels.length > 0 && (
<Box sx={{ display: 'flex', fontWeight: 700, borderBottom: 2, borderColor: 'divider', pb: 0.5, px: 0.5, color: 'text.primary', fontSize: CH_TYPE.small, backgroundColor: C.warnMuted, borderRadius: 1, mb: 0.5 }}>
            <Box sx={{ flex: 1.5 }}>Symbol</Box>
            <Box sx={{ width: 40, flexShrink: 0, textAlign: 'center' }}>Chart</Box>
            <Tooltip title="Normal Buy — fires when current price falls to or below this level." arrow><Box sx={{ flex: 1, color: C.bull }}>NBUY (≤)</Box></Tooltip>
            <Tooltip title="Normal Short — fires when current price rises to or above this level." arrow><Box sx={{ flex: 1, color: C.bear }}>NSHORT (≥)</Box></Tooltip>
            <Tooltip title="Breakout Buy — fires when current price breaks out above this level." arrow><Box sx={{ flex: 1, color: C.neutral }}>BBUY (≥)</Box></Tooltip>
            <Tooltip title="Breakout Short — fires when current price breaks down below this level." arrow><Box sx={{ flex: 1, color: C.accent }}>BSHORT (≤)</Box></Tooltip>
            <Tooltip title="Latest live price (or last close if live unavailable)." arrow><Box sx={{ flex: 1 }}>Current</Box></Tooltip>
            <Tooltip title="Armed (waiting), Triggered (level hit), or Disabled (no levels set)." arrow><Box sx={{ flex: 0.8 }}>Status</Box></Tooltip>
            <Box sx={{ flex: 0.7 }}>Actions</Box>
          </Box>
          )}
          {/* Rows */}
          {watchlistData.priceLevels.length === 0 && (
            <EmptyState kind="search" size="sm" title="No price levels set"
              description={'Click "+ Symbol" to add instruments to watch for NBUY / NSHORT / BBUY / BSHORT level triggers.'} />
          )}
          <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {[...watchlistData.priceLevels]
              .filter(inst => !plSymbolSearch || (inst.symbol || '').toUpperCase().startsWith(plSymbolSearch.toUpperCase()))
              .sort((a, b) => plSortAsc ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol))
              .map((inst: any, plIdx: number) => (
                <Box key={inst.id} sx={{ display: 'flex', alignItems: 'center', py: 0.6, px: 0.5, borderBottom: 1, borderColor: 'divider', fontSize: CH_TYPE.body, borderRadius: 1, transition: 'background-color 120ms ease', bgcolor: plIdx % 2 ? 'transparent' : 'rgba(128,128,128,0.045)', '&:hover': { bgcolor: 'action.hover' } }}>
                  <Box sx={{ flex: 1.5, fontWeight: 600, color: C.neutral, minWidth: 0, overflow: 'hidden' }}>
                    <Tooltip title={inst.symbol || ''} arrow><Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.symbol}</Box></Tooltip>
                  </Box>
                  {/* R149 — dedicated Chart column right after the symbol. */}
                  <Box sx={{ width: 40, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <OpenChartButton onOpen={() => openChartTab(inst.symbol, String(inst.exchange || ''))} size={14} />
                  </Box>
                  {plEditId === inst.id ? (
                    <>
                      {(['nbuy','nshort','bbuy','bshort'] as const).map(field => {
                        const curPrice = Number(inst.currentPrice);
                        const val = parseFloat(plEditValues[field] as string);
                        let warn = false;
                        if (Number.isFinite(val) && Number.isFinite(curPrice) && curPrice > 0) {
                          if (field === 'nbuy' && val > curPrice) warn = true;       // NBUY ≤ current so val should be ≤ current
                          else if (field === 'nshort' && val < curPrice) warn = true; // NSHORT ≥ current
                          else if (field === 'bbuy' && val < curPrice) warn = true;   // BBUY ≥ current
                          else if (field === 'bshort' && val > curPrice) warn = true; // BSHORT ≤ current
                        }
                        return (
                          <Box key={field} sx={{ flex: 1, pr: 0.5 }}>
                            <TextField size="small" type="number" value={plEditValues[field]}
                              error={warn}
                              title={warn ? 'Value is already past current price — would trigger immediately' : ''}
                              onChange={e => setPlEditValues(v => ({ ...v, [field]: e.target.value }))}
                              onDoubleClick={() => setPlEditValues(v => ({ ...v, [field]: String(inst.currentPrice || '') }))}
                              sx={{ width: '100%' }} inputProps={{ style: { padding: '3px 5px', fontSize: CH_TYPE.small } }} />
                          </Box>
                        );
                      })}
                      <Box sx={{ flex: 1 }} />
                      <Box sx={{ flex: 0.8 }} />
                      <Box sx={{ flex: 0.7, display: 'flex', gap: 0.3 }}>
                        <Button size="small" variant="contained" sx={{ fontSize: CH_TYPE.small, py: 0.2, px: 0.8, minWidth: 0, backgroundColor: C.bull }}
                          onClick={() => savePriceLevels(inst.id)}>Save</Button>
                        <Button size="small" sx={{ fontSize: CH_TYPE.small, py: 0.2, minWidth: 0 }}
                          onClick={() => setPlEditId(null)}>✕</Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      {[
                        { field: 'nbuy', cond: inst.nbuy != null && inst.currentPrice != null && inst.currentPrice <= inst.nbuy, color: C.bull },
                        { field: 'nshort', cond: inst.nshort != null && inst.currentPrice != null && inst.currentPrice >= inst.nshort, color: C.bear },
                        { field: 'bbuy', cond: inst.bbuy != null && inst.currentPrice != null && inst.currentPrice >= inst.bbuy, color: C.neutral },
                        { field: 'bshort', cond: inst.bshort != null && inst.currentPrice != null && inst.currentPrice <= inst.bshort, color: C.accent },
                      ].map(({ field, cond, color }) => (
                        <Box key={field} sx={{ flex: 1, color: cond ? color : 'text.disabled', fontWeight: cond ? 700 : 400, cursor: 'pointer' }}
                          title="Double-click to fill with current price"
                          onDoubleClick={() => {
                            setPlEditId(inst.id);
                            setPlEditValues({
                              nbuy: inst.nbuy ?? '',
                              nshort: inst.nshort ?? '',
                              bbuy: inst.bbuy ?? '',
                              bshort: inst.bshort ?? '',
                              [field]: inst.currentPrice ?? ''
                            });
                          }}>
                          {(inst as any)[field] != null ? formatPrice(Number((inst as any)[field]), resolveDecimals(inst)) : '-'}
                          {cond && ' ●'}
                          {(inst as any)[field] != null && levelOptionCapable(inst.symbol, inst.exchange) && (() => {
                            const att = lvlAtts.find(a => a.instrumentId === inst.id && a.levelKey === field && (a.status === 'ARMED' || a.status === 'FIRING'));
                            const nm = att ? (findTemplate(att.templateId)?.name || att.templateId) : '';
                            return (
                              <Tooltip arrow title={att
                                ? `Opens ${nm} (${attExpiryText(att)}, ×${att.lots}, ${att.tradingMode === 'LIVE' ? 'LIVE — real orders' : 'PAPER'}) when this level fires${priceLevelAutoExecute ? '' : ' — PAUSED while Auto-Execute is OFF (alert only)'}. Click to edit or remove.`
                                : 'Attach an option strategy that opens when this level fires (PAPER or LIVE)'}>
                                <Box component="span" data-level-option={`${inst.symbol}:${field}`}
                                  onClick={(e: any) => { e.stopPropagation(); setLvlDlg({ inst, key: field as LevelKey }); }}
                                  onDoubleClick={(e: any) => e.stopPropagation()}
                                  sx={{ ml: 0.4, cursor: 'pointer', fontSize: CH_TYPE.small, px: 0.3, borderRadius: 0.5,
                                    color: att ? (att.tradingMode === 'LIVE' ? C.bear : priceLevelAutoExecute ? C.warn : C.dim) : 'text.disabled', opacity: att ? 1 : 0.55,
                                    fontWeight: att ? 700 : 400, '&:hover': { opacity: 1, bgcolor: 'action.hover' } }}>
                                  ⚡{att ? ' ' + (findTemplate(att.templateId)?.name || att.templateId).replace(' (credit)', '') + ' ' + attExpiryShort(att) + (att.tradingMode === 'LIVE' ? ' LIVE' : '') : ''}
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </Box>
                      ))}
                      <Box sx={{ flex: 1, fontWeight: 600 }}>{inst.currentPrice != null ? formatPrice(Number(inst.currentPrice), resolveDecimalsFor(inst, Number(inst.currentPrice))) : '-'}</Box>
                      <Box sx={{ flex: 0.8 }}>
                        {(() => {
                          const cp = Number(inst.currentPrice);
                          const dp = resolveDecimals(inst);
                          const fmt = (v: any) => v == null ? '—' : formatPrice(Number(v), resolveDecimalsFor(inst, Number(v)));
                          const levels = [inst.nbuy, inst.nshort, inst.bbuy, inst.bshort];
                          const hasAny = levels.some(l => l != null);
                          if (!hasAny) {
                            return (
                              <Tooltip title="No trigger levels set on this symbol. Click Edit to add NBUY / NSHORT / BBUY / BSHORT — nothing will fire until at least one level is configured." arrow>
                                <Chip size="small" label="Disabled" sx={{ height: 18, fontSize: CH_TYPE.micro, backgroundColor: C.dim, color: C.onSolid }} />
                              </Tooltip>
                            );
                          }
                          // A level only fires once it has been ARMED — i.e. the backend has
                          // observed price OUTSIDE its trigger zone, so this hit is a genuine
                          // CROSS. A level set on the wrong side of the market starts inside
                          // its own zone and is held disarmed; showing it as "Triggered" would
                          // promise a fire that will never come.
                          const arm: any = (inst as any).priceLevelArm || {};
                          const isArmed = (k: 'nbuy' | 'nshort' | 'bbuy' | 'bshort', level: number) =>
                            arm?.[k]?.at === level ? arm[k].armed === true : true;
                          const hits: string[] = [];
                          const waiting: string[] = [];
                          const bucket = (k: 'nbuy' | 'nshort' | 'bbuy' | 'bshort', level: number, text: string) =>
                            (isArmed(k, level) ? hits : waiting).push(text);
                          if (inst.nbuy != null && Number.isFinite(cp) && cp > 0 && cp <= inst.nbuy) bucket('nbuy', inst.nbuy, `NBUY ${fmt(inst.nbuy)} (price ≤)`);
                          if (inst.nshort != null && Number.isFinite(cp) && cp > 0 && cp >= inst.nshort) bucket('nshort', inst.nshort, `NSHORT ${fmt(inst.nshort)} (price ≥)`);
                          if (inst.bbuy != null && Number.isFinite(cp) && cp > 0 && cp >= inst.bbuy) bucket('bbuy', inst.bbuy, `BBUY ${fmt(inst.bbuy)} (breakout ≥)`);
                          if (inst.bshort != null && Number.isFinite(cp) && cp > 0 && cp <= inst.bshort) bucket('bshort', inst.bshort, `BSHORT ${fmt(inst.bshort)} (breakdown ≤)`);
                          const triggered = hits.length > 0;
                          if (!triggered && waiting.length > 0) {
                            return (
                              <Tooltip arrow title={
                                `Set on the wrong side of the market: ${waiting.join(', ')} — price is ALREADY past ${waiting.length > 1 ? 'these levels' : 'this level'}. ` +
                                `To avoid firing an unintended order the instant it was saved, ${waiting.length > 1 ? 'they are' : 'it is'} held disarmed until price moves back out and genuinely crosses in. ` +
                                `If you meant to trade here right now, use the Buy/Short buttons; otherwise move the level to the correct side.`
                              }>
                                <Chip size="small" label="Wrong side" sx={{ height: 18, fontSize: CH_TYPE.micro, backgroundColor: C.dim, color: C.onSolid, fontWeight: 700 }} />
                              </Tooltip>
                            );
                          }
                          if (triggered) {
                            const action = priceLevelAutoExecute
                              ? 'Auto-Execute is ON — an order will be placed on the broker as soon as the trigger evaluator picks this up.'
                              : 'Auto-Execute is OFF — only an alert is raised; you decide whether to act.';
                            return (
                              <Tooltip title={`Triggered: ${hits.join(', ')}. ${action}`} arrow>
                                <Chip size="small" label="Triggered" sx={{ height: 18, fontSize: CH_TYPE.micro, backgroundColor: C.warn, color: C.onSolid, fontWeight: 700 }} />
                              </Tooltip>
                            );
                          }
                          // Armed — describe what'll happen next at each set level
                          const lines: string[] = [];
                          if (inst.nbuy != null) lines.push(`Will fire BUY when price ≤ ${fmt(inst.nbuy)} (currently ${fmt(cp)})`);
                          if (inst.nshort != null) lines.push(`Will fire SHORT when price ≥ ${fmt(inst.nshort)} (currently ${fmt(cp)})`);
                          if (inst.bbuy != null) lines.push(`Will fire BREAKOUT BUY when price ≥ ${fmt(inst.bbuy)} (currently ${fmt(cp)})`);
                          if (inst.bshort != null) lines.push(`Will fire BREAKDOWN SHORT when price ≤ ${fmt(inst.bshort)} (currently ${fmt(cp)})`);
                          const tail = priceLevelAutoExecute
                            ? ' · Auto-Execute ON: placing an order on hit.'
                            : ' · Auto-Execute OFF: alert only.';
                          return (
                            <Tooltip title={lines.join(' · ') + tail} arrow>
                              <Chip size="small" label="Armed" sx={{ height: 18, fontSize: CH_TYPE.micro, backgroundColor: C.bull, color: C.onSolid }} />
                            </Tooltip>
                          );
                        })()}
                      </Box>
                      <Box sx={{ flex: 0.7, display: 'flex', alignItems: 'center' }}>
                        <Button size="small" variant="outlined" sx={{ fontSize: CH_TYPE.small, py: 0.2, px: 0.6, minWidth: 0 }}
                          onClick={() => {
                            setPlEditId(inst.id);
                            setPlEditValues({ nbuy: inst.nbuy ?? '', nshort: inst.nshort ?? '', bbuy: inst.bbuy ?? '', bshort: inst.bshort ?? '' });
                          }}>Edit</Button>
                        <Tooltip title="Remove from Pending Manual Trigger Watch">
                          <IconButton size="small" sx={{ p: 0.2, ml: 0.2, color: C.dim, '&:hover': { color: C.bear } }}
                            onClick={() => removeFromPriceLevels(inst.id, inst.symbol)}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </>
                  )}
                </Box>
              ))}
          </Box>

          {/* HVN levels — drawn on the chart (HVN tool); fire a one-time alert +
              Telegram when price CROSSES the level (either direction), then
              auto-remove. No order is ever placed for HVN. */}
          {(watchlistData.hvnLevels?.length ?? 0) > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#9c27b0', display: 'block', mb: 0.5 }}>
                HVN Levels ({watchlistData.hvnLevels!.length}) — alert + Telegram on cross (no order)
              </Typography>
              <Box sx={{ display: 'flex', fontWeight: 700, borderBottom: 1, borderColor: 'divider', pb: 0.3, px: 0.5, fontSize: CH_TYPE.small, color: 'text.secondary' }}>
                <Box sx={{ flex: 1.5 }}>Symbol</Box>
                <Box sx={{ width: 40, flexShrink: 0, textAlign: 'center' }}>Chart</Box>
                <Box sx={{ flex: 1 }}>HVN Level</Box>
                <Box sx={{ flex: 1 }}>Current</Box>
                <Box sx={{ flex: 0.7 }}>Actions</Box>
              </Box>
              <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                {[...watchlistData.hvnLevels!]
                  .filter((h: any) => !plSymbolSearch || (h.symbol || '').toUpperCase().startsWith(plSymbolSearch.toUpperCase()))
                  .map((h: any, hvnIdx: number) => {
                    const prec = resolveDecimals(h);
                    const fmt = (v: any) => (v == null || v === '') ? '—' : formatPrice(Number(v), prec);
                    return (
                      <Box key={`hvn-${h.id}`} sx={{ display: 'flex', alignItems: 'center', py: 0.5, px: 0.5, borderBottom: 1, borderColor: 'divider', fontSize: CH_TYPE.body, borderRadius: 1, transition: 'background-color 120ms ease', bgcolor: hvnIdx % 2 ? 'transparent' : 'rgba(128,128,128,0.045)', '&:hover': { bgcolor: 'action.hover' } }}>
                        <Box sx={{ flex: 1.5, fontWeight: 600, color: C.neutral, minWidth: 0, overflow: 'hidden' }}>
                          <Tooltip title={h.symbol || ''} arrow><Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.symbol}</Box></Tooltip>
                        </Box>
                        <Box sx={{ width: 40, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <OpenChartButton onOpen={() => openChartTab(h.symbol, String(h.exchange || ''))} size={14} />
                        </Box>
                        <Box sx={{ flex: 1, color: '#9c27b0', fontWeight: 600 }}>{fmt(h.price)}</Box>
                        <Box sx={{ flex: 1 }}>{fmt(h.currentPrice)}</Box>
                        <Box sx={{ flex: 0.7, display: 'flex', alignItems: 'center' }}>
                          <Tooltip title="Remove HVN level">
                            <IconButton size="small" sx={{ p: 0.2, ml: 0.2, color: C.dim, '&:hover': { color: C.bear } }} onClick={() => removeHvnLevel(h.id, h.symbol)}>
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          )}
        </Paper>
      )}
      </CollapsibleSection>

      <Box data-dash-anchor="alerts" sx={{ scrollMarginTop: 120 }} />
      <CollapsibleSection id="recent-alerts" title="Recent Alerts" count={(summary?.recentAlerts || []).length}
        info={
          <Tooltip title="How Recent Alerts works — features, color codes, actions" arrow>
          <IconButton size="small" onClick={() => setRecentAlertsHelpOpen(true)} sx={{ color: C.neutral }}>
          <InfoOutlined fontSize="small" />
          </IconButton>
          </Tooltip>
        }
      >
      {/* Recent Alerts */}
      <Paper sx={{
        p: 2.5,
        mb: 2,
        borderLeft: `3px solid ${chHairline(isDark)}`,
        boxShadow: 2,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-1px)'
        }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          
          <Select
            size="small"
            value={alertPeriod}
            onChange={(e) => setAlertPeriod(e.target.value)}
            sx={{ minWidth: 90, fontSize: CH_TYPE.body }}
          >
            {ALERT_PERIODS.map(p => (
              <MenuItem key={p} value={p} sx={{ fontSize: CH_TYPE.body }}>{p}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Alert Filters — Action / Interval / Strategy / Confirmed */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by symbol..."
            value={alertFilters.symbol}
            onChange={(e) => setAlertFilters({ ...alertFilters, symbol: e.target.value })}
            sx={{ flex: '1 1 160px' }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Action</InputLabel>
            <Select value={alertFilters.action} label="Action" onChange={(e) => setAlertFilters({ ...alertFilters, action: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="BUY">BUY</MenuItem>
              <MenuItem value="SELL">SELL</MenuItem>
              <MenuItem value="SHORT">SHORT</MenuItem>
              <MenuItem value="COVER">COVER</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Interval</InputLabel>
            <Select value={alertFilters.interval} label="Interval" onChange={(e) => setAlertFilters({ ...alertFilters, interval: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {['1m','3m','5m','15m','30m','1h','2h','4h','1d'].map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Strategy..."
            value={alertFilters.strategy}
            onChange={(e) => setAlertFilters({ ...alertFilters, strategy: e.target.value })}
            sx={{ width: 120 }}
          />
          <FormControl size="small" sx={{ minWidth: 115 }}>
            <InputLabel>Confirmed</InputLabel>
            <Select value={alertFilters.confirmed} label="Confirmed" onChange={(e) => setAlertFilters({ ...alertFilters, confirmed: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Y">Yes (Y)</MenuItem>
              <MenuItem value="N">No (N)</MenuItem>
            </Select>
          </FormControl>
          {(alertFilters.action || alertFilters.interval || alertFilters.strategy || alertFilters.confirmed || alertFilters.symbol) && (
            <Button size="small" variant="outlined"
              onClick={() => setAlertFilters({ action: '', interval: '', strategy: '', confirmed: '', symbol: '' })}>
              Clear
            </Button>
          )}
        </Box>

        {summary?.recentAlerts && summary.recentAlerts.length > 0 ? (
          <Box>
            <Box sx={{ display: 'flex', fontWeight: 'bold', borderBottom: 2, borderColor: 'divider', pb: 1, backgroundColor: 'action.hover', p: 1, borderRadius: 1, color: 'text.primary', fontSize: CH_TYPE.body }}>
              <Box sx={{ flex: 1.2 }}>Symbol</Box>
              <Box sx={{ width: 44, flexShrink: 0, textAlign: 'center' }}>Chart</Box>
              <Box sx={{ flex: 1.5 }}>Strategy / Interval</Box>
              <Box sx={{ flex: 1.5 }}>Create Time / Prices</Box>
              <Box sx={{ flex: 1 }}>Alert Type</Box>
              <Box sx={{ flex: 0.7 }}>Strength</Box>
              <Box sx={{ flex: 0.8 }}>Confirmed</Box>
              <Box sx={{ flex: 0.9 }}>FNO</Box>
              <Box sx={{ flex: 1.2 }}>Actions</Box>
            </Box>
            {/* Scrollable grouped alerts — max 50vh so Parked Alerts stays visible */}
            <Box sx={{ maxHeight: 'calc(50vh - 60px)', overflowY: 'auto', pr: 0.5 }}>
            {/* Grouped alerts by symbol */}
            {groupedAlerts
              .filter(([symbol, alerts]) => {
                if (alertFilters.symbol && !symbol.toLowerCase().includes(alertFilters.symbol.toLowerCase())) return false;
                // Apply Action/Interval/Strategy/Confirmed filters against ONLY the latest alert per symbol.
                // Using `.some()` over historical alerts caused symbols with mixed BUY/SHORT history to show
                // up under either filter even though only the displayed (latest) alert mattered.
                const latest = alerts[0];
                if (!latest) return false;
                const pd = (latest.parsedData as any) || {};
                if (alertFilters.action && (pd.action || '').toUpperCase() !== alertFilters.action) return false;
                if (alertFilters.interval) {
                  // Normalise BOTH sides to minutes so the filter matches regardless of how
                  // the timeframe was stored (in-house scans use minutes e.g. 1440; TradingView
                  // uses resolutions e.g. 'D'). Without this, "1d" matched nothing because the
                  // daily alerts were stored as 1440, not 'D'.
                  const want = tfToMinutes(alertFilters.interval);
                  const have = tfToMinutes(pd.timeframe);
                  if (want == null || have == null || want !== have) return false;
                }
                if (alertFilters.strategy) {
                  const q = alertFilters.strategy.toLowerCase();
                  const inStrategy = (pd.strategy || '').toLowerCase().includes(q);
                  const inLevelType = (pd.levelType || '').toLowerCase().includes(q);
                  if (!inStrategy && !inLevelType) return false;
                }
                if (alertFilters.confirmed && pd.confirmed !== alertFilters.confirmed) return false;
                return true;
              })
              .map(([symbol, alerts]) => {
                const latestAlert = alerts[0];
                const rowStyle = getAlertRowStyle(latestAlert);
                const isExpanded = expandedSymbols.has(symbol);
                const confirmed = (latestAlert.parsedData as any)?.confirmed;
                const action = (latestAlert.parsedData as any)?.action || '';

                return (
                  <React.Fragment key={symbol}>
                    {/* Parent row */}
                    <Box
                      onClick={() => toggleSymbol(symbol)}
                      sx={{
                        display: 'flex',
                        borderBottom: 1,
                        borderColor: 'rgba(255,255,255,0.2)',
                        py: 1,
                        px: 1,
                        backgroundColor: rowStyle.bg,
                        borderRadius: 1,
                        mb: 0.25,
                        cursor: 'pointer',
                        alignItems: 'center',
                        borderLeft: `3px solid ${rowStyle.color}`,
                        '&:hover': { filter: 'brightness(0.96)' }
                      }}
                    >
                      <Box sx={{
                        flex: 1.2, fontWeight: 600, color: rowStyle.color,
                        display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', fontSize: CH_TYPE.body,
                        // Sticky frozen column — same visual pattern as Trades Management
                        position: 'sticky', left: 0, zIndex: 2,
                        alignSelf: 'stretch', my: -1, py: 1, ml: -1, pl: 1,
                        // Opaque paper UNDER the tint, not the tint on its own. A sticky
                        // column exists to hide content scrolling beneath it, which a
                        // translucent background does not do -- and because this sits inside
                        // the row, which already paints rowStyle.bg, applying it again
                        // doubled the tint and took the symbol to 3.91:1. Same pattern the
                        // Trades Management sticky column already uses.
                        backgroundColor: 'background.paper',
                        backgroundImage: `linear-gradient(${rowStyle.bg}, ${rowStyle.bg})`,
                      }}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <Tooltip title={symbol} arrow><Box component="span" sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{symbol}</Box></Tooltip>
                        {latestAlert.instrument?.exchange && (
                          <Chip label={String(latestAlert.instrument.exchange).toUpperCase()} size="small"
                            sx={{ bgcolor: 'transparent', color: 'text.secondary', fontWeight: 500, fontSize: CH_TYPE.micro, height: 18, border: (theme) => `1px solid ${theme.palette.divider}` }} />
                        )}
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: CH_TYPE.small }}>({alerts.length})</Typography>
                        {latestAlert.wlLong && <Chip label="WL:L" size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18 }} />}
                        {latestAlert.wlShort && <Chip label="WL:S" size="small" sx={{ bgcolor: C.bearMuted, color: C.bear, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18 }} />}
                      </Box>
                      {/* R149 — dedicated Chart column right after the symbol. */}
                      <Box sx={{ width: 44, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <OpenChartButton onOpen={() => openChartTab(symbol, String(latestAlert.instrument?.exchange || ''), chartOpenQuery((latestAlert.parsedData as any)?.timeframe, (latestAlert.parsedData as any)?.strategy || (latestAlert.parsedData as any)?.signalSource))} />
                      </Box>
                      {/* Strategy / Interval column: strategy name + timeframe */}
                      <Box sx={{ flex: 1.5, fontSize: CH_TYPE.body, color: 'text.secondary', display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                        {((latestAlert.parsedData as any)?.strategy || (latestAlert.parsedData as any)?.levelType) ? (
                          <Typography variant="caption" sx={{ color: 'text.primary', fontSize: CH_TYPE.small, fontWeight: 700 }}>{(latestAlert.parsedData as any).strategy || (latestAlert.parsedData as any).levelType}</Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: CH_TYPE.small, fontStyle: 'italic' }}>—</Typography>
                        )}
                        {(latestAlert.parsedData as any)?.timeframe && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: CH_TYPE.small }}>{formatTimeframe((latestAlert.parsedData as any).timeframe)}</Typography>
                        )}
                      </Box>
                      {/* Create Time / Prices */}
                      <Box sx={{ flex: 1.5, fontSize: CH_TYPE.body, color: 'text.primary' }}>
                        {fmtDate(latestAlert.createdAt, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.2, flexWrap: 'wrap' }}>
                          {latestAlert.triggerPrice && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: CH_TYPE.micro }}>@{formatPrice(Number(latestAlert.triggerPrice), resolveDecimalsFor(latestAlert.instrument, Number(latestAlert.triggerPrice)))}</Typography>}
                          {latestAlert.currentPrice && <Typography variant="caption" sx={{ color: C.neutral, fontSize: CH_TYPE.micro }}>now {formatPrice(Number(latestAlert.currentPrice), resolveDecimalsFor(latestAlert.instrument, Number(latestAlert.currentPrice)))}</Typography>}
                        </Box>
                      </Box>
                      {/* Alert Type: action direction chip */}
                      <Box sx={{ flex: 1 }}>
                        {action ? (
                          <><Chip label={action} size="small" sx={{ bgcolor: rowStyle.bg, color: rowStyle.color, border: `1px solid ${rowStyle.color}55`, fontWeight: 700, fontSize: CH_TYPE.small, height: 18 }} /><ExitTag alert={latestAlert} color={rowStyle.color} /></>
                        ) : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>}
                      </Box>
                      {/* Strength */}
                      <Box sx={{ flex: 0.7, fontSize: CH_TYPE.body, color: rowStyle.color }}>
                        <StrengthBadge strength={(latestAlert as any).strength ?? (latestAlert.parsedData as any)?.signalStrength} />
                      </Box>
                      {/* Confirmed */}
                      <Box sx={{ flex: 0.8 }}>
                        {confirmed === 'Y' ?
                          <Chip label="Y" size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, border: `1px solid ${C.bullBorder}`, fontSize:'0.62rem', height:16 }} /> :
                          confirmed === 'N' ?
                          <Chip label="N" size="small" sx={{ bgcolor: C.dimMuted, color: C.dim, border: `1px solid ${C.dim}44`, fontSize:'0.62rem', height:16 }} /> :
                          null}
                      </Box>
                      {/* FNO Score Badge */}
                      <Box sx={{ flex: 0.9, display: 'flex', alignItems: 'center' }}
                        onClick={e => e.stopPropagation()}>
                        <FnoScoreBadge
                          alertId={latestAlert.id}
                          symbol={symbol}
                          direction={(latestAlert.parsedData as any)?.action === 'SHORT' ? 'SHORT' : 'LONG'}
                          assessment={fnoAssessments[latestAlert.id]}
                        />
                        <AiVerdictChip
                          assessment={aiByAlert[latestAlert.id]}
                          signalDirection={(latestAlert.parsedData as any)?.action === 'SHORT' ? 'SHORT' : 'LONG'}
                          compact
                          symbol={symbol}
                          {...((latestAlert as any).instrument?.exchange
                            ? { exchange: String((latestAlert as any).instrument.exchange).toUpperCase() }
                            : {})}
                          alertId={latestAlert.id}
                          timeframe={(latestAlert.parsedData as any)?.timeframe ?? null}
                          onAssessed={(a) => setAiByAlert(prev => ({ ...prev, [latestAlert.id]: a }))}
                        />
                      </Box>
                      {/* Actions */}
                      <Box sx={{ flex: 1.2, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        {(latestAlert as any).renotifyAt && (
                          <Typography variant="caption" sx={{ color: C.accent, fontSize: CH_TYPE.micro, mr: 0.3 }}>
                            ↺{(latestAlert as any).renotifyAt} {(latestAlert as any).renotifyDirection === 'above' ? '↑' : '↓'}
                          </Typography>
                        )}
                        {(latestAlert as any).renotifyAfterTime && (
                          <RenotifyCountdown target={(latestAlert as any).renotifyAfterTime} color={C.accent} />
                        )}
                        <Tooltip arrow title={<ExecuteTooltipContent action={(latestAlert.parsedData as any)?.action} entrySplitCount={userSettings?.entrySplitCount} portfolioReservePercent={userSettings?.portfolioReservePercent} />}>
                        <Button size="small" variant="outlined"
                          sx={{ borderColor: C.bullBorder, color: C.bull, fontSize: CH_TYPE.micro, py: 0.3, px: 0.8, minWidth: 0, '&:hover': { backgroundColor: C.bullMuted } }}
                          onClick={(e) => { e.stopPropagation(); openAlertEditPanel(latestAlert); }}>
                          Execute
                        </Button>
                        </Tooltip>
                        <Tooltip arrow title="Park — move this alert to the Parked list (snooze it) without trading. You can execute it later from Parked Alerts.">
                        <Button size="small" variant="outlined"
                          sx={{ borderColor: C.warnBorder, color: C.warn, fontSize: CH_TYPE.micro, py: 0.3, px: 0.8, minWidth: 0, '&:hover': { backgroundColor: C.warnMuted } }}
                          onClick={(e) => { e.stopPropagation(); handleParkAlert(latestAlert.id, symbol); }}>
                          Park
                        </Button>
                        </Tooltip>
                        {/* Round 64 — end-of-row Chart icon removed (duplicate of the one beside the symbol). */}
                      </Box>
                    </Box>

                    {/* Inline execute panel for this alert row */}
                    {editingAlertId === latestAlert.id && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', px: 1, py: 0.75, backgroundColor: C.bullMuted, borderRadius: 1, mb: 0.25, border: `1px solid ${C.bullBorder}` }}>
                        <Select size="small" value={alertCustomOrderType} onChange={e => setAlertCustomOrderType(e.target.value)}
                          sx={{ width: 85, fontSize: CH_TYPE.small, '.MuiSelect-select': { py: '4px', px: '6px' } }}>
                          <MenuItem value="LIMIT" sx={{ fontSize: CH_TYPE.small }}>LIMIT</MenuItem>
                          <MenuItem value="MARKET" sx={{ fontSize: CH_TYPE.small }}>MARKET</MenuItem>
                        </Select>
                        {alertCustomOrderType !== 'MARKET' && (
                          <TextField size="small" label="Entry" value={alertCustomEntry} onChange={e => setAlertCustomEntry(e.target.value)} sx={{ width: 85 }} InputLabelProps={{ style: { fontSize: CH_TYPE.small } }} inputProps={{ style: { padding: '4px 6px', fontSize: CH_TYPE.small } }} />
                        )}
                        <TextField size="small" label="Qty (Lots)" value={alertCustomQuantity} onChange={e => setAlertCustomQuantity(e.target.value)} sx={{ width: 75 }} InputLabelProps={{ style: { fontSize: CH_TYPE.small } }} inputProps={{ style: { padding: '4px 6px', fontSize: CH_TYPE.small } }} />
                        <TextField size="small" label="Stop Loss" value={alertCustomSL} onChange={e => setAlertCustomSL(e.target.value)} sx={{ width: 85 }} InputLabelProps={{ style: { fontSize: CH_TYPE.small } }} inputProps={{ style: { padding: '4px 6px', fontSize: CH_TYPE.small } }} />
                        <TextField size="small" label="Target" value={alertCustomTarget} onChange={e => setAlertCustomTarget(e.target.value)} sx={{ width: 80 }} InputLabelProps={{ style: { fontSize: CH_TYPE.small } }} inputProps={{ style: { padding: '4px 6px', fontSize: CH_TYPE.small } }} />
                        <Button size="small" variant="contained" sx={{ backgroundColor: C.bull, fontSize: CH_TYPE.small, px: 1.5 }} onClick={() => handleExecuteWithCustomParams(latestAlert.id)}>Place</Button>
                        <Button size="small" sx={{ fontSize: CH_TYPE.small }} onClick={() => setEditingAlertId(null)}>Cancel</Button>
                      </Box>
                    )}

                    {/* Child rows (expanded) */}
                    {isExpanded && alerts.map((alert: any, idx: number) => {
                      const childStyle = getAlertRowStyle(alert);
                      const childAction = (alert.parsedData as any)?.action || '';
                      return (
                        <Box key={alert.id} sx={{
                          display: 'flex', borderBottom: 1, borderColor: 'rgba(0,0,0,0.08)',
                          py: 0.5, px: 1, backgroundColor: childStyle.bg, opacity: 0.9,
                          alignItems: 'center', fontSize: CH_TYPE.small, mb: 0.1, borderRadius: 1
                        }}>
                          <Box sx={{ flex: 1.2, pl: 3, color: 'text.secondary' }}>#{idx + 1}</Box>
                          <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                            {((alert.parsedData as any)?.strategy || (alert.parsedData as any)?.levelType)
                              ? <Typography variant="caption" sx={{ fontSize:'0.62rem', color:'text.primary', fontWeight:600 }}>{(alert.parsedData as any).strategy || (alert.parsedData as any).levelType}</Typography>
                              : <Typography variant="caption" sx={{ fontSize:'0.62rem', color:'text.disabled', fontStyle:'italic' }}>—</Typography>}
                            {(alert.parsedData as any)?.timeframe && <Typography variant="caption" sx={{ fontSize:'0.6rem', color:'text.secondary' }}>{formatTimeframe((alert.parsedData as any).timeframe)}</Typography>}
                          </Box>
                          <Box sx={{ flex: 1.5, color: 'text.primary' }}>
                            {fmtDate(alert.createdAt, { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', second:'2-digit' })}
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.1 }}>
                              {alert.triggerPrice && <Typography variant="caption" sx={{ color:'text.secondary', fontSize:'0.6rem' }}>@{formatPrice(Number(alert.triggerPrice), resolveDecimalsFor(alert.instrument, Number(alert.triggerPrice)))}</Typography>}
                              {alert.currentPrice && <Typography variant="caption" sx={{ color: C.neutral, fontSize:'0.6rem' }}>now {formatPrice(Number(alert.currentPrice), resolveDecimalsFor(alert.instrument, Number(alert.currentPrice)))}</Typography>}
                            </Box>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            {childAction ? <><Chip label={childAction} size="small" sx={{ bgcolor: childStyle.bg, color: childStyle.color, border: `1px solid ${childStyle.color}55`, fontWeight:700, fontSize:'0.62rem', height:16 }} /><ExitTag alert={alert} color={childStyle.color} small /></> : <Typography variant="caption" sx={{ color:'text.secondary' }}>—</Typography>}
                          </Box>
                          <Box sx={{ flex: 0.7, color: childStyle.color }}><StrengthBadge strength={(alert as any).strength ?? (alert.parsedData as any)?.signalStrength} /></Box>
                          <Box sx={{ flex: 0.8 }}>
                            {(alert.parsedData as any)?.confirmed === 'Y' ?
                              <Chip label="Y" size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, border: `1px solid ${C.bullBorder}`, fontSize:'0.6rem', height:16 }} /> :
                              <Chip label="N" size="small" sx={{ bgcolor: C.dimMuted, color: C.dim, border: `1px solid ${C.dim}44`, fontSize:'0.6rem', height:16 }} />}
                          </Box>
                          <Box sx={{ flex: 0.9 }} />{/* FNO spacer — child rows have no FNO badge */}
                          <Box sx={{ flex: 1.2, display: 'flex', gap: 0.5 }}>
                            <Tooltip arrow title={<ExecuteTooltipContent action={(alert.parsedData as any)?.action} entrySplitCount={userSettings?.entrySplitCount} portfolioReservePercent={userSettings?.portfolioReservePercent} />}>
                            <Button size="small" variant="outlined"
                              sx={{ fontSize: CH_TYPE.micro, py: 0.2, px: 0.6, minWidth: 0, borderColor: C.bullBorder, color: C.bull, '&:hover': { backgroundColor: C.bullMuted } }}
                              onClick={() => openAlertEditPanel(alert)}>Execute</Button>
                            </Tooltip>
                            <Button size="small" variant="outlined"
                              sx={{ fontSize: CH_TYPE.micro, py: 0.2, px: 0.6, minWidth: 0, borderColor: C.warnBorder, color: C.warn, '&:hover': { backgroundColor: C.warnMuted } }}
                              onClick={() => handleParkAlert(alert.id, symbol)}>Park</Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </Box>{/* end scrollable */}
          </Box>
        ) : (
          <EmptyState kind="inbox" size="sm" title="No recent alerts"
            description="Incoming TradingView / strategy alerts for the selected period will show up here." />
        )}
      </Paper>
      </CollapsibleSection>

      <Box data-dash-anchor="parked" sx={{ scrollMarginTop: 120 }} />
      <CollapsibleSection id="parked-alerts" title="Parked Alerts / Scanner" count={(summary?.parkedAlerts || []).length}
        info={
          <Tooltip title="How Parked Alerts works — features, ReNotify behaviour, color codes" arrow>
          <IconButton size="small" onClick={() => setParkedAlertsHelpOpen(true)} sx={{ color: C.accent }}>
          <InfoOutlined fontSize="small" />
          </IconButton>
          </Tooltip>
        }
      >
      {/* Parked Alerts */}
      <Paper sx={{
        p: 2.5,
        mb: 2,
        borderLeft: `3px solid ${chHairline(isDark)}`,
        boxShadow: 2,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-1px)'
        }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1.5 }}>
          <Box>
            
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Alerts set aside for later review
            </Typography>
          </Box>
          <Select
            size="small"
            value={parkedAlertPeriod}
            onChange={(e) => setParkedAlertPeriod(e.target.value)}
            sx={{ minWidth: 90, fontSize: CH_TYPE.body }}
          >
            {['1d','3d','7d','14d','30d','90d','all'].map(p => (
              <MenuItem key={p} value={p} sx={{ fontSize: CH_TYPE.body }}>{p === 'all' ? 'All time' : p}</MenuItem>
            ))}
          </Select>
        </Box>
        {/* Parked Filters — Action / Interval / Strategy / Confirmed */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search symbol..."
            value={parkedFilters.symbol}
            onChange={(e) => setParkedFilters(f => ({ ...f, symbol: e.target.value }))}
            sx={{ flex: '1 1 140px' }} />
          <FormControl size="small" sx={{ minWidth: 95 }}>
            <InputLabel>Action</InputLabel>
            <Select value={parkedFilters.action} label="Action" onChange={(e) => setParkedFilters(f => ({ ...f, action: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="BUY">BUY</MenuItem>
              <MenuItem value="SELL">SELL</MenuItem>
              <MenuItem value="SHORT">SHORT</MenuItem>
              <MenuItem value="COVER">COVER</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 95 }}>
            <InputLabel>Interval</InputLabel>
            <Select value={parkedFilters.interval} label="Interval" onChange={(e) => setParkedFilters(f => ({ ...f, interval: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              {['1m','3m','5m','15m','30m','1h','2h','4h','1d'].map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" placeholder="Strategy..."
            value={parkedFilters.strategy}
            onChange={(e) => setParkedFilters(f => ({ ...f, strategy: e.target.value }))}
            sx={{ width: 110 }} />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Confirmed</InputLabel>
            <Select value={parkedFilters.confirmed} label="Confirmed" onChange={(e) => setParkedFilters(f => ({ ...f, confirmed: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Y">Yes (Y)</MenuItem>
              <MenuItem value="N">No (N)</MenuItem>
            </Select>
          </FormControl>
          {(parkedFilters.action || parkedFilters.interval || parkedFilters.strategy || parkedFilters.confirmed || parkedFilters.symbol) && (
            <Button size="small" variant="outlined"
              onClick={() => setParkedFilters({ action: '', interval: '', strategy: '', confirmed: '', symbol: '' })}>Clear</Button>
          )}
        </Box>

        {groupedParkedAlerts.length > 0 ? (
          <Box>
            {/* Parked header - same columns as Recent */}
            <Box sx={{ display: 'flex', fontWeight: 'bold', borderBottom: 2, borderColor: 'divider', pb: 1, backgroundColor: 'action.hover', p: 1, borderRadius: 1, color: 'text.primary', fontSize: CH_TYPE.body }}>
              <Box sx={{ flex: 1.2 }}>Symbol</Box>
              <Box sx={{ width: 44, flexShrink: 0, textAlign: 'center' }}>Chart</Box>
              <Box sx={{ flex: 1.5 }}>Strategy / Interval</Box>
              <Box sx={{ flex: 1.5 }}>Create Time / Prices</Box>
              <Box sx={{ flex: 1 }}>Alert Type</Box>
              <Box sx={{ flex: 0.7 }}>Strength</Box>
              <Box sx={{ flex: 0.8 }}>Confirmed</Box>
              <Box sx={{ flex: 0.9 }}>FNO</Box>
              <Box sx={{ flex: 1.5 }}>Actions</Box>
            </Box>
            <Box sx={{ maxHeight: 'calc(50vh - 60px)', overflowY: 'auto', pr: 0.5 }}>
            {groupedParkedAlerts
              .filter(([symbol, palerts]) => {
                if (parkedFilters.symbol && !symbol.toLowerCase().includes(parkedFilters.symbol.toLowerCase())) return false;
                // Filters apply to ONLY the latest parked alert per symbol (the row that's actually displayed).
                const latest = (palerts as any[])[0];
                if (!latest) return false;
                const pd = (latest.parsedData as any) || {};
                if (parkedFilters.action && (pd.action || '').toUpperCase() !== parkedFilters.action) return false;
                if (parkedFilters.interval) {
                  // Normalise both sides to minutes (see Recent Alerts note) so the daily/weekly
                  // filters match alerts stored as minutes (1440/10080) as well as 'D'/'W'.
                  const want = tfToMinutes(parkedFilters.interval);
                  const have = tfToMinutes(pd.timeframe);
                  if (want == null || have == null || want !== have) return false;
                }
                if (parkedFilters.strategy) {
                  const q = parkedFilters.strategy.toLowerCase();
                  const inStrategy = (pd.strategy || '').toLowerCase().includes(q);
                  const inLevelType = (pd.levelType || '').toLowerCase().includes(q);
                  if (!inStrategy && !inLevelType) return false;
                }
                if (parkedFilters.confirmed && pd.confirmed !== parkedFilters.confirmed) return false;
                return true;
              })
              .map(([symbol, palerts]) => {
                const latestAlert = palerts[0];
                const rowStyle = getAlertRowStyle(latestAlert);
                const isPExpanded = expandedParkedSymbols.has(symbol);
                const action = (latestAlert.parsedData as any)?.action || '';
                const confirmed = (latestAlert.parsedData as any)?.confirmed;
                // Period filtering now happens at the alert level in
                // groupedParkedAlerts, so no group-level cutoff here.

                return (
                  <React.Fragment key={symbol}>
                    {/* Parent row */}
                    <Box
                      onClick={() => toggleParkedSymbol(symbol)}
                      sx={{
                        display: 'flex', borderBottom: 1, borderColor: 'rgba(255,255,255,0.2)',
                        py: 1, px: 1, backgroundColor: rowStyle.bg, borderRadius: 1, mb: 0.25,
                        cursor: 'pointer', alignItems: 'center', borderLeft: `3px solid ${rowStyle.color}`,
                        '&:hover': { filter: 'brightness(0.96)' }
                      }}
                    >
                      <Box sx={{
                        flex: 1.2, fontWeight: 600, color: rowStyle.color,
                        display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', fontSize: CH_TYPE.body,
                        position: 'sticky', left: 0, zIndex: 2,
                        alignSelf: 'stretch', my: -1, py: 1, ml: -1, pl: 1,
                        // Opaque paper UNDER the tint, not the tint on its own. A sticky
                        // column exists to hide content scrolling beneath it, which a
                        // translucent background does not do -- and because this sits inside
                        // the row, which already paints rowStyle.bg, applying it again
                        // doubled the tint and took the symbol to 3.91:1. Same pattern the
                        // Trades Management sticky column already uses.
                        backgroundColor: 'background.paper',
                        backgroundImage: `linear-gradient(${rowStyle.bg}, ${rowStyle.bg})`,
                      }}>
                        {isPExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <Tooltip title={symbol} arrow><Box component="span" sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{symbol}</Box></Tooltip>
                        {latestAlert.instrument?.exchange && (
                          <Chip label={String(latestAlert.instrument.exchange).toUpperCase()} size="small"
                            sx={{ bgcolor: 'transparent', color: 'text.secondary', fontWeight: 500, fontSize: CH_TYPE.micro, height: 18, border: (theme) => `1px solid ${theme.palette.divider}` }} />
                        )}
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: CH_TYPE.small }}>({palerts.length})</Typography>
                        {latestAlert.wlLong && <Chip label="WL:L" size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18 }} />}
                        {latestAlert.wlShort && <Chip label="WL:S" size="small" sx={{ bgcolor: C.bearMuted, color: C.bear, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18 }} />}
                      </Box>
                      {/* R149 — dedicated Chart column right after the symbol. */}
                      <Box sx={{ width: 44, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <OpenChartButton onOpen={() => openChartTab(symbol, String(latestAlert.instrument?.exchange || ''), chartOpenQuery((latestAlert.parsedData as any)?.timeframe, (latestAlert.parsedData as any)?.strategy || (latestAlert.parsedData as any)?.signalSource))} />
                      </Box>
                      <Box sx={{ flex: 1.5, fontSize: CH_TYPE.body, color: 'text.secondary', display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                        {((latestAlert.parsedData as any)?.strategy || (latestAlert.parsedData as any)?.levelType)
                          ? <Typography variant="caption" sx={{ color: 'text.primary', fontSize: CH_TYPE.small, fontWeight: 700 }}>{(latestAlert.parsedData as any).strategy || (latestAlert.parsedData as any).levelType}</Typography>
                          : <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: CH_TYPE.small, fontStyle: 'italic' }}>—</Typography>}
                        {(latestAlert.parsedData as any)?.timeframe && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: CH_TYPE.micro }}>{formatTimeframe((latestAlert.parsedData as any).timeframe)}</Typography>}
                      </Box>
                      <Box sx={{ flex: 1.5, fontSize: CH_TYPE.body, color: 'text.primary' }}>
                        {fmtDate(latestAlert.createdAt, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.2, flexWrap: 'wrap' }}>
                          {(latestAlert as any).triggerPrice && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: CH_TYPE.micro }}>@{formatPrice(Number((latestAlert as any).triggerPrice), resolveDecimals((latestAlert as any).instrument))}</Typography>}
                          {(latestAlert as any).currentPrice && <Typography variant="caption" sx={{ color: C.neutral, fontSize: CH_TYPE.micro }}>now {formatPrice(Number((latestAlert as any).currentPrice), resolveDecimals((latestAlert as any).instrument))}</Typography>}
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {action ? <><Chip label={action} size="small" sx={{ bgcolor: rowStyle.bg, color: rowStyle.color, border: `1px solid ${rowStyle.color}55`, fontWeight: 700, fontSize: CH_TYPE.small, height: 18 }} /><ExitTag alert={latestAlert} color={rowStyle.color} /></> : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>}
                      </Box>
                      <Box sx={{ flex: 0.7, fontSize: CH_TYPE.body, color: rowStyle.color }}>
                        <StrengthBadge strength={(latestAlert as any).strength ?? (latestAlert.parsedData as any)?.signalStrength} />
                      </Box>
                      <Box sx={{ flex: 0.8 }}>
                        {confirmed === 'Y' ? <Chip label="Y" size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, border: `1px solid ${C.bullBorder}`, fontSize:'0.62rem', height:16 }} /> :
                          confirmed === 'N' ? <Chip label="N" size="small" sx={{ bgcolor: C.dimMuted, color: C.dim, border: `1px solid ${C.dim}44`, fontSize:'0.62rem', height:16 }} /> : null}
                      </Box>
                      {/* FNO Score Badge — Parked Alerts */}
                      <Box sx={{ flex: 0.9, display: 'flex', alignItems: 'center' }}
                        onClick={e => e.stopPropagation()}>
                        <FnoScoreBadge
                          alertId={latestAlert.id}
                          symbol={symbol}
                          direction={(latestAlert.parsedData as any)?.action === 'SHORT' ? 'SHORT' : 'LONG'}
                          assessment={fnoAssessments[latestAlert.id]}
                        />
                        <AiVerdictChip
                          assessment={aiByAlert[latestAlert.id]}
                          signalDirection={(latestAlert.parsedData as any)?.action === 'SHORT' ? 'SHORT' : 'LONG'}
                          compact
                          symbol={symbol}
                          {...((latestAlert as any).instrument?.exchange
                            ? { exchange: String((latestAlert as any).instrument.exchange).toUpperCase() }
                            : {})}
                          alertId={latestAlert.id}
                          timeframe={(latestAlert.parsedData as any)?.timeframe ?? null}
                          onAssessed={(a) => setAiByAlert(prev => ({ ...prev, [latestAlert.id]: a }))}
                        />
                      </Box>
                      <Box sx={{ flex: 1.5, display: 'flex', gap: 0.5, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                        {(latestAlert as any).renotifyAt && (
                          <Typography variant="caption" sx={{ color: C.accent, fontSize: CH_TYPE.micro, mr: 0.3 }}>
                            ↺{(latestAlert as any).renotifyAt} {(latestAlert as any).renotifyDirection === 'above' ? '↑' : '↓'}
                          </Typography>
                        )}
                        {(latestAlert as any).renotifyAfterTime && (
                          <RenotifyCountdown target={(latestAlert as any).renotifyAfterTime} color={C.accent} />
                        )}
                        <Tooltip arrow title={<ExecuteTooltipContent action={(latestAlert.parsedData as any)?.action} entrySplitCount={userSettings?.entrySplitCount} portfolioReservePercent={userSettings?.portfolioReservePercent} />}>
                        <Button size="small" variant="outlined"
                          sx={{ borderColor: C.bullBorder, color: C.bull, fontSize: CH_TYPE.micro, py: 0.3, px: 0.8, minWidth: 0, '&:hover': { backgroundColor: C.bullMuted } }}
                          onClick={() => openAlertEditPanel(latestAlert)}>Execute</Button>
                        </Tooltip>
                        <Button size="small" variant="outlined"
                          sx={{ fontSize: CH_TYPE.micro, py: 0.3, px: 0.8, minWidth: 0, borderColor: C.accentBorder, color: C.accent }}
                          onClick={() => setRenotifyDialogAlert(latestAlert)}
                        >ReNotify</Button>
                        <Button size="small" variant="outlined"
                          sx={{ fontSize: CH_TYPE.micro, py: 0.3, px: 0.8, minWidth: 0, borderColor: C.bearBorder, color: C.bear, '&:hover': { backgroundColor: C.bearMuted } }}
                          onClick={() => handleDismissAlert(latestAlert)}>Dismiss</Button>
                        {/* Round 64 — end-of-row Chart icon removed (duplicate of the one beside the symbol). */}
                      </Box>
                    </Box>

                    {/* Inline execute panel */}
                    {editingAlertId === latestAlert.id && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', px: 1, py: 0.75, backgroundColor: C.bullMuted, borderRadius: 1, mb: 0.25, border: `1px solid ${C.bullBorder}` }}>
                        <Select size="small" value={alertCustomOrderType} onChange={e => setAlertCustomOrderType(e.target.value)}
                          sx={{ width: 85, fontSize: CH_TYPE.small, '.MuiSelect-select': { py: '4px', px: '6px' } }}>
                          <MenuItem value="LIMIT" sx={{ fontSize: CH_TYPE.small }}>LIMIT</MenuItem>
                          <MenuItem value="MARKET" sx={{ fontSize: CH_TYPE.small }}>MARKET</MenuItem>
                        </Select>
                        {alertCustomOrderType !== 'MARKET' && (
                          <TextField size="small" label="Entry" value={alertCustomEntry} onChange={e => setAlertCustomEntry(e.target.value)} sx={{ width: 85 }} InputLabelProps={{ style: { fontSize: CH_TYPE.small } }} inputProps={{ style: { padding: '4px 6px', fontSize: CH_TYPE.small } }} />
                        )}
                        <TextField size="small" label="Qty (Lots)" value={alertCustomQuantity} onChange={e => setAlertCustomQuantity(e.target.value)} sx={{ width: 75 }} InputLabelProps={{ style: { fontSize: CH_TYPE.small } }} inputProps={{ style: { padding: '4px 6px', fontSize: CH_TYPE.small } }} />
                        <TextField size="small" label="Stop Loss" value={alertCustomSL} onChange={e => setAlertCustomSL(e.target.value)} sx={{ width: 85 }} InputLabelProps={{ style: { fontSize: CH_TYPE.small } }} inputProps={{ style: { padding: '4px 6px', fontSize: CH_TYPE.small } }} />
                        <TextField size="small" label="Target" value={alertCustomTarget} onChange={e => setAlertCustomTarget(e.target.value)} sx={{ width: 80 }} InputLabelProps={{ style: { fontSize: CH_TYPE.small } }} inputProps={{ style: { padding: '4px 6px', fontSize: CH_TYPE.small } }} />
                        <Button size="small" variant="contained" sx={{ backgroundColor: C.bull, fontSize: CH_TYPE.small, px: 1.5 }} onClick={() => handleExecuteWithCustomParams(latestAlert.id)}>Place</Button>
                        <Button size="small" sx={{ fontSize: CH_TYPE.small }} onClick={() => setEditingAlertId(null)}>Cancel</Button>
                      </Box>
                    )}

                    {/* Child rows */}
                    {isPExpanded && palerts.map((alert: any, idx: number) => {
                      const childStyle = getAlertRowStyle(alert);
                      const childAction = (alert.parsedData as any)?.action || '';
                      return (
                        <Box key={alert.id} sx={{
                          display: 'flex', borderBottom: 1, borderColor: 'rgba(0,0,0,0.08)',
                          py: 0.5, px: 1, backgroundColor: childStyle.bg, opacity: 0.9,
                          alignItems: 'center', fontSize: CH_TYPE.small, mb: 0.1, borderRadius: 1
                        }}>
                          <Box sx={{ flex: 1.2, pl: 3, color: 'text.secondary' }}>#{idx + 1}</Box>
                          <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                            {((alert.parsedData as any)?.strategy || (alert.parsedData as any)?.levelType)
                              ? <Typography variant="caption" sx={{ fontSize:'0.62rem', color:'text.primary', fontWeight:600 }}>{(alert.parsedData as any).strategy || (alert.parsedData as any).levelType}</Typography>
                              : <Typography variant="caption" sx={{ fontSize:'0.62rem', color:'text.disabled', fontStyle:'italic' }}>—</Typography>}
                            {(alert.parsedData as any)?.timeframe && <Typography variant="caption" sx={{ fontSize:'0.6rem', color:'text.secondary' }}>{formatTimeframe((alert.parsedData as any).timeframe)}</Typography>}
                          </Box>
                          <Box sx={{ flex: 1.5, color: 'text.primary' }}>
                            {fmtDate(alert.createdAt, { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.1 }}>
                              {alert.triggerPrice && <Typography variant="caption" sx={{ color:'text.secondary', fontSize:'0.6rem' }}>@{formatPrice(Number(alert.triggerPrice), resolveDecimalsFor(alert.instrument, Number(alert.triggerPrice)))}</Typography>}
                              {alert.currentPrice && <Typography variant="caption" sx={{ color: C.neutral, fontSize:'0.6rem' }}>now {formatPrice(Number(alert.currentPrice), resolveDecimalsFor(alert.instrument, Number(alert.currentPrice)))}</Typography>}
                            </Box>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            {childAction ? <><Chip label={childAction} size="small" sx={{ bgcolor: childStyle.bg, color: childStyle.color, border: `1px solid ${childStyle.color}55`, fontWeight:700, fontSize:'0.62rem', height:16 }} /><ExitTag alert={alert} color={childStyle.color} small /></> : <Typography variant="caption" sx={{ color:'text.secondary' }}>—</Typography>}
                          </Box>
                          <Box sx={{ flex: 0.7, color: childStyle.color }}><StrengthBadge strength={(alert as any).strength ?? (alert.parsedData as any)?.signalStrength} /></Box>
                          <Box sx={{ flex: 0.8 }}>
                            {(alert.parsedData as any)?.confirmed === 'Y' ?
                              <Chip label="Y" size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, border: `1px solid ${C.bullBorder}`, fontSize:'0.6rem', height:16 }} /> :
                              <Chip label="N" size="small" sx={{ bgcolor: C.dimMuted, color: C.dim, border: `1px solid ${C.dim}44`, fontSize:'0.6rem', height:16 }} />}
                          </Box>
                          <Box sx={{ flex: 0.9 }} />{/* FNO spacer */}
                          <Box sx={{ flex: 1.5, display: 'flex', gap: 0.5 }}>
                            <Tooltip arrow title={<ExecuteTooltipContent action={(alert.parsedData as any)?.action} entrySplitCount={userSettings?.entrySplitCount} portfolioReservePercent={userSettings?.portfolioReservePercent} />}>
                            <Button size="small" variant="outlined"
                              sx={{ fontSize: CH_TYPE.micro, py: 0.2, px: 0.6, minWidth: 0, borderColor: C.bullBorder, color: C.bull, '&:hover': { backgroundColor: C.bullMuted } }}
                              onClick={() => openAlertEditPanel(alert)}>Execute</Button>
                            </Tooltip>
                            <Button size="small" variant="outlined"
                              sx={{ fontSize: CH_TYPE.micro, py: 0.2, px: 0.6, minWidth: 0, borderColor: C.bearBorder, color: C.bear, '&:hover': { backgroundColor: C.bearMuted } }}
                              onClick={() => handleDismissAlert(alert)}>Dismiss</Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        ) : (
          <EmptyState kind="inbox" size="sm" title="No parked alerts"
            description="Alerts you set aside for later will be collected here." />
        )}
      </Paper>
      </CollapsibleSection>

      <Box data-dash-anchor="watchlist" sx={{ scrollMarginTop: 120 }} />
      <CollapsibleSection id="watchlist" title="Watchlist" count={watchlistData?.watchlist?.length || 0}
        info={
          <Tooltip title="Remove ALL symbols from the Long watchlist"><span>
          <Button size="small" variant="outlined" color="success" onClick={() => clearAllWatchlist('wlLong')} sx={{ fontSize: CH_TYPE.small, py: 0.3 }}>Clear Long</Button>
          </span></Tooltip>
        }
      >
      {/* Watchlist — split Long (left) / Short (right) */}
      {watchlistData && (
        <Paper sx={{ p: 2.5, mb: 2, borderLeft: `3px solid ${chHairline(isDark)}`, boxShadow: 2, borderRadius: 2 }}>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {/* Long column */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: C.bull, display: 'block', mb: 0.5, textTransform: 'uppercase', fontSize: CH_TYPE.small }}>
                WL Long ({watchlistData.watchlist.filter((i: any) => i.wlLong).length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {watchlistData.watchlist.filter((i: any) => i.wlLong).map((inst: any) => (
                  <Box key={inst.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, border: `1px solid ${C.bullBorder}`, backgroundColor: C.bullMuted }}>
                    <Typography sx={{ fontWeight: 600, fontSize: CH_TYPE.body, color: C.bull, flex: 1 }}>{inst.symbol}</Typography>
                    {inst.currentPrice != null && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatPrice(Number(inst.currentPrice), resolveDecimalsFor(inst, Number(inst.currentPrice)))}
                      </Typography>
                    )}
                    <Tooltip title={inst.isFavourite ? 'Remove from favourites' : 'Mark as favourite ★'}>
                      <IconButton size="small" onClick={() => toggleWatchlistFav(inst)} sx={{ p: 0.2 }}>
                        {inst.isFavourite ? <StarIcon sx={{ fontSize: 14, color: '#ffb300' }} /> : <StarBorderIcon sx={{ fontSize: 14, color: C.dim, '&:hover': { color: '#ffb300' } }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Notify">
                      <IconButton size="small" onClick={() => setWlNotifyDialog({ instId: inst.id, symbol: inst.symbol, side: 'long', currentPrice: inst.currentPrice || 0, decimalPrecision: inst.decimalPrecision })}
                        sx={{ p: 0.2, color: C.bull, opacity: 0.7, '&:hover': { opacity: 1 } }}>
                        <NotificationsIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <OpenChartButton onOpen={() => openChartTab(inst.symbol, String(inst.exchange || ''))} size={14} />
                    <IconButton size="small" onClick={() => removeFromWatchlist(inst.id, 'wlLong')}
                      sx={{ p: 0.2, color: C.dim, '&:hover': { color: C.bear } }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
            {/* Short column */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: C.bear, display: 'block', mb: 0.5, textTransform: 'uppercase', fontSize: CH_TYPE.small }}>
                WL Short ({watchlistData.watchlist.filter((i: any) => i.wlShort).length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {watchlistData.watchlist.filter((i: any) => i.wlShort).map((inst: any) => (
                  <Box key={inst.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, border: `1px solid ${C.bearBorder}`, backgroundColor: C.bearMuted }}>
                    <Typography sx={{ fontWeight: 600, fontSize: CH_TYPE.body, color: C.bear, flex: 1 }}>{inst.symbol}</Typography>
                    {inst.currentPrice != null && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatPrice(Number(inst.currentPrice), resolveDecimalsFor(inst, Number(inst.currentPrice)))}
                      </Typography>
                    )}
                    <Tooltip title={inst.isFavourite ? 'Remove from favourites' : 'Mark as favourite ★'}>
                      <IconButton size="small" onClick={() => toggleWatchlistFav(inst)} sx={{ p: 0.2 }}>
                        {inst.isFavourite ? <StarIcon sx={{ fontSize: 14, color: '#ffb300' }} /> : <StarBorderIcon sx={{ fontSize: 14, color: C.dim, '&:hover': { color: '#ffb300' } }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Notify">
                      <IconButton size="small" onClick={() => setWlNotifyDialog({ instId: inst.id, symbol: inst.symbol, side: 'short', currentPrice: inst.currentPrice || 0, decimalPrecision: inst.decimalPrecision })}
                        sx={{ p: 0.2, color: C.bear, opacity: 0.7, '&:hover': { opacity: 1 } }}>
                        <NotificationsIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <OpenChartButton onOpen={() => openChartTab(inst.symbol, String(inst.exchange || ''))} size={14} />
                    <IconButton size="small" onClick={() => removeFromWatchlist(inst.id, 'wlShort')}
                      sx={{ p: 0.2, color: C.dim, '&:hover': { color: C.bear } }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
      </CollapsibleSection>

      <Box data-dash-anchor="sysperf" sx={{ scrollMarginTop: 120 }} />
      <CollapsibleSection id="sysperf" title="System Performance">
      {/* System Performance Tile - CHANGE 8c */}
      <Paper sx={{
        p: 2.5,
        mb: 2,
        borderLeft: `3px solid ${chHairline(isDark)}`,
        boxShadow: 2,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' }
      }}>
        
        {perfLoading ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1, py: 1 }}>
            {[0, 1, 2, 3, 4, 5].map(i => <KpiSkeleton key={i} />)}
          </Box>
        ) : perfData ? (
          <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <MetricCard align="center" dense label="Win Rate"
                help="Share of closed trades that were profitable over the selected period."
                tone={(perfData.tradingStats?.winRate || 0) >= 50 ? 'bull' : 'bear'}
                value={`${(perfData.tradingStats?.winRate ?? 0).toFixed(1)}%`} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <MetricCard align="center" dense label="Total Trades"
                help="Number of closed trades in the selected period."
                value={perfData.tradingStats?.totalTrades ?? 0} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <MetricCard align="center" dense label="Total P&L"
                help="Net realised profit / loss across all closed trades in the period."
                tone={(perfData.tradingStats?.totalPnl || 0) >= 0 ? 'bull' : 'bear'}
                value={formatCurrency(perfData.tradingStats?.totalPnl || 0)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <MetricCard align="center" dense label="Avg Win"
                help="Average realised profit on winning trades."
                tone="bull"
                value={formatCurrency(perfData.tradingStats?.avgWin || 0)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <MetricCard align="center" dense label="Avg Loss"
                help="Average realised loss on losing trades — compare against Avg Win to gauge your reward-to-risk."
                tone="bear"
                value={formatCurrency(perfData.tradingStats?.avgLoss || 0)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <MetricCard align="center" dense label="Max Drawdown"
                help="Largest peak-to-trough equity decline over the period."
                tone="bear"
                value={formatCurrency(perfData.tradingStats?.maxDrawdown || 0)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <MetricCard align="center" dense label="Failed Orders"
                help="Broker orders that were rejected or failed in the period — investigate if non-zero."
                tone={(perfData.systemHealth?.failedOrders || 0) > 0 ? 'warn' : 'bull'}
                value={perfData.systemHealth?.failedOrders || 0} />
            </Grid>
          </Grid>

          {/* Win/Loss Pie chart + P&L Bar chart */}
          {perfData.tradingStats?.totalTrades > 0 && (
            <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              {/* Win / Loss Pie */}
              <Box sx={{ flex: '0 0 220px' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5, textAlign: 'center' }}>
                  Win / Loss Split
                </Typography>
                <PieChart width={220} height={160}>
                  <Pie
                    data={[
                      { name: 'Wins', value: perfData.tradingStats?.wins || 0 },
                      { name: 'Losses', value: perfData.tradingStats?.losses || 0 }
                    ]}
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    dataKey="value"
                    label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    <Cell key="wins" fill={C.chartBull} />
                    <Cell key="losses" fill={C.chartBear} />
                  </Pie>
                  <RechartsTooltip formatter={(value: any, name: any) => [value, name]} />
                </PieChart>
              </Box>

              {/* Daily P&L bar (follows perfPeriod) */}
              {perfDailyPnl.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5, textAlign: 'center' }}>
                    {perfPeriod}d Daily P&L
                  </Typography>
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart data={perfDailyPnl} margin={{ top: 20, right: 4, left: 4, bottom: 18 }}>
                      {/* `interval={0}` forced EVERY day onto the axis — thirty rotated labels
                          overlapping into an unreadable band. "preserveStartEnd" lets
                          recharts drop labels until they fit, and the ones that survive
                          are legible at 11px instead of 10. */}
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd"
                        minTickGap={18} angle={-30} textAnchor="end" height={40} />
                      <RechartsTooltip
                        formatter={(value: any) => [formatCurrency(value), 'P&L']}
                        labelFormatter={(label: any) => label}
                      />
                      <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>
                        {perfDailyPnl.map((entry, idx) => (
                          <Cell key={idx} fill={entry.pnl >= 0 ? C.chartBull : C.chartBear} />
                        ))}
                        <LabelList dataKey="pnl" position="top" style={{ fontSize: 9, fill: C.dim }}
                          formatter={(v: any) => v === 0 ? '' : (v >= 0 ? '+' : '') + Number(v).toFixed(0)} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* Portfolio value growth (combined account value over the period). */}
              {perfPortfolio.filter(p => p.combined != null).length > 1 && (
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5, textAlign: 'center' }}>
                    {perfPeriod}d Portfolio value
                  </Typography>
                  <ResponsiveContainer width="100%" height={170}>
                    <LineChart data={perfPortfolio} margin={{ top: 20, right: 8, left: 4, bottom: 18 }}>
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={22} angle={-30} textAnchor="end" height={40}
                        tickFormatter={(d: any) => String(d).slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} width={52} domain={['auto', 'auto']}
                        tickFormatter={(v: any) => Math.abs(v) >= 1e5 ? `₹${(v / 1e5).toFixed(1)}L` : Math.abs(v) >= 1e3 ? `₹${(v / 1e3).toFixed(0)}k` : `₹${v}`} />
                      <RechartsTooltip formatter={(value: any) => [formatCurrency(value), 'Value']} labelFormatter={(l: any) => l} />
                      <Line type="monotone" dataKey="combined" stroke={C.chartBull} strokeWidth={2} dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          )}
          </>
        ) : (
          <EmptyState kind="chart" size="sm" title="No performance data yet"
            description="Close a few trades and your win rate, P&L and drawdown stats will populate here." />
        )}
      </Paper>
      </CollapsibleSection>

      {/* Add Symbol Dialog (Issues 13 & 14) */}
      <Dialog open={addSymbolDialog.open} onClose={() => { setAddSymbolDialog({ ...addSymbolDialog, open: false }); setAddSymbolSelected(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {addSymbolDialog.mode === 'priceLevelWatch' ? 'Add Symbol to Pending Manual Trigger Watch' :
            addSymbolDialog.mode === 'wlLong' ? 'Add Symbol to Long Watchlist' : 'Add Symbol to Short Watchlist'}
        </DialogTitle>
        <DialogContent>
          {!addSymbolSelected ? (
            <>
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Search instrument (e.g. ETHUSD, BTCUSD)..."
                value={addSymbolSearch}
                onChange={e => {
                  const val = e.target.value.toUpperCase();
                  setAddSymbolSearch(val);
                  searchInstrumentsForDialog(val);
                }}
                inputProps={{ style: { textTransform: 'uppercase' }, autoCapitalize: 'characters' }}
                sx={{ mt: 1 }}
              />
              {addSymbolSearching && <CircularProgress size={20} sx={{ mt: 1 }} />}
              {addSymbolResults.length > 0 && (
                <List dense sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 300, overflowY: 'auto' }}>
                  {addSymbolResults.map((inst: any) => (
                    <ListItem key={inst.id} disablePadding>
                      <ListItemButton onClick={() => handleAddSymbolFromDialog(inst)}>
                        <ListItemText
                          primary={inst.symbol}
                          secondary={`${inst.exchange} • ${inst.tradeSegment || ''}`}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
              {!addSymbolSearching && addSymbolSearch && addSymbolResults.length === 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>No instruments found</Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Set price levels for <strong>{addSymbolSelected.symbol}</strong> (at least one required):
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
                {(() => {
                  const cp = Number(addSymbolSelected?.currentPrice);
                  const nbuyV = parseFloat(addSymbolPLValues.nbuy);
                  const nshortV = parseFloat(addSymbolPLValues.nshort);
                  const bbuyV = parseFloat(addSymbolPLValues.bbuy);
                  const bshortV = parseFloat(addSymbolPLValues.bshort);
                  const warnIf = (active: boolean) => Number.isFinite(cp) && cp > 0 && active;
                  return (
                    <>
                      <TextField size="small" label="NBUY (≤)" type="number" value={addSymbolPLValues.nbuy}
                        error={warnIf(Number.isFinite(nbuyV) && nbuyV > cp)}
                        onChange={e => setAddSymbolPLValues(v => ({ ...v, nbuy: e.target.value }))}
                        onDoubleClick={() => setAddSymbolPLValues(v => ({ ...v, nbuy: String(addSymbolSelected?.currentPrice || '') }))}
                        helperText={warnIf(Number.isFinite(nbuyV) && nbuyV > cp) ? 'Above current — triggers instantly' : 'Buy when price drops to (dbl-click = current)'} />
                      <TextField size="small" label="NSHORT (≥)" type="number" value={addSymbolPLValues.nshort}
                        error={warnIf(Number.isFinite(nshortV) && nshortV < cp)}
                        onChange={e => setAddSymbolPLValues(v => ({ ...v, nshort: e.target.value }))}
                        onDoubleClick={() => setAddSymbolPLValues(v => ({ ...v, nshort: String(addSymbolSelected?.currentPrice || '') }))}
                        helperText={warnIf(Number.isFinite(nshortV) && nshortV < cp) ? 'Below current — triggers instantly' : 'Short when price rises to (dbl-click = current)'} />
                      <TextField size="small" label="BBUY (≥)" type="number" value={addSymbolPLValues.bbuy}
                        error={warnIf(Number.isFinite(bbuyV) && bbuyV < cp)}
                        onChange={e => setAddSymbolPLValues(v => ({ ...v, bbuy: e.target.value }))}
                        onDoubleClick={() => setAddSymbolPLValues(v => ({ ...v, bbuy: String(addSymbolSelected?.currentPrice || '') }))}
                        helperText={warnIf(Number.isFinite(bbuyV) && bbuyV < cp) ? 'Below current — triggers instantly' : 'Breakout buy above (dbl-click = current)'} />
                      <TextField size="small" label="BSHORT (≤)" type="number" value={addSymbolPLValues.bshort}
                        error={warnIf(Number.isFinite(bshortV) && bshortV > cp)}
                        onChange={e => setAddSymbolPLValues(v => ({ ...v, bshort: e.target.value }))}
                        onDoubleClick={() => setAddSymbolPLValues(v => ({ ...v, bshort: String(addSymbolSelected?.currentPrice || '') }))}
                        helperText={warnIf(Number.isFinite(bshortV) && bshortV > cp) ? 'Above current — triggers instantly' : 'Breakout short below (dbl-click = current)'} />
                    </>
                  );
                })()}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {addSymbolSelected && (
            <Button onClick={() => setAddSymbolSelected(null)}>← Back</Button>
          )}
          <Button onClick={() => { setAddSymbolDialog({ ...addSymbolDialog, open: false }); setAddSymbolSelected(null); }}>Cancel</Button>
          {addSymbolSelected && (
            <Button variant="contained" onClick={handleConfirmPriceLevelAdd}>Add</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Trades Management Help Dialog */}
      <Dialog open={trailHelpOpen} onClose={() => setTrailHelpOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ fontWeight: 700 }}>Trades Management — Complete Guide</DialogTitle>
        <DialogContent dividers>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>1. How a trade gets into this table</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>(a) Via Charts → BUY / SHORT button</b> — the alert carries both Initial SL (ISL) and Target. Both are populated immediately and the trade is created in OPEN state.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>(b) Via broker-terminal sync</b> — when you open a position directly in Delta / Zerodha, the system creates a row by syncing the broker's position. ISL and Target may be missing in this case. Click the SL or Target cell to set them manually.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>2. The three SL levels</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>ISL</b> (Initial SL) — original stop from the alert. Anchors the R-multiple computation; never moves automatically.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>Manual SL</b> — your override (clicking the SL cell or dragging the SL line on the chart). Replaces ISL as the operative stop until trailing kicks in.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>TSL</b> (Trailing SL) — only computed AFTER activation. Ratchets monotonically in the profit direction; never relaxes.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>3. SL Risk &amp; activation gate</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Once ISL is available, <b>SL Risk</b> is computed:
            <i> abs(Entry − ISL) × Qty × Lot × FX</i>. This is the rupee value of the worst-case loss
            using the currently operative SL.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Trailing SL activates ONLY when current open profit reaches <b>N × SL Risk</b>, where
            <i> N = Trail SL Activation (× R MFE threshold)</i> in Settings → Trailing SL (default <b>2</b>).
            Until then, the operative breach level is the Manual SL (if any) or ISL — there is NO TSL displayed.
            On activation, the SL column flips to show the live TSL and the Trail chip turns ON.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>4. Breach detection — applies to BOTH ISL and TSL</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            When price touches the operative SL (whichever it is — ISL pre-activation or TSL post-activation),
            a confirmation timer starts:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, pl: 2 }}>
            • <b>Normal mode</b> — price must hold past the level for <i>Normal Breach Hold (minutes)</i> (Settings → Trailing SL, default ~1) before square-off.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • <b>Spike mode</b> — a sudden ATR/range expansion shortens the wait to <i>Spike Breach Hold (seconds)</i> (default ~30s) so flash moves don't trap you.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>5. SL Trigger toggle (per trade)</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            On every open-trade row there is an <b>SL Trigger</b> toggle. When <b>ON</b> (default): on confirmed breach, the system fires a market exit order with the broker.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            When <b>OFF</b>: SL computation continues (TSL still ratchets, breach is still detected with the full hold-window) but on a confirmed breach
            <b> no exit order is placed</b> — you receive a Telegram alert instead and the row gets a dashed red SL-OFF border so you can manage the exit yourself.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>6. Auto-Trail snooze</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Click the Trail chip (Trail ON) to snooze trailing for a chosen duration. TSL freezes at its current value but breach detection still runs — your downside is still protected against that frozen level until the snooze expires.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>7. Profit lock</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            When P&amp;L reaches a configured multiple of SL Risk (default <b>5×</b>), the row gets a 🔒 marker — TSL has locked profits into solidly green territory and only tightens further from there.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>8. Editing values</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Click the <b>SL</b> cell to set a Manual SL. Dragging the SL line on the chart does the same thing — and once TSL is active, dragging higher than TSL <i>ratchets</i> the TSL too.
          </Typography>
          <Typography variant="body2">
            • Click the <b>Target</b> cell to set / change Target. Target hit closes the trade immediately at market (no hold window).
          </Typography>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrailHelpOpen(false)} variant="contained">Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Price Level Watch Help Dialog */}
      <Dialog open={plwHelpOpen} onClose={() => setPlwHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Pending Manual Trigger Watch — How It Works</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Pending Manual Trigger Watch (price-level triggers) monitors symbols you've added and fires alerts when the live price crosses one of four configurable trigger levels.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>The four trigger types</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            <Typography variant="body2"><b style={{ color: C.bull }}>NBUY (≤)</b> — Normal Buy. Fires when price <i>falls to or below</i> the level. Use for value/dip buys.</Typography>
            <Typography variant="body2"><b style={{ color: C.bear }}>NSHORT (≥)</b> — Normal Short. Fires when price <i>rises to or above</i> the level. Use for resistance shorts.</Typography>
            <Typography variant="body2"><b style={{ color: C.neutral }}>BBUY (≥)</b> — Breakout Buy. Fires when price <i>breaks out above</i> the level. Use for momentum entries.</Typography>
            <Typography variant="body2"><b style={{ color: C.accent }}>BSHORT (≤)</b> — Breakout Short. Fires when price <i>breaks down below</i> the level. Use for breakdown entries.</Typography>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>A level must be armed before it can fire</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            A level fires on a <b>crossing</b>, not merely on price being past it. When you save a level, the app records which side of it the price is on; the trigger arms only once price is <i>outside</i> the zone it fires in, and then fires when price crosses in.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            So a level saved on the <b>wrong side</b> of the market — an NBUY <i>above</i> the current price, or a BBUY <i>below</i> it — will <b>not</b> fire straight away. It shows as <b>"Wrong side"</b> and stays disarmed until price moves out past it and genuinely crosses back in. This is what stops a mistyped level from placing an unintended order on the very next 30-second cycle. If you actually want to trade at the current price, use the Buy / Short buttons instead.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Multiple triggers per symbol — interaction rules</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            All four levels can be set on the same symbol simultaneously. They are evaluated independently against every live tick.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <b>Side-clearing on fire</b> — when <i>any</i> long-side level fires (NBUY or BBUY), <i>both</i> NBUY and BBUY are cleared on this symbol. Same for short-side: firing NSHORT or BSHORT clears both NSHORT and BSHORT. The opposite side stays armed and continues to monitor.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Rationale: once you've taken the long signal, the trader-intent is "I'm in a long now"; further long triggers should not fire until you explicitly re-arm. The short side is unaffected so a counter-move still alerts you.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>Re-arming</b> — Edit the row and re-enter the cleared levels (or pick fresh ones). Until you do, the row's Status will read "Disabled" if no levels remain on either side, or "Armed" with whatever side is still set.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Status column</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <b>Armed</b> — at least one level is set and waiting for a price cross.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <b>Triggered</b> — a level was hit on the current tick; an alert was raised in <i>Recent Alerts</i> with that level as the strategy name (e.g. "NBUY"), and a Telegram message was sent. The same-side levels were then cleared.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <b>Wrong side</b> — a level is set on the side of the market price is already on, so it is held disarmed (see above). Move it to the correct side, or wait for price to cross back out.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>Disabled</b> — no levels are set on this instrument.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>Overlapping levels</b> — if a long-side and a short-side level are both satisfied at the same price (e.g. NSHORT set below NBUY), firing both would open a long and a short at once. Nothing is fired; you get a Telegram notice asking you to fix the levels.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Auto-Execute toggle</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            When ON, a price-level trigger places an actual broker order using the instrument's Manual Trade defaults (qty from investment × leverage). When OFF, only an alert is raised — you decide whether to act. Either way, the same-side levels are cleared after the fire.
            Auto-executed entries are protected automatically: an <b>ISL</b> (prev-2 CLH low/high ± breach buffer) and a <b>Target</b> (entry ± N×risk from your Trail Settings) are derived from recent 15m candles and stamped on the trade, so SL monitoring and auto-trailing engage immediately. If derivation fails (no candle data), the trade opens unprotected and you're warned on Telegram — set an SL manually.
            Auto-Execute also obeys the global <b>Order Placement</b> kill-switch in Trading Controls — flip that OFF to halt orders without losing your watchlist.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Inline editing</Typography>
          <Typography variant="body2">
            Click any level cell to edit. Cells turn red if a value is already past current price (it would trigger immediately). Double-click a level cell to copy the current price into it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlwHelpOpen(false)} variant="contained">Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Recent Alerts — Help */}
      <Dialog open={recentAlertsHelpOpen} onClose={() => setRecentAlertsHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Recent Alerts — How it works</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>Recent Alerts</b> shows live signals from your strategies and TradingView webhooks within the period you pick (top-right dropdown). Alerts are grouped by symbol — click a symbol to expand sibling alerts (history of the last 10 firings for that symbol).
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Color coding</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <b style={{ color: C.bull }}>Green</b> — bullish action (BUY, COVER, COVER_FLAT). Long entries or short exits.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <b style={{ color: C.bear }}>Red</b> — bearish action (SHORT, SELL, SELL_FLAT). Short entries or long exits.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b style={{ color: C.accent }}>Accent</b> — informational notification (no action signal).
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Columns</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>Symbol</b> — sticky column with exchange chip and a chart icon (opens TradingView-style chart in a new tab, routed to the correct exchange).</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>Strategy / Interval</b> — strategy name + timeframe (1m, 5m, 15m, 1h, …) of the firing candle.</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>Create Time / Prices</b> — when the alert was raised, with the trigger price (@) and the live price (now).</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>Alert Type</b> — the action chip (BUY/SHORT/etc.).</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>Strength</b> — signal strength (★ count) from the strategy.</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>Confirmed</b> — Y = bar-close confirmed; N = intra-bar (may flip).</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>FNO</b> — F&O score badge (option chain bias) where available.</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}><b>Actions</b> — Execute opens the inline order panel; Park sends the symbol's alerts to the Parked section.</Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Filters</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Filter by symbol, action, interval, strategy, or confirmed/unconfirmed. Filters apply to the <i>latest</i> alert per symbol.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>What to expect</Typography>
          <Typography variant="body2">
            New alerts arrive in real-time over WebSocket and via the 30-second polling refresh. A symbol moves to <b>Parked Alerts</b> once you click Park; it returns to Recent automatically when a fresh alert fires for that symbol or when a ReNotify on a parked alert triggers.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecentAlertsHelpOpen(false)} variant="contained">Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Parked Alerts — Help */}
      <Dialog open={parkedAlertsHelpOpen} onClose={() => setParkedAlertsHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Parked Alerts / Scanner — How it works</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>Parked Alerts</b> is your "later review" pile. When you click <b>Park</b> on a Recent alert, that alert (and all sibling alerts for the same symbol) move here so they stop cluttering the live view but remain accessible.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.accent }}>ReNotify (the key feature)</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Click <b>ReNotify</b> on a parked row to schedule a re-alert in two ways:
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5, pl: 2 }}>
            • <b>At Price</b> — fire when the live price crosses your level (above or below).
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, pl: 2 }}>
            • <b>After Time</b> — fire after N minutes / hours / days. A live countdown ⏱ appears next to the row.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>What happens when ReNotify fires:</b> the <i>same</i> parked alert moves back to Recent Alerts (its status flips to TRIGGERED) — <i>no new alert is created</i>. A Telegram message is sent, the dashboard refreshes, and the symbol is no longer shown in Parked. To send it back to Parked, click Park on it again.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Setting a new ReNotify always replaces the previous one (price-based and time-based are mutually exclusive).
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Color coding</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <b style={{ color: C.bull }}>Green</b> — bullish action; <b style={{ color: C.bear }}>Red</b> — bearish action; <b style={{ color: C.accent }}>Accent</b> — other / informational.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Same convention as Recent Alerts — color is driven by the alert's action, not by P&L.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Columns + Actions</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>Symbol</b> — sticky column, exchange chip, chart icon (opens chart routed to the correct exchange).</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>Execute</b> — opens the inline order panel for this parked alert.</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>ReNotify</b> — schedule a price- or time-based re-alert (see above).</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}><b>Dismiss</b> — remove the alert permanently.</Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: C.neutral }}>Period filter</Typography>
          <Typography variant="body2">
            The dropdown (top-right) limits parked alerts to those acknowledged within that window. Pick "All time" to see everything you've ever parked.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParkedAlertsHelpOpen(false)} variant="contained">Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Trade Dialog */}
      <Dialog open={manualTradeDialog} onClose={() => { setManualTradeDialog(false); setManualTradeLtp(null); setManualTradeSelectedInst(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Manual Trade</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Symbol autosuggest — debounced type-ahead with inline results */}
          <Autocomplete
            size="small"
            options={manualTradeSymbolResults}
            value={manualTradeSelectedInst}
            inputValue={manualTradeForm.symbol}
            loading={manualTradeSearching}
            getOptionLabel={(opt: any) => opt ? `${opt.symbol} (${opt.exchange})` : ''}
            isOptionEqualToValue={(a: any, b: any) => a?.id === b?.id}
            filterOptions={(x) => x}
            noOptionsText={manualTradeForm.symbol.length < 2 ? 'Type at least 2 characters…' : 'No matching instruments'}
            onInputChange={(_e, val, reason) => {
              if (reason === 'reset') return;
              const upper = val.toUpperCase();
              setManualTradeForm(f => ({ ...f, symbol: upper }));
            }}
            onChange={async (_e, inst: any) => {
              if (!inst) {
                setManualTradeSelectedInst(null);
                setManualTradeLtp(null);
                return;
              }
              setManualTradeForm(f => ({ ...f, symbol: inst.symbol, exchange: inst.exchange || 'NSE' }));
              setManualTradeSelectedInst(inst);
              setManualTradeLtp(null);
              setManualTradeLtpLoading(true);
              try {
                const resp = await api.get<any>(`/instruments/${inst.id}/ltp`);
                const ltp = Number(resp.data?.data?.ltp || 0);
                const source = resp.data?.data?.source || 'stored';
                if (ltp > 0) {
                  setManualTradeLtp({ value: ltp, source });
                  setManualTradeForm(f => (f.entryPrice ? f : { ...f, entryPrice: String(ltp) }));
                }
              } catch (_) { /* non-fatal */ }
              setManualTradeLtpLoading(false);
            }}
            renderOption={(props, opt: any) => (
              <li {...props} key={opt.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.symbol} <Box component="span" sx={{ color: 'text.secondary', fontSize: CH_TYPE.small, ml: 0.5 }}>{opt.exchange}</Box></Typography>
                  {opt.name && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{opt.name}</Typography>}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Symbol"
                placeholder="Search symbol…"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {manualTradeSearching ? <CircularProgress size={14} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {(manualTradeSelectedInst && (manualTradeLtpLoading || manualTradeLtp)) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, bgcolor: (theme) => theme.palette.action.hover, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Live price:</Typography>
              {manualTradeLtpLoading && <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>fetching…</Typography>}
              {!manualTradeLtpLoading && manualTradeLtp && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: C.accent }}>
                    {fmt.price(manualTradeLtp.value, manualTradeSelectedInst)}
                  </Typography>
                  <Chip size="small" label={manualTradeLtp.source.toUpperCase()} sx={{ height: 18, fontSize: CH_TYPE.small }} />
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setManualTradeForm(f => ({ ...f, entryPrice: String(manualTradeLtp.value) }))}
                    sx={{ ml: 'auto', minWidth: 0, fontSize: CH_TYPE.small }}
                  >
                    Use as Entry
                  </Button>
                </>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Exchange</InputLabel>
              <Select label="Exchange" value={manualTradeForm.exchange} onChange={e => setManualTradeForm(f => ({ ...f, exchange: e.target.value }))}>
                {['NSE', 'BSE', 'NFO', 'MCX', 'CDS', 'BFO', 'DELTA'].map(ex => <MenuItem key={ex} value={ex}>{ex}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Side</InputLabel>
              <Select label="Side" value={manualTradeForm.side} onChange={e => setManualTradeForm(f => ({ ...f, side: e.target.value }))}>
                <MenuItem value="LONG" sx={{ color: C.bull, fontWeight: 600 }}>LONG</MenuItem>
                <MenuItem value="SHORT" sx={{ color: C.bear, fontWeight: 600 }}>SHORT</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <TextField label="Quantity" size="small" type="number"
                value={manualTradeForm.quantity} onChange={e => setManualTradeForm(f => ({ ...f, quantity: e.target.value }))} />
              {manualTradeSuggestedQty != null && (
                <Chip
                  size="small"
                  label={`Suggested: ${manualTradeSuggestedQty}`}
                  onClick={() => setManualTradeForm(f => ({ ...f, quantity: String(manualTradeSuggestedQty) }))}
                  sx={{ alignSelf: 'flex-start', height: 18, fontSize: CH_TYPE.small, cursor: 'pointer' }}
                />
              )}
            </Box>
            <TextField label="Entry Price" size="small" type="number" sx={{ flex: 1 }}
              value={manualTradeForm.entryPrice} onChange={e => setManualTradeForm(f => ({ ...f, entryPrice: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Stop Loss (optional)" size="small" type="number" sx={{ flex: 1 }}
              value={manualTradeForm.stopLoss} onChange={e => setManualTradeForm(f => ({ ...f, stopLoss: e.target.value }))} />
            <TextField label="Target (optional)" size="small" type="number" sx={{ flex: 1 }}
              value={manualTradeForm.target} onChange={e => setManualTradeForm(f => ({ ...f, target: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Trade Type</InputLabel>
              <Select label="Trade Type" value={manualTradeForm.tradeType} onChange={e => setManualTradeForm(f => ({ ...f, tradeType: e.target.value }))}>
                <MenuItem value="INTRADAY">INTRADAY</MenuItem>
                <MenuItem value="POSITIONAL">POSITIONAL</MenuItem>
                <MenuItem value="SWING">SWING</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Product Type</InputLabel>
              <Select label="Product Type" value={manualTradeForm.productType} onChange={e => setManualTradeForm(f => ({ ...f, productType: e.target.value }))}>
                <MenuItem value="MIS">MIS</MenuItem>
                <MenuItem value="CNC">CNC</MenuItem>
                <MenuItem value="NRML">NRML</MenuItem>
                <MenuItem value="MTF">MTF</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField label="Notes (optional)" size="small" multiline rows={2}
            value={manualTradeForm.notes} onChange={e => setManualTradeForm(f => ({ ...f, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualTradeDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!manualTradeForm.symbol || !manualTradeForm.quantity || !manualTradeForm.entryPrice}
            onClick={async () => {
              try {
                await tradingService.createManualTrade({
                  symbol: manualTradeForm.symbol,
                  exchange: manualTradeForm.exchange,
                  side: manualTradeForm.side,
                  quantity: Number(manualTradeForm.quantity),
                  entryPrice: Number(manualTradeForm.entryPrice),
                  stopLoss: manualTradeForm.stopLoss ? Number(manualTradeForm.stopLoss) : undefined,
                  target: manualTradeForm.target ? Number(manualTradeForm.target) : undefined,
                  tradeType: manualTradeForm.tradeType,
                  productType: manualTradeForm.productType,
                  notes: manualTradeForm.notes || undefined
                });
                toast.success('Manual trade created');
                setManualTradeDialog(false);
                setManualTradeForm({ symbol: '', exchange: 'NSE', side: 'LONG', quantity: '', entryPrice: '', stopLoss: '', target: '', tradeType: 'POSITIONAL', productType: 'CNC', notes: '' });
                dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
              } catch (err: any) {
                toast.error(err?.response?.data?.error?.message || 'Failed to create manual trade');
              }
            }}
            sx={{ bgcolor: C.accent, '&:hover': { bgcolor: C.accent, filter: 'brightness(0.9)' } }}
          >
            Create Trade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renotify Dialog */}
      <RenotifyDialog
        open={!!renotifyDialogAlert}
        symbol={renotifyDialogAlert?.instrument?.symbol || renotifyDialogAlert?.symbol || ''}
        currentPrice={renotifyDialogAlert?.currentPrice}
        decimalPrecision={renotifyDialogAlert?.instrument?.decimalPrecision}
        existingRenotifyAt={renotifyDialogAlert?.renotifyAt}
        existingRenotifyAfterTime={renotifyDialogAlert?.renotifyAfterTime}
        defaultDirection={(() => {
          const act = (renotifyDialogAlert?.parsedData?.action || '').toUpperCase();
          if (['BUY','COVER','COVER_FLAT'].includes(act)) return 'below';
          if (['SHORT','SELL','SELL_FLAT'].includes(act)) return 'above';
          return undefined;
        })()}
        onClose={() => setRenotifyDialogAlert(null)}
        onSave={(payload) => {
          const alertId = renotifyDialogAlert?.id;
          if (!alertId) return;
          api.put(`/alerts/${alertId}/custom-prices`, payload)
            .then(() => {
              toast.success('ReNotify saved');
              setRenotifyDialogAlert(null);
              dispatch(fetchDashboardSummary(alertPeriodToDays(alertPeriod)));
            })
            .catch(() => toast.error('Failed to set ReNotify'));
        }}
      />

      {/* Watchlist Notify Dialog */}
      <RenotifyDialog
        open={!!wlNotifyDialog}
        symbol={wlNotifyDialog?.symbol || ''}
        currentPrice={wlNotifyDialog?.currentPrice}
        decimalPrecision={wlNotifyDialog?.decimalPrecision}
        title="Notify"
        defaultDirection={wlNotifyDialog?.side === 'long' ? 'below' : wlNotifyDialog?.side === 'short' ? 'above' : undefined}
        onClose={() => setWlNotifyDialog(null)}
        onSave={(payload) => {
          if (!wlNotifyDialog) return;
          const { instId, symbol, side } = wlNotifyDialog;
          api.post('/alerts', {
            instrumentId: instId,
            type: 'PRICE',
            priority: 'MEDIUM',
            title: `Notify: ${symbol}`,
            message: payload.renotifyAt
              ? `${symbol} price ${payload.renotifyDirection} ${payload.renotifyAt}`
              : `${symbol} time-based notification`,
            triggerPrice: payload.renotifyAt || 0,
            renotifyAt: payload.renotifyAt,
            renotifyDirection: payload.renotifyDirection,
            renotifyAfterTime: payload.renotifyAfterTime,
            parsedData: { symbol, action: side === 'long' ? 'BUY' : 'SHORT' }
          })
            .then(() => {
              toast.success(`Notification set for ${symbol}`);
              setWlNotifyDialog(null);
            })
            .catch(() => toast.error('Failed to create notification'));
        }}
      />
      {lvlDlgEl}

    </Box>
  );
};

export default DashboardLayout;
